export const conversationalSurveyTranslations = {
  en: {
    tutorial: {
      title: "Getting started",
      step1Title: "Welcome to the Survey Builder",
      step1Body: "Build conversational surveys where an AI asks smart follow-up questions and adapts to each respondent — far richer than a static form.",
      step2Title: "Add your questions",
      step2Body: "Add questions of any type — multiple choice, scales, open text — and drag to reorder them. Use page breaks and sections to shape the flow.",
      step3Title: "Configure the AI",
      step3Body: "Set the language, tone, and how deeply the AI probes with follow-ups on open-ended answers. You can auto-translate the whole survey too.",
      step4Title: "Share & analyze",
      step4Body: "Publish and share your link, then watch responses and analytics arrive as participants complete the survey.",
      back: "Back",
      next: "Next",
      done: "Get started",
      skip: "Skip",
      help: "How it works",
    },
    import: {
      menuLabel: "Import file", reviewTitle: "Review imported survey",
      reviewIntro: "We drafted {{n}} questions from your file. Review below, then create the survey — you can fully edit it in the builder.",
      cancel: "Cancel", titleField: "Survey title", descriptionField: "Description",
      reportTitle: "Import notes", preview: "Questions", pageBreak: "Page break",
      sectionBreak: "Section", required: "required", create: "Create survey",
      parseFailed: "Could not import this file.", createFailed: "Could not create the survey.",
      uploadTitle: "Import a survey", parsing: "Reading your file…",
      dropHere: "Drag a file here, or click to browse",
      dropHint: "Up to 25 MB. We draft a survey you can review and edit.",
      supported: "Supported formats",
      formatNote: "AI turns Word & PDF into a survey — it transcribes an existing questionnaire or drafts one from your notes or brief. Qualtrics .qsf and QualiTaTi .qlt are parsed exactly.",
      exportDefinition: "Download survey", exportWord: "Download as Word (.docx)", exportQlt: "Download as QualiTaTi (.qlt)", exportFailed: "Export failed.",
    },
    translations: {
      menuLabel: "Translations", title: "Survey translations",
      intro: "Add a language to auto-translate the whole survey with AI, then review and edit each string. Respondents pick their language at the start; anything left blank falls back to the default ({{lang}}).",
      source: "source", addLanguage: "Add language",
      empty: "No languages yet. Click “Add language” to create an AI translation.",
      surveyInfo: "Survey info", fieldTitle: "Title", fieldDescription: "Description",
      fieldThankYouTitle: "Thank-you title", fieldThankYouMessage: "Thank-you message",
      fieldPrompt: "Question", options: "Options", columns: "Scale columns",
      retranslate: "Re-translate", close: "Close", save: "Save",
      translated: "Auto-translation complete. Review and edit below.", translateFailed: "Translation failed.",
      saved: "Translation saved.", saveFailed: "Save failed.",
      confirmDelete: "Remove this language and all its translations?", deleteFailed: "Delete failed.",
      confirmRetranslate: "Re-translate from scratch? This overwrites your edits for this language.",
      loadFailed: "Failed to load translations.",
    },
    hub: {
      emptyTitle: "Create your first survey",
      emptyBody: "Start from scratch, generate one with AI, pick a template, or import a Word, PDF, or Qualtrics file.",
      emptyNoResults: "No surveys match your filters.",
      clearFilters: "Clear filters",
      title: "Surveys",
      createProject: "Create survey project",
      manageSurveys: "Manage surveys",
      createDialogTitle: "New project for surveys",
      projectName: "Project name",
      projectOutline: "Project outline",
      interviewType: "Interview type",
      interviewLanguage: "Interview language",
      cancel: "Cancel",
      save: "Save",
      createSuccess: "Project created",
      createError: "Create project failed",
      fillRequired: "Please fill required fields",
      loadError: "Failed to load projects",
      confirmDeleteProject: "Delete this survey project and all its surveys? This cannot be undone.",
      projectDeleted: "Project deleted successfully",
      surveysInProject: "Surveys in this project",
      noSurveysYet: "No surveys yet",
      manageProjectSurveys: "Manage Surveys",
      noProjectsYet: "No survey projects created yet",
      experimentalTitle: "Experimental Feature",
      experimentalMessage: "Conversational Survey is an experimental tool currently in active development. Some features may change or behave unexpectedly. Your feedback helps us improve!",
      search: "Search surveys",
      filterAll: "All",
      filterDraft: "Draft",
      filterPublished: "Published",
      sortNewest: "Newest first",
      sortAlpha: "Alphabetical",
      noResults: "No surveys match your search",
      analytics: "Analytics",
      duplicate: "Duplicate",
      duplicateSuccess: "Survey duplicated",
    },
    list: {
      emptyTitle: "No surveys in this project yet",
      emptyBody: "Create your first survey to start collecting responses.",
      fromTemplate: "Start from template",
      fromWizard: "Generate with AI",
      fromTemplateTitle: "Start from a template",
      fromTemplateSubtitle: "Pick a template to get a survey draft you can edit. We pre-fill questions, AI follow-up settings, and a sensible default mode.",
      templatesEmpty: "No templates available right now.",
      creatingFromTemplate: "Creating…",
      wizard: {
        title: "Generate a survey with AI",
        subtitle: "Tell Mimi what you want to learn and she'll draft a survey you can edit.",
        generating: "Mimi is drafting your survey…",
        generatingHint: "This usually takes 20-40 seconds. We're generating a tailored question set with appropriate AI follow-up settings.",
        goalLabel: "What do you want to learn?",
        goalPlaceholder: "e.g. Why customers churn after the first month — what drives the decision and what would have changed it.",
        goalHelp: "Specific goals produce better surveys. Aim for 1-3 sentences.",
        audienceLabel: "Who will take this survey?",
        audiencePlaceholder: "e.g. Customers who cancelled in the last 30 days",
        languageLabel: "Language",
        depthLabel: "Depth",
        nQuestionsLabel: "Target # of questions",
        generate: "Generate",
        errorGoalTooShort: "Goal needs to be at least 10 characters so the AI has something to work with.",
        errorAudienceTooShort: "Tell us who the audience is so the AI can pitch questions appropriately.",
      },
      title: "Survey",
      create: "Create survey",
      createDialogTitle: "New survey",
      titleField: "Title",
      descriptionField: "Description",
      cancel: "Cancel",
      save: "Save",
      builder: "Builder",
      export: "Export",
      publish: "Publish",
      status: "Status",
      linkCopied: "Share link copied",
      publishFirst: "Please publish the survey first",
      publishReady: "Survey published",
      publishError: "Publish failed",
      createSuccess: "Survey created",
      createError: "Create failed",
      exportReady: "CSV ready",
      exportError: "Export failed",
      publishFirst: "Please publish the survey first",
      error: "Failed to load surveys",
      copyLink: "Copy share link",
      downloadCsv: "Download CSV",
      deleteSurvey: "Delete survey",
      publishGetLink: "Publish & get link",
      published: "Survey published — your share link is ready.",
      questionsLabel: "questions",
      responsesLabel: "responses"
    },
    builder: {
      minDwellLabel: "Minimum view time (seconds)",
      minDwellHelp: "Respondents must stay on this page this long before Next/Submit unlocks. 0 = off.",
      saving: "Saving…",
      saved: "Saved",
      saveFailed: "Couldn't save changes",
      aiFollowUp: {
        label: "AI follow-up",
        helperNone: "No follow-up — respondents answer and move on",
        helperLight: "Light — one conversational follow-up to enrich the answer",
        helperDeep: "Deep — up to three follow-ups for in-depth qualitative insight",
        optionNone: "None — no follow-up",
        optionLight: "Light — 1 follow-up",
        optionDeep: "Deep — up to 3 follow-ups",
      },
      cancel: "Cancel",
      surveyMode: {
        label: "Survey mode",
        form: {
          title: "Traditional survey",
          subtitle: "Standard form-based survey, no AI",
        },
        aiFollowUp: {
          title: "AI follow-up survey",
          subtitle: "Form survey + AI probes on open-ended answers",
          hint: "Tip: set \"AI follow-up\" intensity on each open-ended question below. Light = 1 probe; Deep = up to 3.",
        },
        aiInterview: {
          title: "Conversational survey",
          subtitle: "AI-led conversational interview, free-flowing dialogue",
        },
      },
      preflight: {
        title: "Pre-publish check",
        loading: "Checking your survey…",
        ready: "Ready to publish",
        readyHint: "Your survey passed every check. Respondents will see a clean experience.",
        hasErrors: "Fix the issues below before publishing:",
        warningsOnly: "You can publish, but here are a few things to double-check:",
        fallback: "Could not run the health check — publishing anyway is allowed.",
        rerun: "Re-check",
      },
      constantSumItems: 'Items',
      constantSumItemsHelp: 'One item per line',
      constantSumTotal: 'Total',
      constantSumTotalHelp: 'Sum respondents must allocate (e.g. 100)',
      emailAutoConfig: 'Validates as an email address',
      fileAcceptHelp: 'Allowed file types (comma separated, e.g. .pdf,.docx)',
      fileAcceptTypes: 'Accepted types',
      fileMaxSize: 'Max file size (MB)',
      imageChoiceHelp: 'One image URL per line. Optional labels match by order.',
      imageChoiceLabels: 'Labels',
      imageChoiceMultiple: 'Allow multiple selections',
      imageChoiceUrls: 'Image URLs',
      phoneAutoConfig: 'Validates as a phone number',
      ratingMax: 'Maximum rating',
      sectionBreakInfo: 'Visual separator only — no answer is collected.',
      thankYouPage: 'Thank-you page',
      thankYouRedirectHelp: 'Optional URL to redirect to after submission',
      yesNoAutoConfig: 'Yes / No choice',
      loadError: "Failed to load survey",
      saveMeta: "Survey updated",
      metaSaved: "Survey details saved",
      saveError: "Save failed",
      addError: "Add question failed",
      questionAdded: "Question added",
      saveQuestion: "Question saved",
      questionSaved: "Question saved successfully",
      questionDeleted: "Question deleted",
      deleteError: "Delete failed",
      reorderError: "Reorder failed",
      confirmDelete: "Delete this question?",
      deleteSurvey: "Delete Survey",
      confirmDeleteSurvey: "Are you sure you want to delete this survey? This cannot be undone.",
      surveyDeleted: "Survey deleted successfully",
      surveyFlow: "Survey Flow",
      addBlock: "Add Block",
      addRandomizer: "Add Randomizer",
      blockPlaceholder: "Untitled Block",
      deleteBlock: "Delete Block",
      confirmDeleteBlock: "Delete this block? Its questions will become unassigned.",
      randomizer: "Randomizer",
      randomizerShow: "Show",
      randomizerOf: "of",
      addVariant: "Add Variant",
      questionsCount: "{n} question(s)",
      unassigned: "Unassigned Questions",
      noBlocks: "No blocks yet",
      renameBlock: "Rename",
      addQuestion: "Add",
      publishCopied: "Published and link copied",
      publishSuccess: "Survey published successfully",
      publishError: "Publish failed",
      linkCopied: "Link copied to clipboard",
      questions: "Questions",
      noQuestions: "No questions yet",
      clickToAdd: "Click the button below to add your first question",
      addFirstQuestion: "Add Question",
      untitledQuestion: "(Untitled question)",
      promptPlaceholder: "Enter your question here...",
      surveyTitle: "Survey Title",
      surveyDescription: "Survey Description",
      noDescription: "No description",
      questionType: "Question Type",
      variableName: "Short ID",
      variableHelp: "A short unique nickname for this question (letters, numbers, underscores). Example: favorite_color",
      pipingChip: "Insert a past answer",
      pipingTooltipTitle: "Show a past answer inside this question",
      pipingTooltipBody: "Click a tag to copy it, then paste into your question text. When the respondent sees it, it becomes their earlier answer.",
      // Share & Distribution (Phase 6)
      share: "Share",
      shareTitle: "Share your survey",
      tabLink: "Link / QR",
      tabEmbed: "Embed",
      tabSocial: "Social",
      tabProtect: "Protect",
      copy: "Copy",
      copied: "Copied",
      linkCopied: "Link copied",
      qrHint: "Scan with a phone camera to open the survey",
      downloadQR: "Download QR code (PNG)",
      embedHelp: "Paste this HTML into your website to embed the survey.",
      copyEmbed: "Copy embed code",
      embedCopied: "Embed code copied",
      preview: "Preview",
      socialHelp: "Share your survey on social platforms:",
      shareOn: "Share on",
      passwordProtection: "Password protection",
      passwordHint: "Require respondents to enter a password before starting.",
      passwordActive: "A password is set. Enter a new one below to change it, or clear to remove.",
      passwordPlaceholder: "Leave blank for no password",
      passwordSaved: "Password updated",
      passwordRemoved: "Password removed",
      removePassword: "Remove password",
      apply: "Apply",
      schedule: "Schedule",
      scheduleHelp: "Automatically open and close your survey at specific times. Leave blank for always open.",
      openAt: "Opens at",
      closeAt: "Closes at",
      saveSchedule: "Save schedule",
      scheduleSaved: "Schedule saved",
      saveError: "Save failed",
      // Theming (Phase 5)
      themeBranding: "Theme & Branding",
      themeBrandingHelp: "Customize the look and feel of the survey page respondents see.",
      themePresets: "Quick presets",
      primaryColor: "Primary color",
      backgroundColor: "Background",
      textColor: "Text color",
      fontFamily: "Font style",
      fontSystem: "System default (modern)",
      fontSerif: "Serif (classic)",
      fontRounded: "Rounded (friendly)",
      fontMono: "Monospace (technical)",
      logoUrl: "Logo image URL",
      logoUrlHelp: "Shown above the survey title. Optional.",
      headerImageUrl: "Header banner URL",
      headerImageUrlHelp: "Wide banner image at the top. Optional.",
      themePreview: "Preview",
      sampleSurveyTitle: "Your Survey Title",
      sampleQuestionText: "Here is what a question looks like.",
      sampleSubmit: "Submit",
      resetTheme: "Reset to default",
      requiredQuestion: "Required question",
      isConversational: "Ask conversationally",
      askForm: "Ask by filling form",
      optionsPlaceholder: "Option 1\nOption 2\nOption 3",
      minValue: "Minimum value",
      maxValue: "Maximum value",
      saveChanges: "Save Changes",
      moreOptions: "more options",
      moveUp: "Move up",
      moveDown: "Move down",
      moveToBlock: "Move to block",
      expand: "Expand",
      collapse: "Collapse",
      delete: "Delete",
      duplicate: "Duplicate",
      duplicateSuccess: "Question duplicated",
      republish: "Republish",
      unpublish: "Unpublish",
      unpublishing: "Unpublishing…",
      unpublishSuccess: "Survey unpublished. It is now a draft.",
      unpublishError: "Failed to unpublish survey",
      unpublishTooltip: "Block new participants. Existing responses are kept; the share link is preserved for re-publish.",
      confirmUnpublish: "Unpublish this survey? New participants will be blocked. Existing responses are kept. You can re-publish later with the same link.",
      copyLink: "Copy share link",
      meta: "Survey info",
      titleField: "Title",
      descriptionField: "Description",
      descriptionPlaceholder: "Describe the purpose of this survey...",
      language: "Language",
      save: "Save",
      imageUrl: "Image URL",
      imageUrlHelp: "Optional: Enter a URL to display an image",
      imagePreview: "Preview",
      imageLoadError: "Failed to load image. Please check the URL.",
      addImage: "Add image",
      questionImage: "Question image",
      removeImage: "Remove image",
      uploadImage: "Upload image",
      uploading: "Uploading…",
      imageUrlPlaceholder: "Or paste image URL",
      imageHelp: "PNG, JPEG, GIF, or WebP. Max 2MB.",
      imageUploadFailed: "Upload failed.",
      uploadImages: "Upload images",
      addMoreImages: "Add more",
      urlPasteHint: "Press Enter to add",
      removeAllImages: "Remove all",
      itemsToRank: "Items to Rank",
      itemsToRankHelp: "Enter items to be ranked, one per line",
      bipolarLabels: "Scale Labels",
      leftLabel: "Left Label",
      rightLabel: "Right Label",
      surveyLanguage: "Survey Language",
      surveyLanguageHelp: "Select 'Flexible' to let participants choose their language",
      flexible: "Flexible (Participant chooses)",
      publish: "Publish & Copy Link",
      editor: "Question editor",
      selectPrompt: "Select a question to edit",
      prompt: "Prompt",
      variable: "Variable name",
      required: "Required",
      options: "Options (one per line)",
      optionsHelp: "Enter one option per line",
      min: "Min",
      max: "Max",
      saveQuestionBtn: "Save question",
      types: {
        image: "Image",
        short_text: "Short Answer",
        long_text: "Paragraph",
        single_choice: "Multiple Choice",
        multi_choice: "Checkboxes",
        number: "Number",
        date: "Date",
        ranking: "Ranking",
        bipolar_scale: "Bipolar Scale",
        info_text: "Information Text",
        slider: "Slider",
        dropdown: "Dropdown",
        nps: "Net Promoter Score",
        matrix: "Matrix",
        ai_interview: "AI Interview",
        rating_scale: "Rating Scale",
        yes_no: "Yes / No",
        email: "Email",
        phone: "Phone Number",
        image_choice: "Image Choice",
        file_upload: "File Upload",
        constant_sum: "Constant Sum",
        section_break: "Section Break",
        page_break: "Page Break"
      },
      typeDescriptions: {
        short_text: "Brief text response",
        long_text: "Long-form text response",
        single_choice: "Choose one option",
        multi_choice: "Select multiple options",
        number: "Numeric value",
        date: "Date picker",
        ranking: "Rank items in order",
        bipolar_scale: "Scale between two opposites",
        info_text: "Display text or images (no answer required)",
        slider: "Slide to select a value in a range",
        dropdown: "Select one option from a dropdown list",
        nps: "Rate from 0 to 10 (Net Promoter Score)",
        matrix: "Rate multiple items on the same scale",
        ai_interview: "Embed an AI-powered interview conversation",
        rating_scale: "Star rating (e.g. 1-5 stars)",
        yes_no: "Simple yes or no question",
        email: "Email address with validation",
        phone: "Phone number with validation",
        image_choice: "Select from image options",
        file_upload: "Upload a file attachment",
        constant_sum: "Distribute points across items",
        section_break: "Visual separator between sections",
        page_break: "Starts a new page for respondents"
      },
      stepValue: "Step",
      npsAutoConfig: "NPS is automatically configured as a 0-10 scale. No extra configuration needed.",
      matrixRows: "Row Labels (one per line)",
      matrixRowsHelp: "Enter the items to rate, one per line",
      matrixColumns: "Column Labels (one per line)",
      matrixColumnsHelp: "Enter the scale options, one per line (e.g., Strongly Disagree, Disagree, Neutral, Agree, Strongly Agree)",
      interviewConfig: {
        title: "AI Interview Settings",
        interactionMode: "Interaction Mode",
        voiceToVoice: "Voice to Voice",
        voiceToText: "Voice to Text",
        textToText: "Text to Text",
        textToVoice: "Text to Voice",
        language: "Interview Language",
        maxDuration: "Max Duration (minutes)",
        outline: "Interview Outline / Prompt",
        outlinePlaceholder: "Describe what the AI should ask about...",
        outlineHelp: "The AI interviewer will follow this outline to guide the conversation.",
      },
      defaultPrompt: "New question",
      preview: "Preview",
      previewMode: "Preview Mode",
      conditions: "Conditions",
      addCondition: "Add condition",
      removeCondition: "Remove",
      conditionLogicAnd: "AND (all must match)",
      conditionLogicOr: "OR (any can match)",
      conditionQuestion: "If question",
      conditionOperator: "Operator",
      conditionValue: "Value",
      operatorEquals: "equals",
      operatorNotEquals: "does not equal",
      operatorContains: "contains",
      operatorGreaterThan: "is greater than",
      operatorLessThan: "is less than",
      operatorIsEmpty: "is empty",
      operatorIsNotEmpty: "is not empty",
      pipingHelp: "Tip: Use ${variable_name} to insert a previous answer",
      availableVariables: "Available: {{variables}}",
      randomizeQuestions: "Randomize question order",
      randomizeOptions: "Randomize option order",
      thankYouSettings: "Thank You Page",
      thankYouTitle: "Title",
      thankYouMessage: "Message",
      thankYouRedirect: "Redirect URL (optional)",
      maxResponses: "Max responses (optional)",
      maxResponsesHelp: "Leave empty for unlimited",
      deliveryMode: "Delivery Mode",
      deliveryConversational: "Conversational (AI Interview)",
      deliveryForm: "Standard Form",
      deliveryModeHelp: "Conversational mode uses AI to conduct interviews. Standard form mode shows all questions at once.",
      testWithSynthetic: "Test with Synthetic Human",
      fromLibrary: "From Library",
      fromLibraryHint: "Copy from pre-built templates & bundles",
      templateLibrary: "Template Library",
      templates: "Templates",
      bundles: "Bundles",
      allTemplates: "All Templates",
      myTemplates: "My Templates",
      allCategories: "All Categories",
      selectTemplate: "Select a template",
      templateName: "Template Name",
      category: "Category",
      search: "Search",
      preview: "Preview",
      inserting: "Inserting...",
      insertTemplate: "Insert Template",
      insertBundle: "Insert Bundle",
      noTemplates: "No templates found",
      noBundles: "No bundles found",
      templateInsertedSuccess: "Template inserted successfully",
      bundleInsertedSuccess: "Bundle inserted successfully",
      templateInsertError: "Failed to insert template",
      cancel: "Cancel",
      skipLogic: "Skip Logic",
      skipLogicHelp: "Show this question only when conditions are met",
      displayLogic: "Display Logic",
      displayLogicHelp: "Show this question only when previous answers match these conditions",
      conditionIf: "If",
      logicTab: "Logic",
      noLogicYet: "No skip logic configured",
      noLogicHint: "Click a question and scroll to the Skip Logic section to add conditions.",
      logicSummaryPrefix: "Show this question when",
      settings: "Survey Settings",
    },
    export: {
      loadError: "Failed to load survey",
      description: "Download submitted responses as CSV.",
      button: "Download CSV",
      success: "Export ready",
      error: "Export failed",
      xlsx: "Download XLSX"
    },
    analytics: {
      filterScopeNote: "This date filter only narrows the response list and cross-tabs. The summary cards, charts and AI summary always reflect all responses.",
      clearFilter: 'Clear filter',
      columnQuestion: 'Column question',
      crossTab: 'Cross-tab',
      crossTabHelp: 'Compare how answers to one question break down by another.',
      crossTabNeedTwo: 'Pick two questions to compare.',
      crossTabPickTwo: 'Select a row and a column question',
      crossTabTitle: 'Cross-tabulation',
      duration: 'Duration',
      filterBy: 'Filter by',
      from: 'From',
      individualResponses: 'Individual responses',
      language: 'Language',
      next: 'Next',
      noAnswer: 'No answer',
      noResponsesYet: 'No responses yet',
      deleteResponse: 'Delete response',
      confirmDeleteResponse: 'Delete this response permanently? This cannot be undone.',
      responseDeleted: 'Response deleted.',
      deleteFailed: 'Failed to delete response.',
      download: 'Download',
      downloadCsv: 'Download CSV',
      downloadXlsx: 'Download Excel (XLSX)',
      exportFailed: 'Export failed.',
      prev: 'Previous',
      respondent: 'Respondent',
      rowQuestion: 'Row question',
      sampleResponses: 'Sample responses',
      submittedAt: 'Submitted at',
      to: 'To',
      viewResponses: 'View responses',
      wordCloud: 'Word cloud',
      title: "Survey Analytics",
      totalResponses: "Total Responses",
      completionRate: "Completion Rate",
      avgDuration: "Avg. Duration",
      minutes: "minutes",
      responsesOverTime: "Responses Over Time",
      questionBreakdown: "Question Breakdown",
      responses: "responses",
      noData: "No responses yet",
      refresh: "Refresh",
      detractors: "Detractors (0-6)",
      passives: "Passives (7-8)",
      promoters: "Promoters (9-10)"
    },
    start: {
      untitledSurvey: "Untitled survey",
      timeEstimate: "About {{minutes}} {{unit}}",
      questionCount: "{{count}} {{unit}}",
      minute: "minute",
      minutes: "minutes",
      question: "question",
      questions: "questions",
      privacyNote: "Your responses are confidential",
      startButton: "Start survey",
      selectLanguageFirst: "Please select a language to continue.",
      title: "Start survey",
      subtitle: "Launch the conversational survey.",
      button: "Start",
      loading: "Starting survey...",
      error: "Unable to start session",
      selectLanguage: "Select Your Language",
      selectLanguageSubtitle: "Choose the language for your survey session",
      surveyFull: "This survey has reached its response limit",
      // Friendly state pages (shown before language selection / session start)
      state: {
        closed: {
          title: "This survey is closed",
          message: "The survey is no longer accepting responses."
        },
        not_open_yet: {
          title: "This survey hasn't opened yet",
          message: "The survey is not yet open for responses. Please check back later."
        },
        full: {
          title: "This survey has reached its response limit",
          message: "Thanks for your interest! The survey is no longer accepting new responses."
        },
        unpublished: {
          title: "This survey is not yet published",
          message: "The researcher has not finished setting up this survey yet."
        },
        invalid: {
          title: "This survey link is invalid",
          message: "The link you used may be incorrect or expired. Please contact the researcher who sent it."
        }
      },
      // Password prompt (shown when survey has access_password)
      passwordRequired: {
        title: "Password required",
        hint: "This survey is protected. Enter the password the researcher shared with you.",
        placeholder: "Password",
        submit: "Continue",
        wrong: "Incorrect password. Please try again."
      }
    },
    session: {
      dwellWait: "You can continue in {{n}}s",
      redirectingIn: "Redirecting you in {seconds}…",
      continueNow: "Continue now",
      cancelRedirect: "Cancel",
      redirectCancelled: "Auto-redirect cancelled.",
      continueToSite: "Continue to the site",
      loadError: "Failed to load session",
      connected: "Connected to assistant",
      connectionError: "Connection error",
      reconnecting: "Reconnecting to assistant...",
      connectionLost: "Connection lost. Please refresh the page.",
      typeHere: "Type your answer here...",
      send: "Send",
      wsError: "Chat connection failed",
      answersSaved: "Answers saved",
      saveError: "Save failed",
      submitted: "Submitted",
      submitError: "Submit failed",
      progress: "Progress: {{answered}} / {{total}} required",
      placeholder: "Type your answer",
      send: "Send",
      verification: "Verification",
      required: "Required",
      saveEdits: "Save edits",
      submit: "Approve & Submit",
      loading: "Loading...",
      processing: "Processing your response...",
      stopRecording: "Stop Recording",
      pressToSpeak: "Press to Speak",
      reviewSubmit: "Review & Submit",
      formMode: "Standard Form",
      conversationalMode: "AI Interview",
      voicePlayback: "Voice",
      switchToForm: "Switch to Form",
      switchToConversational: "Switch to AI Interview",
      answerMore: "Answer {{count}} more required question(s)",
      transcribeFailed: "Could not transcribe audio. Please try again.",
      audioProcessError: "Audio processing failed: {{error}}",
      micError: "Cannot access microphone: {{error}}",
      rankingPlaceholder: "Enter ranking order (e.g., A > B > C)",
      thankYouTitle: "Thank You!",
      thankYouMessage: "Your responses have been submitted successfully. We appreciate your time and feedback.",
      returnHome: "Return to Home",
      status_in_progress: "In Progress",
      pageOf: "Page {current} of {total}",
      nextPage: "Next",
      previousPage: "Back",
      status_submitted: "Submitted",
      status_awaiting_verification: "Under Review",
      previewBanner: "[PREVIEW MODE] This is a preview. Responses will not be saved to analytics.",
      redirecting: "Redirecting...",
      progressLabel: "Progress",
      autoSaving: "Saving…",
      autoSaved: "Saved",
      saveAndContinue: "Save & continue later",
      saveAndContinueTooltip: "Save your progress and get a link to continue later",
      saveAndContinueTitle: "Save your progress",
      saveAndContinueBody: "Your answers have been saved. Bookmark or email yourself this link to continue where you left off — from any device.",
      copyLink: "Copy link",
      linkCopied: "Link copied to clipboard",
      close: "Close",
      alreadySubmittedWarning: "Our records show you may have already submitted this survey. You can still continue, but duplicate responses may be excluded.",
      requiredField: "required",
      fieldRequired: "{label} is required",
      invalidEmail: "Please enter a valid email address",
      invalidPhone: "Please enter a valid phone number",
      invalidNumber: "Please enter a valid number",
      sumMustEqual: "Values must sum to {total} (current: {sum})",
      npsNotLikely: "Not at all likely",
      npsExtremely: "Extremely likely",
      chooseFile: "Choose File",
      chooseFileDrag: "Choose file or drag & drop",
      fileSelected: "File selected",
      selectedPrefix: "Selected:",
      acceptedPrefix: "Accepted:",
      fileTooLarge: "File size must be less than {size}MB",
      constantSumInstruction: "Allocate a total of {total} points across the items below.",
      remaining: "Remaining:",
      pageBreakLabel: "Page Break",
      noFormConfigured: "No form configured",
      noFormConfiguredBody: "The project owner hasn't set up a custom form yet.",
      unsupportedField: "Unsupported field type: {type}",
      imageNotConfigured: "Image URL not configured",
      videoNotConfigured: "Video URL not configured",
      dragToReorder: "Drag to reorder",
      moveUp: "Move up",
      moveDown: "Move down",
      requiredSummary: "Please answer the highlighted question(s) before continuing.",
      yes: "Yes",
      no: "No",
      optionN: "Option {n}",
      surveyImageAlt: "Survey information",
      interview: {
        title: "AI Interview",
        description: "This question includes a short AI interview.",
        start: "Start Interview",
        starting: "Starting interview...",
        ending: "Saving interview...",
        completed: "Interview Completed",
        end: "End",
        stopRecording: "Stop Recording",
        tapToSpeak: "Tap to Speak",
        typePlaceholder: "Type your answer...",
      },
    },
    languages: {
      en: "English",
      zh: "Chinese",
      fr: "French",
      de: "German",
      es: "Spanish",
      nb: "Norwegian",
      nl: "Dutch",
      pt: "Portuguese",
      ja: "Japanese",
      ar: "Arabic",
      flexible: "Flexible (Participant chooses)"
    },
    synthetic: {
      dialogTitle: "Test with Synthetic Participant",
      dialogSubtitle: "Configure the demographic profile of your simulated participant",
      age: "Age",
      gender: "Gender",
      male: "Male",
      female: "Female",
      nonBinary: "Non-binary",
      preferNotToSay: "Prefer not to say",
      nationality: "Nationality",
      ethnicity: "Ethnicity",
      education: "Education",
      highSchool: "High school",
      bachelors: "Bachelor's degree",
      masters: "Master's degree",
      phd: "PhD / Doctorate",
      vocational: "Vocational / Trade",
      other: "Other",
      occupation: "Occupation",
      personality: "Personality traits",
      additionalContext: "Additional context (optional)",
      runTest: "Run Test",
      cancel: "Cancel",
      simulating: "Simulating participant responses...",
      simulatingHint: "This may take 15-30 seconds",
      resultsTitle: "Synthetic Test Results",
      syntheticPersona: "Synthetic Participant Profile",
      synthetic: "Synthetic",
      question: "Question",
      type: "Type",
      answer: "Answer",
      close: "Close",
    },
    status: {
      draft: "Draft",
      published: "Published",
      archived: "Archived"
    }
  },
  zh: {
    tutorial: {
      title: "快速上手",
      step1Title: "欢迎使用问卷构建器",
      step1Body: "构建对话式问卷，让 AI 提出有针对性的追问并适应每位受访者 — 远比静态表单更丰富。",
      step2Title: "添加你的问题",
      step2Body: "添加任意类型的问题 — 单选、量表、开放文本 — 并拖动重新排序。使用分页和分节来设计流程。",
      step3Title: "配置 AI",
      step3Body: "设置语言、语气，以及 AI 对开放式回答追问的深度。你还可以自动翻译整份问卷。",
      step4Title: "分享与分析",
      step4Body: "发布并分享你的链接，随着参与者完成问卷，实时查看回复与分析。",
      back: "上一步",
      next: "下一步",
      done: "开始使用",
      skip: "跳过",
      help: "使用说明",
    },
    import: {
      menuLabel: "导入文件", reviewTitle: "审核导入的问卷",
      reviewIntro: "我们从你的文件中草拟了 {{n}} 个问题。请在下方审核，然后创建问卷——你可以在编辑器中完整编辑。",
      cancel: "取消", titleField: "问卷标题", descriptionField: "描述",
      reportTitle: "导入提示", preview: "问题", pageBreak: "分页符",
      sectionBreak: "分节", required: "必填", create: "创建问卷",
      parseFailed: "无法导入此文件。", createFailed: "无法创建问卷。",
      uploadTitle: "导入问卷", parsing: "正在读取文件…",
      dropHere: "将文件拖到此处，或点击浏览",
      dropHint: "最大 25 MB。我们会生成一份可供你审核和编辑的问卷。",
      supported: "支持的格式",
      formatNote: "AI 会把 Word 和 PDF 转换为问卷——既可逐字录入现有问卷，也能根据你的笔记或简介起草问卷。Qualtrics .qsf 与 QualiTaTi .qlt 会被精确解析。",
      exportDefinition: "下载问卷", exportWord: "下载为 Word (.docx)", exportQlt: "下载为 QualiTaTi (.qlt)", exportFailed: "导出失败。",
    },
    translations: {
      menuLabel: "翻译", title: "问卷翻译",
      intro: "添加一种语言即可用 AI 自动翻译整份问卷，然后逐条审核和编辑。受访者在开始时选择语言；留空的内容会回退到默认语言（{{lang}}）。",
      source: "原文", addLanguage: "添加语言",
      empty: "尚无语言。点击「添加语言」以创建 AI 翻译。",
      surveyInfo: "问卷信息", fieldTitle: "标题", fieldDescription: "描述",
      fieldThankYouTitle: "致谢标题", fieldThankYouMessage: "致谢信息",
      fieldPrompt: "问题", options: "选项", columns: "量表列",
      retranslate: "重新翻译", close: "关闭", save: "保存",
      translated: "自动翻译完成。请在下方审核和编辑。", translateFailed: "翻译失败。",
      saved: "翻译已保存。", saveFailed: "保存失败。",
      confirmDelete: "移除此语言及其所有翻译？", deleteFailed: "删除失败。",
      confirmRetranslate: "重新从头翻译？这将覆盖你对该语言的编辑。",
      loadFailed: "加载翻译失败。",
    },
    hub: {
      emptyTitle: "创建你的第一份问卷",
      emptyBody: "从空白开始、用 AI 生成、选择模板，或导入 Word、PDF 或 Qualtrics 文件。",
      emptyNoResults: "没有符合筛选条件的问卷。",
      clearFilters: "清除筛选",
      title: "问卷",
      createProject: "创建问卷项目",
      manageSurveys: "管理问卷",
      createDialogTitle: "新建问卷项目",
      projectName: "项目名称",
      projectOutline: "项目大纲",
      interviewType: "访谈类型",
      interviewLanguage: "访谈语言",
      cancel: "取消",
      save: "保存",
      createSuccess: "项目已创建",
      createError: "创建项目失败",
      fillRequired: "请填写必填项",
      loadError: "加载项目失败",
      confirmDeleteProject: "确定删除此问卷项目及其所有问卷吗？此操作无法撤销。",
      projectDeleted: "项目已成功删除",
      surveysInProject: "该项目中的问卷",
      noSurveysYet: "暂无问卷",
      manageProjectSurveys: "管理问卷",
      noProjectsYet: "尚未创建问卷项目",
      experimentalTitle: "实验功能",
      experimentalMessage: "对话式问卷是一个正在积极开发中的实验性工具。部分功能可能会更改或出现意外行为。您的反馈有助于我们改进！",
      search: "搜索问卷",
      filterAll: "全部",
      filterDraft: "草稿",
      filterPublished: "已发布",
      sortNewest: "最新优先",
      sortAlpha: "按字母排序",
      noResults: "没有匹配的问卷",
      analytics: "分析",
      duplicate: "复制",
      duplicateSuccess: "问卷已复制",
    },
    list: {
      emptyTitle: "此项目中还没有问卷",
      emptyBody: "创建你的第一份问卷，开始收集回复。",
      fromTemplate: "从模板开始",
      fromWizard: "用 AI 生成",
      fromTemplateTitle: "从模板开始",
      fromTemplateSubtitle: "选择一个模板获取可编辑的问卷草稿。我们会预填问题、AI 追问设置和合理的默认模式。",
      templatesEmpty: "当前没有可用模板。",
      creatingFromTemplate: "正在创建…",
      wizard: {
        title: "用 AI 生成问卷",
        subtitle: "告诉 Mimi 您想了解什么，她会起草一份您可编辑的问卷。",
        generating: "Mimi 正在为您起草问卷…",
        generatingHint: "通常需要 20-40 秒。我们正在生成一份匹配 AI 追问设置的问题集。",
        goalLabel: "您想了解什么？",
        goalPlaceholder: "例如：客户在第一个月后流失的原因——是什么驱动了这一决定，什么本可以改变这一结果。",
        goalHelp: "目标越具体，问卷越好。建议 1-3 句话。",
        audienceLabel: "谁将完成这份问卷？",
        audiencePlaceholder: "例如：过去 30 天内取消订阅的客户",
        languageLabel: "语言",
        depthLabel: "深度",
        nQuestionsLabel: "目标问题数",
        generate: "生成",
        errorGoalTooShort: "目标至少需要 10 个字符，AI 才能据此生成。",
        errorAudienceTooShort: "请告知受众群体，AI 才能合理设计问题。",
      },
      title: "问卷",
      create: "新建问卷",
      createDialogTitle: "创建问卷",
      titleField: "标题",
      descriptionField: "描述",
      cancel: "取消",
      save: "保存",
      builder: "编辑器",
      export: "导出",
      publish: "发布",
      status: "状态",
      linkCopied: "分享链接已复制",
      publishFirst: "请先发布问卷",
      publishReady: "发布成功",
      publishError: "发布失败",
      createSuccess: "创建成功",
      createError: "创建失败",
      exportReady: "CSV 已准备",
      exportError: "导出失败",
      error: "加载问卷失败",
      copyLink: "复制分享链接",
      downloadCsv: "下载CSV",
      deleteSurvey: "删除问卷",
      publishGetLink: "发布并获取链接",
      published: "问卷已发布，分享链接已就绪。",
      questionsLabel: "个问题",
      responsesLabel: "份回复"
    },
    builder: {
      minDwellLabel: "最短停留时间（秒）",
      minDwellHelp: "受访者必须在此页停留这么久后才能点击下一步/提交。0 = 关闭。",
      saving: "保存中…",
      saved: "已保存",
      saveFailed: "无法保存更改",
      aiFollowUp: {
        label: "AI 追问",
        helperNone: "无追问 — 受访者作答后直接进入下一题",
        helperLight: "Light — 一次对话式追问，丰富回答",
        helperDeep: "Deep — 最多三次追问，获得深度质性洞察",
        optionNone: "无 — 不追问",
        optionLight: "Light — 1 次追问",
        optionDeep: "Deep — 最多 3 次追问",
      },
      cancel: "取消",
      surveyMode: {
        label: "问卷模式",
        form: {
          title: "传统问卷",
          subtitle: "标准表单式问卷，无 AI",
        },
        aiFollowUp: {
          title: "AI 追问问卷",
          subtitle: "表单问卷 + 对开放式回答进行 AI 追问",
          hint: "提示：在下方每个开放式问题上设置「AI 追问」强度。Light = 1 次追问；Deep = 最多 3 次。",
        },
        aiInterview: {
          title: "对话式问卷",
          subtitle: "AI 主导的对话式访谈，自由对话",
        },
      },
      preflight: {
        title: "发布前检查",
        loading: "正在检查您的问卷…",
        ready: "可以发布了",
        readyHint: "您的问卷通过了所有检查。受访者将看到流畅的体验。",
        hasErrors: "发布前请先解决以下问题：",
        warningsOnly: "可以发布，但建议先确认以下几点：",
        fallback: "无法运行健康检查 — 仍允许发布。",
        rerun: "重新检查",
      },
      pipingChip: '插入以往的回答',
      pipingTooltipTitle: '在此问题中显示以往的回答',
      pipingTooltipBody: '点击一个标签进行复制，然后粘贴到你的问题文本中。受访者看到时，它会变成他们之前的回答。',
      constantSumItems: '项目',
      constantSumItemsHelp: '每行一个项目',
      constantSumTotal: '总计',
      constantSumTotalHelp: '受访者必须分配的总和（例如 100）',
      emailAutoConfig: '按电子邮件地址格式验证',
      fileAcceptHelp: '允许的文件类型（逗号分隔，例如 .pdf,.docx）',
      fileAcceptTypes: '接受的类型',
      fileMaxSize: '最大文件大小（MB）',
      imageChoiceHelp: '每行一个图片 URL。可选标签按顺序对应。',
      imageChoiceLabels: '标签',
      imageChoiceMultiple: '允许多选',
      imageChoiceUrls: '图片 URL',
      phoneAutoConfig: '按电话号码格式验证',
      ratingMax: '最高评分',
      sectionBreakInfo: '仅作视觉分隔 — 不收集回答。',
      thankYouPage: '感谢页',
      thankYouRedirectHelp: '提交后可选跳转的 URL',
      yesNoAutoConfig: '是 / 否 选项',
      loadError: "加载问卷失败",
      saveMeta: "问卷已更新",
      metaSaved: "问卷信息已保存",
      saveError: "保存失败",
      addError: "添加问题失败",
      questionAdded: "问题已添加",
      questionSaved: "问题保存成功",
      questionDeleted: "问题已删除",
      reorderError: "排序失败",
      confirmDelete: "确定删除此问题？",
      deleteSurvey: "删除问卷",
      confirmDeleteSurvey: "确定要删除此问卷吗？此操作无法撤销。",
      surveyDeleted: "问卷已成功删除",
      surveyFlow: "问卷流程",
      addBlock: "添加模块",
      addRandomizer: "添加随机器",
      blockPlaceholder: "未命名模块",
      deleteBlock: "删除模块",
      confirmDeleteBlock: "删除此模块？其中的题目将变为未分配状态。",
      randomizer: "随机器",
      randomizerShow: "显示",
      randomizerOf: "个，共",
      addVariant: "添加变体",
      questionsCount: "{n}道题",
      unassigned: "未分配问题",
      noBlocks: "暂无模块",
      renameBlock: "重命名",
      addQuestion: "添加",
      publishSuccess: "问卷发布成功",
      linkCopied: "链接已复制到剪贴板",
      noQuestions: "暂无问题",
      clickToAdd: "点击下方按钮添加第一个问题",
      addFirstQuestion: "添加问题",
      untitledQuestion: "(未命名问题)",
      promptPlaceholder: "在此输入您的问题...",
      surveyTitle: "问卷标题",
      surveyDescription: "问卷描述",
      noDescription: "暂无描述",
      questionType: "问题类型",
      variableName: "变量名",
      variableHelp: "使用下划线命名法；必须唯一",
      requiredQuestion: "必答题",
      isConversational: "对话式提问",
      askForm: "表单式提问",
      optionsPlaceholder: "选项1\n选项2\n选项3",
      minValue: "最小值",
      maxValue: "最大值",
      saveChanges: "保存更改",
      moreOptions: "个选项",
      moveUp: "上移",
      moveDown: "下移",
      moveToBlock: "移动到区块",
      expand: "展开",
      collapse: "折叠",
      delete: "删除",
      duplicate: "复制",
      duplicateSuccess: "问题已复制",
      republish: "重新发布",
      unpublish: "取消发布",
      unpublishing: "正在取消发布…",
      unpublishSuccess: "已取消发布，问卷现为草稿状态。",
      unpublishError: "取消发布失败",
      unpublishTooltip: "阻止新参与者参加。已收集的回复保留，分享链接保留以便重新发布。",
      confirmUnpublish: "确认取消发布此问卷？新参与者将无法进入。已收集的回复将保留，您可以稍后用相同链接重新发布。",
      copyLink: "复制分享链接",
      saveQuestion: "问题已保存",
      deleteError: "删除失败",
      publishCopied: "已发布并复制链接",
      publishError: "发布失败",
      linkCopied: "已复制链接",
      questions: "问题列表",
      meta: "问卷信息",
      titleField: "标题",
      descriptionField: "描述",
      descriptionPlaceholder: "描述此问卷的目的...",
      language: "语言",
      save: "保存",
      imageUrl: "图片网址",
      imageUrlHelp: "可选：输入图片网址以显示图片",
      imagePreview: "预览",
      imageLoadError: "加载图片失败。请检查网址。",
      addImage: "添加图片",
      questionImage: "问题图片",
      removeImage: "删除图片",
      uploadImage: "上传图片",
      uploading: "上传中…",
      imageUrlPlaceholder: "或粘贴图片URL",
      imageHelp: "PNG、JPEG、GIF或WebP。最大2MB。",
      imageUploadFailed: "上传失败。",
      uploadImages: "上传图片",
      addMoreImages: "添加更多",
      urlPasteHint: "按回车键添加",
      removeAllImages: "全部删除",
      itemsToRank: "待排序项目",
      itemsToRankHelp: "输入需要排序的项目，每行一个",
      bipolarLabels: "量表标签",
      leftLabel: "左侧标签",
      rightLabel: "右侧标签",
      surveyLanguage: "问卷语言",
      surveyLanguageHelp: "选择'灵活'以让参与者选择他们的语言",
      flexible: "灵活（参与者选择）",
      titleField: "标题",
      descriptionField: "描述",
      save: "保存",
      publish: "发布",
      editor: "问题编辑",
      selectPrompt: "选择一个问题进行编辑",
      prompt: "问题文案",
      variable: "变量名",
      variableHelp: "仅限 snake_case，问卷内唯一",
      required: "必填",
      options: "选项（每行一个）",
      optionsHelp: "用于单选/多选",
      min: "最小值",
      max: "最大值",
      saveQuestionBtn: "保存问题",
      types: {
        image: "图片",
        short_text: "简答题",
        long_text: "段落题",
        single_choice: "单选题",
        multi_choice: "多选题",
        number: "数字题",
        date: "日期题",
        ranking: "排序题",
        bipolar_scale: "双极量表",
        info_text: "信息文本",
        slider: "滑块",
        dropdown: "下拉菜单",
        nps: "净推荐值",
        matrix: "矩阵",
        ai_interview: "AI 访谈",
        rating_scale: "评分量表",
        yes_no: "是/否",
        email: "电子邮件",
        phone: "电话号码",
        image_choice: "图片选择",
        file_upload: "文件上传",
        constant_sum: "固定总和",
        section_break: "分节符",
        page_break: "分页符"
      },
      typeDescriptions: {
        short_text: "简短文本回答",
        long_text: "长文本回答",
        single_choice: "选择一个选项",
        multi_choice: "选择多个选项",
        number: "数字值",
        date: "日期选择",
        ranking: "按顺序排列项目",
        bipolar_scale: "在两个对立面之间评分",
        info_text: "显示文本或图片（无需回答）",
        slider: "拖动滑块选择范围内的值",
        dropdown: "从下拉列表中选择一个选项",
        nps: "从0到10评分（净推荐值）",
        matrix: "对多个项目使用同一量表评分",
        ai_interview: "嵌入AI驱动的访谈对话",
        rating_scale: "星级评分（如1-5星）",
        yes_no: "简单的是或否问题",
        email: "带验证的电子邮件地址",
        phone: "带验证的电话号码",
        image_choice: "从图片选项中选择",
        file_upload: "上传文件附件",
        constant_sum: "在各项之间分配分数",
        section_break: "各部分之间的视觉分隔符",
        page_break: "为受访者开始新的一页"
      },
      stepValue: "步长",
      npsAutoConfig: "NPS 自动配置为 0-10 量表，无需额外配置。",
      matrixRows: "行标签（每行一个）",
      matrixRowsHelp: "输入需要评分的项目，每行一个",
      matrixColumns: "列标签（每行一个）",
      matrixColumnsHelp: "输入量表选项，每行一个（例如：非常不同意、不同意、中立、同意、非常同意）",
      interviewConfig: {
        title: "AI 访谈设置",
        interactionMode: "交互模式",
        voiceToVoice: "语音对语音",
        voiceToText: "语音对文字",
        textToText: "文字对文字",
        textToVoice: "文字对语音",
        language: "访谈语言",
        maxDuration: "最长时间（分钟）",
        outline: "访谈大纲/提示",
        outlinePlaceholder: "描述AI应该询问的内容...",
        outlineHelp: "AI访谈员将按照此大纲引导对话。",
      },
      defaultPrompt: "新问题",
      preview: "预览",
      previewMode: "预览模式",
      conditions: "条件",
      addCondition: "添加条件",
      removeCondition: "删除",
      conditionLogicAnd: "且（全部匹配）",
      conditionLogicOr: "或（任一匹配）",
      conditionQuestion: "如果问题",
      conditionOperator: "运算符",
      conditionValue: "值",
      operatorEquals: "等于",
      operatorNotEquals: "不等于",
      operatorContains: "包含",
      operatorGreaterThan: "大于",
      operatorLessThan: "小于",
      operatorIsEmpty: "为空",
      operatorIsNotEmpty: "不为空",
      pipingHelp: "提示：使用 ${variable_name} 插入前一个回答",
      availableVariables: "可用变量：{{variables}}",
      // Share & Distribution (Phase 6)
      share: "分享",
      shareTitle: "分享您的问卷",
      tabLink: "链接 / 二维码",
      tabEmbed: "嵌入代码",
      tabSocial: "社交媒体",
      tabProtect: "保护",
      copy: "复制",
      copied: "已复制",
      linkCopied: "链接已复制",
      qrHint: "用手机摄像头扫描打开问卷",
      downloadQR: "下载二维码 (PNG)",
      embedHelp: "将此HTML代码粘贴到您的网站中以嵌入问卷。",
      copyEmbed: "复制嵌入代码",
      embedCopied: "嵌入代码已复制",
      preview: "预览",
      socialHelp: "在社交平台上分享您的问卷：",
      shareOn: "分享到",
      passwordProtection: "密码保护",
      passwordHint: "要求受访者在开始前输入密码。",
      passwordActive: "已设置密码。下方输入新密码可修改，或留空删除密码。",
      passwordPlaceholder: "留空表示无密码",
      passwordSaved: "密码已更新",
      passwordRemoved: "密码已删除",
      removePassword: "删除密码",
      apply: "应用",
      schedule: "日程安排",
      scheduleHelp: "在指定时间自动打开和关闭您的问卷。留空表示始终开放。",
      openAt: "打开时间",
      closeAt: "关闭时间",
      saveSchedule: "保存日程",
      scheduleSaved: "日程已保存",
      saveError: "保存失败",
      // Theming (Phase 5)
      themeBranding: "主题与品牌",
      themeBrandingHelp: "自定义受访者看到的问卷页面的外观。",
      themePresets: "快速预设",
      primaryColor: "主色",
      backgroundColor: "背景颜色",
      textColor: "文字颜色",
      fontFamily: "字体样式",
      fontSystem: "系统默认（现代）",
      fontSerif: "衬线体（经典）",
      fontRounded: "圆角体（友好）",
      fontMono: "等宽体（技术）",
      logoUrl: "徽标图片网址",
      logoUrlHelp: "显示在问卷标题上方。可选。",
      headerImageUrl: "页头横幅网址",
      headerImageUrlHelp: "页面顶部的宽横幅图片。可选。",
      themePreview: "预览",
      sampleSurveyTitle: "您的问卷标题",
      sampleQuestionText: "这是问题的外观。",
      sampleSubmit: "提交",
      resetTheme: "重置为默认",
      randomizeQuestions: "随机排列问题顺序",
      randomizeOptions: "随机排列选项顺序",
      thankYouSettings: "感谢页面",
      thankYouTitle: "标题",
      thankYouMessage: "消息",
      thankYouRedirect: "重定向URL（可选）",
      maxResponses: "最大回复数（可选）",
      maxResponsesHelp: "留空表示无限制",
      deliveryMode: "分发模式",
      deliveryConversational: "对话式（AI访谈）",
      deliveryForm: "标准表单",
      deliveryModeHelp: "对话模式使用AI进行访谈。标准表单模式一次显示所有问题。",
      testWithSynthetic: "使用模拟人测试",
      fromLibrary: "从库中选择",
      fromLibraryHint: "从预制模板和题组中复制",
      templateLibrary: "模板库",
      templates: "模板",
      bundles: "包",
      allTemplates: "所有模板",
      myTemplates: "我的模板",
      allCategories: "所有类别",
      selectTemplate: "选择模板",
      templateName: "模板名称",
      category: "类别",
      search: "搜索",
      preview: "预览",
      inserting: "插入中...",
      insertTemplate: "插入模板",
      insertBundle: "插入包",
      noTemplates: "未找到模板",
      noBundles: "未找到包",
      templateInsertedSuccess: "模板插入成功",
      bundleInsertedSuccess: "包插入成功",
      templateInsertError: "插入模板失败",
      cancel: "取消",
      skipLogic: "跳过逻辑",
      skipLogicHelp: "仅在满足条件时显示此问题",
      displayLogic: "显示逻辑",
      displayLogicHelp: "仅在前面的回答与这些条件匹配时显示此问题",
      conditionIf: "如果",
      logicTab: "逻辑",
      noLogicYet: "尚未配置跳过逻辑",
      noLogicHint: "点击问题并滚动到跳过逻辑部分以添加条件。",
      logicSummaryPrefix: "在以下条件下显示此问题",
      settings: "问卷设置"
    },
    export: {
      loadError: "加载问卷失败",
      description: "下载已提交的回答（CSV）。",
      button: "下载 CSV",
      success: "导出完成",
      error: "导出失败",
      xlsx: "下载 XLSX"
    },
    analytics: {
      filterScopeNote: "此日期筛选仅缩小回复列表和交叉分析的范围。概览卡片、图表和 AI 摘要始终反映全部回复。",
      clearFilter: '清除筛选',
      columnQuestion: '列问题',
      crossTab: '交叉分析',
      crossTabHelp: '比较一个问题的答案如何按另一个问题分布。',
      crossTabNeedTwo: '请选择两个问题进行比较。',
      crossTabPickTwo: '选择行问题和列问题',
      crossTabTitle: '交叉分析表',
      duration: '时长',
      filterBy: '筛选条件',
      from: '从',
      individualResponses: '个人回答',
      language: '语言',
      next: '下一个',
      noAnswer: '无回答',
      noResponsesYet: '暂无回答',
      deleteResponse: '删除回答',
      confirmDeleteResponse: '永久删除此回答？此操作无法撤销。',
      responseDeleted: '回答已删除。',
      deleteFailed: '删除回答失败。',
      download: '下载',
      downloadCsv: '下载 CSV',
      downloadXlsx: '下载 Excel (XLSX)',
      exportFailed: '导出失败。',
      prev: '上一个',
      respondent: '受访者',
      rowQuestion: '行问题',
      sampleResponses: '回答样例',
      submittedAt: '提交时间',
      to: '至',
      viewResponses: '查看回答',
      wordCloud: '词云',
      title: "问卷分析",
      totalResponses: "总回答数",
      completionRate: "完成率",
      avgDuration: "平均时长",
      minutes: "分钟",
      responsesOverTime: "回答趋势",
      questionBreakdown: "问题分析",
      responses: "条回答",
      noData: "暂无回答",
      refresh: "刷新",
      detractors: "贬损者 (0-6)",
      passives: "中立者 (7-8)",
      promoters: "推荐者 (9-10)"
    },
    start: {
      untitledSurvey: "未命名问卷",
      timeEstimate: "约 {{minutes}} {{unit}}",
      questionCount: "{{count}} {{unit}}",
      minute: "分钟",
      minutes: "分钟",
      question: "题",
      questions: "题",
      privacyNote: "您的回答将保密",
      startButton: "开始问卷",
      selectLanguageFirst: "请先选择一种语言以继续。",
      title: "开始问卷",
      subtitle: "进入对话式问卷。",
      button: "开始",
      loading: "正在启动问卷...",
      error: "无法创建会话",
      selectLanguage: "选择您的语言",
      selectLanguageSubtitle: "为您的问卷会话选择语言",
      surveyFull: "此问卷已达到最大回复数"
    },
    session: {
      dwellWait: "{{n}} 秒后可继续",
      redirectingIn: "{seconds} 秒后跳转…",
      continueNow: "立即继续",
      cancelRedirect: "取消",
      redirectCancelled: "已取消自动跳转。",
      continueToSite: "前往该网站",
      loadError: "加载会话失败",
      connected: "已连接",
      wsError: "聊天连接失败",
      answersSaved: "答案已保存",
      saveError: "保存失败",
      submitted: "已提交",
      submitError: "提交失败",
      progress: "进度：{{answered}} / {{total}} 必填",
      placeholder: "请输入回答",
      send: "发送",
      verification: "核对答案",
      required: "必填",
      saveEdits: "保存修改",
      submit: "确认提交",
      loading: "加载中...",
      processing: "正在处理你的回答...",
      stopRecording: "停止录音",
      pressToSpeak: "按住说话",
      reviewSubmit: "查看并提交",
      formMode: "标准表单",
      conversationalMode: "AI访谈",
      voicePlayback: "语音",
      switchToForm: "切换到表单",
      switchToConversational: "切换到AI访谈",
      answerMore: "还需回答 {{count}} 个必填问题",
      transcribeFailed: "音频转写失败，请重试。",
      audioProcessError: "音频处理失败：{{error}}",
      micError: "无法访问麦克风：{{error}}",
      rankingPlaceholder: "请输入排序（例如：A > B > C）",
      thankYouTitle: "谢谢您！",
      thankYouMessage: "您的回答已成功提交。我们非常感谢您的时间和反馈。",
      returnHome: "返回主页",
      status_in_progress: "进行中",
      pageOf: "第 {current} 页，共 {total} 页",
      nextPage: "下一页",
      previousPage: "上一页",
      status_submitted: "已提交",
      status_awaiting_verification: "审核中",
      previewBanner: "[预览模式] 这是预览。回答将不会保存到分析数据中。",
      redirecting: "正在跳转...",
      progressLabel: "进度",
      autoSaving: "保存中…",
      autoSaved: "已保存",
      saveAndContinue: "保存并稍后继续",
      saveAndContinueTooltip: "保存您的进度并获取链接以便稍后继续",
      saveAndContinueTitle: "保存您的进度",
      saveAndContinueBody: "您的回答已保存。请将此链接加入书签或发送到您的邮箱，以便在任何设备上从中断处继续。",
      copyLink: "复制链接",
      linkCopied: "链接已复制到剪贴板",
      close: "关闭",
      alreadySubmittedWarning: "我们的记录显示您可能已经提交过此问卷。您仍可以继续，但重复的回答可能会被排除。",
      connectionError: "连接错误",
      reconnecting: "正在重新连接助手...",
      connectionLost: "连接已断开，请刷新页面。",
      typeHere: "在此输入您的回答...",
      send: "发送",
      requiredField: "必填",
      fieldRequired: "{label} 为必填项",
      invalidEmail: "请输入有效的电子邮箱地址",
      invalidPhone: "请输入有效的电话号码",
      invalidNumber: "请输入有效的数字",
      sumMustEqual: "各项之和必须等于 {total}（当前：{sum}）",
      npsNotLikely: "完全不可能",
      npsExtremely: "非常可能",
      chooseFile: "选择文件",
      chooseFileDrag: "选择文件或拖放至此",
      fileSelected: "已选择文件",
      selectedPrefix: "已选择：",
      acceptedPrefix: "接受格式：",
      fileTooLarge: "文件大小必须小于 {size}MB",
      constantSumInstruction: "请在以下各项之间分配共 {total} 分。",
      remaining: "剩余：",
      pageBreakLabel: "分页符",
      noFormConfigured: "未配置表单",
      noFormConfiguredBody: "项目所有者尚未设置自定义表单。",
      unsupportedField: "不支持的字段类型：{type}",
      imageNotConfigured: "未配置图片地址",
      videoNotConfigured: "未配置视频地址",
      dragToReorder: "拖动以重新排序",
      moveUp: "上移",
      moveDown: "下移",
      requiredSummary: "请先回答高亮显示的问题再继续。",
      yes: "是",
      no: "否",
      optionN: "选项 {n}",
      surveyImageAlt: "问卷信息",
      interview: {
        title: "AI 访谈",
        description: "此问题包含一段简短的AI访谈。",
        start: "开始访谈",
        starting: "正在启动访谈...",
        ending: "正在保存访谈...",
        completed: "访谈已完成",
        end: "结束",
        stopRecording: "停止录音",
        tapToSpeak: "点击说话",
        typePlaceholder: "输入您的回答...",
      },
    },
    languages: {
      en: "英文",
      zh: "中文",
      fr: "法文",
      de: "德文",
      es: "西班牙文",
      nb: "挪威文",
      nl: "荷兰文",
      pt: "葡萄牙文",
      ja: "日文",
      ar: "阿拉伯文",
      flexible: "灵活（参与者选择）"
    },
    synthetic: {
      dialogTitle: "使用模拟参与者测试",
      dialogSubtitle: "配置模拟参与者的人口统计信息",
      age: "年龄", gender: "性别", male: "男", female: "女", nonBinary: "非二元", preferNotToSay: "不愿透露",
      nationality: "国籍", ethnicity: "民族", education: "教育程度",
      highSchool: "高中", bachelors: "本科", masters: "硕士", phd: "博士", vocational: "职业技术", other: "其他",
      occupation: "职业", personality: "性格特征", additionalContext: "附加背景（可选）",
      runTest: "开始测试", cancel: "取消",
      simulating: "正在模拟参与者回答...", simulatingHint: "可能需要15-30秒",
      resultsTitle: "模拟测试结果", syntheticPersona: "模拟参与者档案", synthetic: "模拟",
      question: "问题", type: "类型", answer: "回答", close: "关闭",
    },
    status: {
      draft: "草稿",
      published: "已发布",
      archived: "已归档"
    }
  },
  fr: {
    tutorial: {
      title: "Premiers pas",
      step1Title: "Bienvenue dans le générateur d’enquêtes",
      step1Body: "Créez des enquêtes conversationnelles où une IA pose des questions de relance pertinentes et s’adapte à chaque répondant — bien plus riche qu’un formulaire statique.",
      step2Title: "Ajoutez vos questions",
      step2Body: "Ajoutez des questions de tout type — choix multiple, échelles, texte libre — et faites-les glisser pour les réorganiser. Utilisez les sauts de page et les sections pour structurer le déroulé.",
      step3Title: "Configurez l’IA",
      step3Body: "Définissez la langue, le ton et la profondeur des relances de l’IA sur les réponses ouvertes. Vous pouvez aussi traduire automatiquement toute l’enquête.",
      step4Title: "Partagez & analysez",
      step4Body: "Publiez et partagez votre lien, puis suivez les réponses et les analyses qui arrivent au fur et à mesure que les participants complètent l’enquête.",
      back: "Retour",
      next: "Suivant",
      done: "Commencer",
      skip: "Ignorer",
      help: "Comment ça marche",
    },
    import: {
      menuLabel: "Importer un fichier", reviewTitle: "Vérifier le questionnaire importé",
      reviewIntro: "Nous avons rédigé {{n}} questions à partir de votre fichier. Vérifiez ci-dessous, puis créez le questionnaire — vous pourrez le modifier entièrement dans l'éditeur.",
      cancel: "Annuler", titleField: "Titre du questionnaire", descriptionField: "Description",
      reportTitle: "Notes d'importation", preview: "Questions", pageBreak: "Saut de page",
      sectionBreak: "Section", required: "obligatoire", create: "Créer le questionnaire",
      parseFailed: "Impossible d'importer ce fichier.", createFailed: "Impossible de créer le questionnaire.",
      uploadTitle: "Importer un questionnaire", parsing: "Lecture de votre fichier…",
      dropHere: "Glissez un fichier ici, ou cliquez pour parcourir",
      dropHint: "Jusqu'à 25 Mo. Nous rédigeons un questionnaire que vous pourrez vérifier et modifier.",
      supported: "Formats pris en charge",
      formatNote: "L'IA transforme Word et PDF en questionnaire — elle retranscrit un questionnaire existant ou en rédige un à partir de vos notes. Qualtrics .qsf et QualiTaTi .qlt sont analysés exactement.",
      exportDefinition: "Télécharger le questionnaire", exportWord: "Télécharger en Word (.docx)", exportQlt: "Télécharger en QualiTaTi (.qlt)", exportFailed: "Échec de l'exportation.",
    },
    translations: {
      menuLabel: "Traductions", title: "Traductions du questionnaire",
      intro: "Ajoutez une langue pour traduire automatiquement tout le questionnaire avec l'IA, puis relisez et modifiez chaque texte. Les répondants choisissent leur langue au début ; tout champ vide revient à la langue par défaut ({{lang}}).",
      source: "source", addLanguage: "Ajouter une langue",
      empty: "Aucune langue pour l'instant. Cliquez sur « Ajouter une langue » pour créer une traduction IA.",
      surveyInfo: "Informations du questionnaire", fieldTitle: "Titre", fieldDescription: "Description",
      fieldThankYouTitle: "Titre de remerciement", fieldThankYouMessage: "Message de remerciement",
      fieldPrompt: "Question", options: "Options", columns: "Colonnes de l'échelle",
      retranslate: "Retraduire", close: "Fermer", save: "Enregistrer",
      translated: "Traduction automatique terminée. Relisez et modifiez ci-dessous.", translateFailed: "Échec de la traduction.",
      saved: "Traduction enregistrée.", saveFailed: "Échec de l'enregistrement.",
      confirmDelete: "Supprimer cette langue et toutes ses traductions ?", deleteFailed: "Échec de la suppression.",
      confirmRetranslate: "Retraduire entièrement ? Cela écrasera vos modifications pour cette langue.",
      loadFailed: "Échec du chargement des traductions.",
    },
    hub: {
      emptyTitle: "Créez votre première enquête",
      emptyBody: "Partez de zéro, générez-en une avec l'IA, choisissez un modèle ou importez un fichier Word, PDF ou Qualtrics.",
      emptyNoResults: "Aucune enquête ne correspond à vos filtres.",
      clearFilters: "Effacer les filtres",
      title: "Enquêtes",
      createProject: "Créer un projet d’enquête",
      manageSurveys: "Gérer les enquêtes",
      createDialogTitle: "Nouveau projet",
      projectName: "Nom du projet",
      projectOutline: "Plan du projet",
      interviewType: "Type d’entretien",
      interviewLanguage: "Langue d’entretien",
      cancel: "Annuler",
      save: "Enregistrer",
      createSuccess: "Projet créé",
      createError: "Échec de création",
      fillRequired: "Veuillez remplir les champs obligatoires",
      loadError: "Impossible de charger les projets",
      experimentalTitle: "Fonctionnalité expérimentale",
      experimentalMessage: "L'Enquête Conversationnelle est un outil expérimental en cours de développement actif. Certaines fonctionnalités peuvent changer ou se comporter de manière inattendue. Vos retours nous aident à nous améliorer !",
      search: "Rechercher des enquêtes",
      filterAll: "Toutes",
      filterDraft: "Brouillon",
      filterPublished: "Publiées",
      sortNewest: "Plus récentes",
      sortAlpha: "Alphabétique",
      noResults: "Aucune enquête ne correspond à votre recherche",
      analytics: "Analytique",
      duplicate: "Dupliquer",
      duplicateSuccess: "Enquête dupliquée",
      confirmDeleteProject: "Supprimer ce projet et toutes ses enquêtes ? Cette action est irréversible.",
      projectDeleted: "Projet supprimé avec succès",
      surveysInProject: "Enquêtes dans ce projet",
      noSurveysYet: "Aucune enquête pour l'instant",
      manageProjectSurveys: "Gérer les enquêtes",
      noProjectsYet: "Aucun projet d'enquête créé pour l'instant",
    },
    list: {
      emptyTitle: "Aucune enquête dans ce projet pour l'instant",
      emptyBody: "Créez votre première enquête pour commencer à recueillir des réponses.",
      fromTemplate: "Partir d'un modèle",
      fromWizard: "Générer avec l'IA",
      fromTemplateTitle: "Partir d'un modèle",
      fromTemplateSubtitle: "Choisissez un modèle pour obtenir un brouillon d'enquête modifiable. Nous pré-remplissons les questions, les relances IA et un mode par défaut adapté.",
      templatesEmpty: "Aucun modèle disponible pour le moment.",
      creatingFromTemplate: "Création…",
      wizard: {
        title: "Générer une enquête avec l'IA",
        subtitle: "Dites à Mimi ce que vous voulez apprendre et elle rédigera une enquête modifiable.",
        generating: "Mimi rédige votre enquête…",
        generatingHint: "Cela prend généralement 20 à 40 secondes. Nous générons un jeu de questions sur mesure avec des paramètres de relance IA appropriés.",
        goalLabel: "Que voulez-vous apprendre ?",
        goalPlaceholder: "ex. Pourquoi les clients résilient après le premier mois — ce qui motive la décision et ce qui aurait pu la changer.",
        goalHelp: "Des objectifs précis produisent de meilleures enquêtes. Visez 1 à 3 phrases.",
        audienceLabel: "Qui répondra à cette enquête ?",
        audiencePlaceholder: "ex. Clients ayant résilié au cours des 30 derniers jours",
        languageLabel: "Langue",
        depthLabel: "Profondeur",
        nQuestionsLabel: "Nb. de questions visé",
        generate: "Générer",
        errorGoalTooShort: "L'objectif doit faire au moins 10 caractères pour que l'IA ait de quoi travailler.",
        errorAudienceTooShort: "Indiquez l'audience pour que l'IA puisse adapter les questions.",
      },
      title: "Enquête",
      create: "Créer une enquête",
      createDialogTitle: "Nouvelle enquête",
      titleField: "Titre",
      descriptionField: "Description",
      cancel: "Annuler",
      save: "Enregistrer",
      builder: "Éditeur",
      export: "Exporter",
      publish: "Publier",
      status: "Statut",
      linkCopied: "Lien copié",
      publishFirst: "Veuillez d'abord publier l'enquête",
      publishReady: "Enquête publiée",
      publishError: "Échec de publication",
      createSuccess: "Enquête créée",
      createError: "Échec de création",
      exportReady: "CSV prêt",
      exportError: "Échec d'export",
      error: "Impossible de charger les enquêtes",
      copyLink: "Copier le lien",
      downloadCsv: "Télécharger CSV",
      deleteSurvey: "Supprimer l'enquête",
      publishGetLink: "Publier et obtenir le lien",
      published: "Enquête publiée — votre lien de partage est prêt.",
      questionsLabel: "questions",
      responsesLabel: "réponses"
    },
    builder: {
      minDwellLabel: "Temps d'affichage minimum (secondes)",
      minDwellHelp: "Les répondants doivent rester sur cette page pendant ce temps avant que Suivant/Envoyer ne se débloque. 0 = désactivé.",
      saving: "Enregistrement…",
      saved: "Enregistré",
      saveFailed: "Impossible d'enregistrer les modifications",
      aiFollowUp: {
        label: "Relance IA",
        helperNone: "Pas de relance — les répondants répondent et passent à la suite",
        helperLight: "Light — une relance conversationnelle pour enrichir la réponse",
        helperDeep: "Deep — jusqu'à trois relances pour une analyse qualitative approfondie",
        optionNone: "Aucune — pas de relance",
        optionLight: "Light — 1 relance",
        optionDeep: "Deep — jusqu'à 3 relances",
      },
      cancel: "Annuler",
      surveyMode: {
        label: "Mode de l'enquête",
        form: {
          title: "Sondage classique",
          subtitle: "Sondage standard sous forme de formulaire, sans IA",
        },
        aiFollowUp: {
          title: "Enquête avec relances IA",
          subtitle: "Formulaire + relances IA sur les questions ouvertes",
          hint: "Astuce : réglez l'intensité des « relances IA » sur chaque question ouverte ci-dessous. Light = 1 relance ; Deep = jusqu'à 3.",
        },
        aiInterview: {
          title: "Sondage conversationnel",
          subtitle: "Entretien conversationnel dirigé par l'IA, dialogue libre",
        },
      },
      preflight: {
        title: "Vérification avant publication",
        loading: "Vérification de votre enquête…",
        ready: "Prêt à publier",
        readyHint: "Votre enquête a réussi toutes les vérifications. Les répondants verront une expérience soignée.",
        hasErrors: "Corrigez les points ci-dessous avant de publier :",
        warningsOnly: "Vous pouvez publier, mais voici quelques points à vérifier :",
        fallback: "Impossible d'exécuter la vérification — la publication reste autorisée.",
        rerun: "Revérifier",
      },
      apply: 'Appliquer',
      backgroundColor: 'Arrière-plan',
      closeAt: 'Se ferme à',
      copied: 'Copié',
      copy: 'Copier',
      copyEmbed: 'Copier le code d\'intégration',
      downloadQR: 'Télécharger le QR code (PNG)',
      embedCopied: 'Code d\'intégration copié',
      embedHelp: 'Collez ce HTML sur votre site pour intégrer le sondage.',
      fontFamily: 'Style de police',
      fontMono: 'Monospace (technique)',
      fontRounded: 'Arrondie (conviviale)',
      fontSerif: 'Serif (classique)',
      fontSystem: 'Par défaut du système (moderne)',
      headerImageUrl: 'URL de la bannière d\'en-tête',
      headerImageUrlHelp: 'Grande image de bannière en haut. Facultatif.',
      logoUrl: 'URL de l\'image du logo',
      logoUrlHelp: 'Affiché au-dessus du titre du sondage. Facultatif.',
      openAt: 'Ouvre à',
      passwordActive: 'Un mot de passe est défini. Saisissez-en un nouveau ci-dessous pour le changer, ou laissez vide pour le supprimer.',
      passwordHint: 'Demander aux répondants de saisir un mot de passe avant de commencer.',
      passwordPlaceholder: 'Laisser vide pour aucun mot de passe',
      passwordProtection: 'Protection par mot de passe',
      passwordRemoved: 'Mot de passe supprimé',
      passwordSaved: 'Mot de passe mis à jour',
      pipingChip: 'Insérer une réponse précédente',
      pipingTooltipBody: 'Cliquez sur une étiquette pour la copier, puis collez-la dans votre texte de question. Quand le répondant la voit, elle devient sa réponse précédente.',
      pipingTooltipTitle: 'Afficher une réponse précédente dans cette question',
      primaryColor: 'Couleur principale',
      qrHint: 'Scannez avec l\'appareil photo du téléphone pour ouvrir le sondage',
      removePassword: 'Supprimer le mot de passe',
      resetTheme: 'Réinitialiser',
      sampleQuestionText: 'Voici à quoi ressemble une question.',
      sampleSubmit: 'Envoyer',
      sampleSurveyTitle: 'Titre de votre sondage',
      saveSchedule: 'Enregistrer la planification',
      schedule: 'Planification',
      scheduleHelp: 'Ouvrez et fermez automatiquement votre sondage à des heures précises. Laissez vide pour toujours ouvert.',
      scheduleSaved: 'Planification enregistrée',
      share: 'Partager',
      shareOn: 'Partager sur',
      shareTitle: 'Partagez votre sondage',
      socialHelp: 'Partagez votre sondage sur les réseaux sociaux :',
      tabEmbed: 'Intégration',
      tabLink: 'Lien / QR',
      tabProtect: 'Protection',
      tabSocial: 'Réseaux sociaux',
      textColor: 'Couleur du texte',
      themeBranding: 'Thème et identité visuelle',
      themeBrandingHelp: 'Personnalisez l\'apparence de la page du sondage vue par les répondants.',
      themePresets: 'Préréglages rapides',
      themePreview: 'Aperçu',
      constantSumItems: 'Éléments',
      constantSumItemsHelp: 'Un élément par ligne',
      constantSumTotal: 'Total',
      constantSumTotalHelp: 'Somme à répartir par les répondants (ex. 100)',
      emailAutoConfig: 'Validé comme adresse e-mail',
      fileAcceptHelp: 'Types de fichiers autorisés (séparés par des virgules, ex. .pdf,.docx)',
      fileAcceptTypes: 'Types acceptés',
      fileMaxSize: 'Taille max du fichier (Mo)',
      imageChoiceHelp: 'Une URL d\'image par ligne. Les libellés facultatifs correspondent dans l\'ordre.',
      imageChoiceLabels: 'Libellés',
      imageChoiceMultiple: 'Autoriser la sélection multiple',
      imageChoiceUrls: 'URL des images',
      phoneAutoConfig: 'Validé comme numéro de téléphone',
      ratingMax: 'Note maximale',
      sectionBreakInfo: 'Séparateur visuel uniquement — aucune réponse collectée.',
      thankYouPage: 'Page de remerciement',
      thankYouRedirectHelp: 'URL facultative vers laquelle rediriger après envoi',
      yesNoAutoConfig: 'Choix Oui / Non',
      loadError: "Impossible de charger l’enquête",
      saveMeta: "Enquête mise à jour",
      saveError: "Échec d'enregistrement",
      addError: "Échec d’ajout",
      questionAdded: "Question ajoutée",
      saveQuestion: "Question enregistrée",
      questionDeleted: "Question supprimée",
      reorderError: "Échec de réorganisation",
      deleteError: "Échec de suppression",
      confirmDelete: "Supprimer cette question ?",
      deleteSurvey: "Supprimer l'enquête",
      confirmDeleteSurvey: "Êtes-vous sûr de vouloir supprimer cette enquête ? Cette action est irréversible.",
      surveyDeleted: "Enquête supprimée avec succès",
      surveyFlow: "Flux de l'enquête",
      addBlock: "Ajouter un bloc",
      addRandomizer: "Ajouter un randomiseur",
      blockPlaceholder: "Bloc sans titre",
      deleteBlock: "Supprimer le bloc",
      confirmDeleteBlock: "Supprimer ce bloc ? Ses questions seront désaffectées.",
      randomizer: "Randomiseur",
      randomizerShow: "Afficher",
      randomizerOf: "sur",
      addVariant: "Ajouter une variante",
      questionsCount: "{n} question(s)",
      unassigned: "Questions non affectées",
      noBlocks: "Aucun bloc",
      renameBlock: "Renommer",
      addQuestion: "Ajouter",
      publishCopied: "Publié et lien copié",
      publishError: "Échec de publication",
      linkCopied: "Lien copié",
      copyLink: "Copier le lien de partage",
      isConversational: "Poser de façon conversationnelle",
      askForm: "Poser via un formulaire",
      questions: "Questions",
      meta: "Informations",
      titleField: "Titre",
      descriptionField: "Description",
      descriptionPlaceholder: "Décrivez l'objectif de cette enquête...",
      language: "Langue",
      save: "Enregistrer",
      imageUrl: "URL de l'image",
      imageUrlHelp: "Optionnel: Entrez une URL pour afficher une image",
      imagePreview: "Aperçu",
      imageLoadError: "Échec du chargement de l'image. Veuillez vérifier l'URL.",
      addImage: "Ajouter une image",
      questionImage: "Image de la question",
      removeImage: "Supprimer l'image",
      uploadImage: "Téléverser une image",
      uploading: "Téléversement…",
      imageUrlPlaceholder: "Ou collez l'URL de l'image",
      imageHelp: "PNG, JPEG, GIF ou WebP. Max 2 Mo.",
      imageUploadFailed: "Échec du téléversement.",
      uploadImages: "Téléverser des images",
      addMoreImages: "Ajouter d'autres",
      urlPasteHint: "Appuyez sur Entrée pour ajouter",
      removeAllImages: "Tout supprimer",
      itemsToRank: "Éléments à classer",
      itemsToRankHelp: "Entrez les éléments à classer, un par ligne",
      bipolarLabels: "Étiquettes d'échelle",
      leftLabel: "Étiquette gauche",
      rightLabel: "Étiquette droite",
      surveyLanguage: "Langue de l'enquête",
      surveyLanguageHelp: "Sélectionnez 'Flexible' pour permettre aux participants de choisir leur langue",
      flexible: "Flexible (Le participant choisit)",
      titleField: "Titre",
      descriptionField: "Description",
      save: "Enregistrer",
      publish: "Publier",
      editor: "Éditeur de question",
      selectPrompt: "Sélectionnez une question",
      prompt: "Texte de la question",
      variable: "Nom de variable",
      variableHelp: "snake_case, unique dans l’enquête",
      required: "Obligatoire",
      options: "Options (une par ligne)",
      optionsHelp: "Pour choix unique/multiple",
      min: "Min",
      max: "Max",
      saveQuestionBtn: "Enregistrer la question",
      types: {
        image: "Image",
        short_text: "Réponse courte",
        long_text: "Paragraphe",
        single_choice: "Choix multiple",
        multi_choice: "Cases à cocher",
        number: "Nombre",
        date: "Date",
        ranking: "Classement",
        bipolar_scale: "Échelle bipolaire",
        info_text: "Texte d'information",
        slider: "Curseur",
        dropdown: "Menu déroulant",
        nps: "Net Promoter Score",
        matrix: "Matrice",
        ai_interview: "Entretien IA",
        rating_scale: "Échelle de notation",
        yes_no: "Oui / Non",
        email: "E-mail",
        phone: "Numéro de téléphone",
        image_choice: "Choix d'image",
        file_upload: "Téléchargement de fichier",
        constant_sum: "Somme constante",
        section_break: "Séparation de section",
        page_break: "Saut de page"
      },
      typeDescriptions: {
        short_text: "Réponse textuelle brève",
        long_text: "Réponse textuelle longue",
        single_choice: "Choisir une option",
        multi_choice: "Sélectionner plusieurs options",
        number: "Valeur numérique",
        date: "Sélecteur de date",
        ranking: "Classer les éléments par ordre",
        bipolar_scale: "Échelle entre deux opposés",
        info_text: "Afficher du texte ou des images (aucune réponse requise)",
        slider: "Glisser pour sélectionner une valeur",
        dropdown: "Sélectionner une option dans une liste déroulante",
        nps: "Noter de 0 à 10 (Net Promoter Score)",
        matrix: "Évaluer plusieurs éléments sur la même échelle",
        ai_interview: "Intégrer une conversation d'entretien pilotée par l'IA",
        rating_scale: "Notation par étoiles (ex. 1-5 étoiles)",
        yes_no: "Question simple oui ou non",
        email: "Adresse e-mail avec validation",
        phone: "Numéro de téléphone avec validation",
        image_choice: "Sélectionner parmi des images",
        file_upload: "Télécharger un fichier",
        constant_sum: "Répartir des points entre les éléments",
        section_break: "Séparateur visuel entre les sections",
        page_break: "Commence une nouvelle page pour les répondants"
      },
      stepValue: "Pas",
      npsAutoConfig: "Le NPS est automatiquement configuré comme une échelle de 0 à 10. Aucune configuration supplémentaire requise.",
      matrixRows: "Étiquettes des lignes (une par ligne)",
      matrixRowsHelp: "Saisissez les éléments à évaluer, un par ligne",
      matrixColumns: "Étiquettes des colonnes (une par ligne)",
      matrixColumnsHelp: "Saisissez les options de l'échelle, une par ligne",
      interviewConfig: {
        title: "Paramètres de l'entretien IA",
        interactionMode: "Mode d'interaction",
        voiceToVoice: "Voix à Voix",
        voiceToText: "Voix à Texte",
        textToText: "Texte à Texte",
        textToVoice: "Texte à Voix",
        language: "Langue de l'entretien",
        maxDuration: "Durée maximale (minutes)",
        outline: "Plan de l'entretien / Consignes",
        outlinePlaceholder: "Décrivez ce que l'IA devrait demander...",
        outlineHelp: "L'intervieweur IA suivra ce plan pour guider la conversation.",
      },
      defaultPrompt: "Nouvelle question",
      metaSaved: "Détails sauvegardés",
      questionSaved: "Question sauvegardée",
      confirmDelete: "Supprimer cette question ?",
      publishSuccess: "Enquête publiée",
      noQuestions: "Aucune question",
      clickToAdd: "Cliquez pour ajouter votre première question",
      addFirstQuestion: "Ajouter une question",
      untitledQuestion: "(Question sans titre)",
      promptPlaceholder: "Entrez votre question...",
      surveyTitle: "Titre de l'enquête",
      surveyDescription: "Description",
      noDescription: "Aucune description",
      questionType: "Type de question",
      variableName: "Nom de variable",
      requiredQuestion: "Question obligatoire",
      optionsPlaceholder: "Option 1\nOption 2\nOption 3",
      minValue: "Valeur minimale",
      maxValue: "Valeur maximale",
      saveChanges: "Enregistrer",
      moreOptions: "options de plus",
      moveUp: "Monter",
      moveDown: "Descendre",
      moveToBlock: "Déplacer vers le bloc",
      expand: "Développer",
      collapse: "Réduire",
      delete: "Supprimer",
      duplicate: "Dupliquer",
      duplicateSuccess: "Question dupliquée",
      republish: "Republier",
      unpublish: "Dépublier",
      unpublishing: "Dépublication…",
      unpublishSuccess: "Enquête dépubliée. Elle est maintenant en brouillon.",
      unpublishError: "Échec de la dépublication",
      unpublishTooltip: "Bloquer les nouveaux participants. Les réponses existantes sont conservées ; le lien de partage est préservé pour la republication.",
      confirmUnpublish: "Dépublier cette enquête ? Les nouveaux participants seront bloqués. Les réponses existantes sont conservées. Vous pourrez republier plus tard avec le même lien.",
      preview: "Aperçu",
      previewMode: "Mode aperçu",
      conditions: "Conditions",
      addCondition: "Ajouter une condition",
      removeCondition: "Supprimer",
      conditionLogicAnd: "ET (toutes doivent correspondre)",
      conditionLogicOr: "OU (au moins une doit correspondre)",
      conditionQuestion: "Si la question",
      conditionOperator: "Opérateur",
      conditionValue: "Valeur",
      operatorEquals: "est égal à",
      operatorNotEquals: "n’est pas égal à",
      operatorContains: "contient",
      operatorGreaterThan: "est supérieur à",
      operatorLessThan: "est inférieur à",
      operatorIsEmpty: "est vide",
      operatorIsNotEmpty: "n’est pas vide",
      pipingHelp: "Astuce : Utilisez ${variable_name} pour insérer une réponse précédente",
      availableVariables: "Disponibles : {{variables}}",
      randomizeQuestions: "Ordre aléatoire des questions",
      randomizeOptions: "Ordre aléatoire des options",
      thankYouSettings: "Page de remerciement",
      thankYouTitle: "Titre",
      thankYouMessage: "Message",
      thankYouRedirect: "URL de redirection (optionnel)",
      maxResponses: "Réponses max (optionnel)",
      maxResponsesHelp: "Laisser vide pour illimité",
      deliveryMode: "Mode de diffusion",
      deliveryConversational: "Conversationnel (Interview IA)",
      deliveryForm: "Formulaire standard",
      deliveryModeHelp: "Le mode conversationnel utilise l'IA pour mener des entretiens. Le mode formulaire standard affiche toutes les questions en une fois.",
      testWithSynthetic: "Tester avec un humain synthétique",
      fromLibrary: "De la bibliothèque",
      fromLibraryHint: "Copier depuis les modèles et bundles prédéfinis",
      templateLibrary: "Bibliothèque de modèles",
      templates: "Modèles",
      bundles: "Lots",
      allTemplates: "Tous les modèles",
      myTemplates: "Mes modèles",
      allCategories: "Toutes les catégories",
      selectTemplate: "Sélectionner un modèle",
      templateName: "Nom du modèle",
      category: "Catégorie",
      search: "Rechercher",
      preview: "Aperçu",
      inserting: "Insertion...",
      insertTemplate: "Insérer un modèle",
      insertBundle: "Insérer un lot",
      noTemplates: "Aucun modèle trouvé",
      noBundles: "Aucun lot trouvé",
      templateInsertedSuccess: "Modèle inséré avec succès",
      bundleInsertedSuccess: "Lot inséré avec succès",
      templateInsertError: "Échec de l'insertion du modèle",
      cancel: "Annuler",
      skipLogic: "Logique de saut",
      skipLogicHelp: "Afficher cette question uniquement lorsque les conditions sont remplies",
      displayLogic: "Logique d'affichage",
      displayLogicHelp: "Afficher cette question uniquement lorsque les réponses précédentes correspondent à ces conditions",
      conditionIf: "Si",
      logicTab: "Logique",
      noLogicYet: "Aucune logique de saut configurée",
      noLogicHint: "Cliquez sur une question et faites défiler jusqu'à la section Logique de saut pour ajouter des conditions.",
      logicSummaryPrefix: "Afficher cette question quand",
      settings: "Paramètres du sondage"
    },
    export: {
      loadError: "Impossible de charger l’enquête",
      description: "Télécharger les réponses soumises en CSV.",
      button: "Télécharger CSV",
      success: "Export prêt",
      error: "Échec d’export",
      xlsx: "Télécharger XLSX"
    },
    analytics: {
      filterScopeNote: "Ce filtre de dates restreint uniquement la liste des réponses et les tableaux croisés. Les cartes récapitulatives, les graphiques et le résumé IA reflètent toujours l'ensemble des réponses.",
      clearFilter: 'Effacer le filtre',
      columnQuestion: 'Question en colonne',
      crossTab: 'Tableau croisé',
      crossTabHelp: 'Comparez comment les réponses à une question se répartissent selon une autre.',
      crossTabNeedTwo: 'Sélectionnez deux questions à comparer.',
      crossTabPickTwo: 'Choisissez une question pour la ligne et la colonne',
      crossTabTitle: 'Tableau croisé',
      duration: 'Durée',
      filterBy: 'Filtrer par',
      from: 'Du',
      individualResponses: 'Réponses individuelles',
      language: 'Langue',
      next: 'Suivant',
      noAnswer: 'Aucune réponse',
      noResponsesYet: 'Aucune réponse pour le moment',
      deleteResponse: 'Supprimer la réponse',
      confirmDeleteResponse: 'Supprimer définitivement cette réponse ? Cette action est irréversible.',
      responseDeleted: 'Réponse supprimée.',
      deleteFailed: 'Échec de la suppression de la réponse.',
      download: 'Télécharger',
      downloadCsv: 'Télécharger CSV',
      downloadXlsx: 'Télécharger Excel (XLSX)',
      exportFailed: "Échec de l'exportation.",
      prev: 'Précédent',
      respondent: 'Répondant',
      rowQuestion: 'Question en ligne',
      sampleResponses: 'Exemples de réponses',
      submittedAt: 'Envoyé le',
      to: 'Au',
      viewResponses: 'Voir les réponses',
      wordCloud: 'Nuage de mots',
      title: "Analytique de l’enquête",
      totalResponses: "Nombre total de réponses",
      completionRate: "Taux de complétion",
      avgDuration: "Durée moyenne",
      minutes: "minutes",
      responsesOverTime: "Réponses dans le temps",
      questionBreakdown: "Analyse par question",
      responses: "réponses",
      noData: "Aucune réponse pour le moment",
      refresh: "Actualiser",
      detractors: "Détracteurs (0-6)",
      passives: "Passifs (7-8)",
      promoters: "Promoteurs (9-10)"
    },
    start: {
      untitledSurvey: "Enquête sans titre",
      timeEstimate: "Environ {{minutes}} {{unit}}",
      questionCount: "{{count}} {{unit}}",
      minute: "minute",
      minutes: "minutes",
      question: "question",
      questions: "questions",
      privacyNote: "Vos réponses restent confidentielles",
      startButton: "Commencer l'enquête",
      selectLanguageFirst: "Veuillez choisir une langue pour continuer.",
      title: "Démarrer l’enquête",
      subtitle: "Lancez l’enquête conversationnelle.",
      button: "Démarrer",
      loading: "Démarrage de l'enquête...",
      error: "Impossible de créer la session",
      selectLanguage: "Sélectionnez votre langue",
      selectLanguageSubtitle: "Choisissez la langue de votre session d'enquête",
      surveyFull: "Cette enquête a atteint sa limite de réponses"
    },
    session: {
      dwellWait: "Vous pourrez continuer dans {{n}} s",
      redirectingIn: "Redirection dans {seconds}…",
      continueNow: "Continuer maintenant",
      cancelRedirect: "Annuler",
      redirectCancelled: "Redirection automatique annulée.",
      continueToSite: "Continuer vers le site",
      loadError: "Impossible de charger la session",
      connected: "Connecté",
      wsError: "Erreur de connexion chat",
      answersSaved: "Réponses enregistrées",
      saveError: "Échec d'enregistrement",
      submitted: "Soumis",
      submitError: "Échec de soumission",
      progress: "Progression : {{answered}} / {{total}} obligatoires",
      placeholder: "Votre réponse",
      send: "Envoyer",
      verification: "Vérification",
      required: "Obligatoire",
      saveEdits: "Enregistrer les modifications",
      submit: "Valider & Soumettre",
      loading: "Chargement...",
      processing: "Traitement de votre réponse...",
      stopRecording: "Arrêter l’enregistrement",
      pressToSpeak: "Appuyez pour parler",
      reviewSubmit: "Vérifier & Soumettre",
      formMode: "Formulaire standard",
      conversationalMode: "Interview IA",
      voicePlayback: "Voix",
      switchToForm: "Passer au formulaire",
      switchToConversational: "Passer à l'interview IA",
      answerMore: "Répondez à {{count}} question(s) obligatoire(s) supplémentaires",
      transcribeFailed: "Transcription audio impossible. Veuillez réessayer.",
      audioProcessError: "Échec du traitement audio : {{error}}",
      micError: "Impossible d’accéder au micro : {{error}}",
      rankingPlaceholder: "Saisissez l’ordre (ex. : A > B > C)",
      thankYouTitle: "Merci !",
      thankYouMessage: "Vos réponses ont été soumises avec succès. Nous apprécions votre temps et vos commentaires.",
      returnHome: "Retour à l’accueil",
      status_in_progress: "En cours",
      pageOf: "Page {current} sur {total}",
      nextPage: "Suivant",
      previousPage: "Précédent",
      status_submitted: "Soumis",
      status_awaiting_verification: "En révision",
      previewBanner: "[MODE APERÇU] Ceci est un aperçu. Les réponses ne seront pas enregistrées dans les analyses.",
      redirecting: "Redirection...",
      progressLabel: "Progression",
      autoSaving: "Enregistrement…",
      autoSaved: "Enregistré",
      saveAndContinue: "Enregistrer et continuer plus tard",
      saveAndContinueTooltip: "Enregistrez votre progression et obtenez un lien pour continuer plus tard",
      saveAndContinueTitle: "Enregistrez votre progression",
      saveAndContinueBody: "Vos réponses ont été enregistrées. Ajoutez ce lien à vos favoris ou envoyez-le-vous par e-mail pour reprendre là où vous vous êtes arrêté, depuis n'importe quel appareil.",
      copyLink: "Copier le lien",
      linkCopied: "Lien copié dans le presse-papiers",
      close: "Fermer",
      alreadySubmittedWarning: "Nos enregistrements indiquent que vous avez peut-être déjà soumis ce sondage. Vous pouvez continuer, mais les réponses en double pourraient être exclues.",
      connectionError: "Erreur de connexion",
      reconnecting: "Reconnexion à l'assistant...",
      connectionLost: "Connexion perdue. Veuillez rafraîchir la page.",
      typeHere: "Tapez votre réponse ici...",
      send: "Envoyer",
      requiredField: "obligatoire",
      fieldRequired: "{label} est obligatoire",
      invalidEmail: "Veuillez saisir une adresse e-mail valide",
      invalidPhone: "Veuillez saisir un numéro de téléphone valide",
      invalidNumber: "Veuillez saisir un nombre valide",
      sumMustEqual: "La somme des valeurs doit être égale à {total} (actuel : {sum})",
      npsNotLikely: "Pas du tout probable",
      npsExtremely: "Extrêmement probable",
      chooseFile: "Choisir un fichier",
      chooseFileDrag: "Choisissez un fichier ou glissez-déposez",
      fileSelected: "Fichier sélectionné",
      selectedPrefix: "Sélectionné :",
      acceptedPrefix: "Accepté :",
      fileTooLarge: "La taille du fichier doit être inférieure à {size} Mo",
      constantSumInstruction: "Répartissez un total de {total} points entre les éléments ci-dessous.",
      remaining: "Restant :",
      pageBreakLabel: "Saut de page",
      noFormConfigured: "Aucun formulaire configuré",
      noFormConfiguredBody: "Le propriétaire du projet n'a pas encore configuré de formulaire personnalisé.",
      unsupportedField: "Type de champ non pris en charge : {type}",
      imageNotConfigured: "URL de l'image non configurée",
      videoNotConfigured: "URL de la vidéo non configurée",
      dragToReorder: "Glissez pour réorganiser",
      moveUp: "Monter",
      moveDown: "Descendre",
      requiredSummary: "Veuillez répondre aux questions surlignées avant de continuer.",
      yes: "Oui",
      no: "Non",
      optionN: "Option {n}",
      surveyImageAlt: "Informations sur l'enquête",
      interview: {
        title: "Entretien IA",
        description: "Cette question comprend un court entretien IA.",
        start: "Commencer l'entretien",
        starting: "Démarrage de l'entretien...",
        ending: "Sauvegarde de l'entretien...",
        completed: "Entretien terminé",
        end: "Terminer",
        stopRecording: "Arrêter l'enregistrement",
        tapToSpeak: "Appuyez pour parler",
        typePlaceholder: "Tapez votre réponse...",
      },
    },
    languages: {
      en: "Anglais",
      zh: "Chinois",
      fr: "Français",
      de: "Allemand",
      es: "Espagnol",
      nb: "Norvégien",
      nl: "Néerlandais",
      pt: "Portugais",
      ja: "Japonais",
      ar: "Arabe",
      flexible: "Flexible (Le participant choisit)"
    },
    synthetic: {
      dialogTitle: "Tester avec un participant synthétique",
      dialogSubtitle: "Configurez le profil démographique de votre participant simulé",
      age: "Âge", gender: "Genre", male: "Homme", female: "Femme", nonBinary: "Non-binaire", preferNotToSay: "Préfère ne pas dire",
      nationality: "Nationalité", ethnicity: "Ethnicité", education: "Éducation",
      highSchool: "Lycée", bachelors: "Licence", masters: "Master", phd: "Doctorat", vocational: "Formation professionnelle", other: "Autre",
      occupation: "Profession", personality: "Traits de personnalité", additionalContext: "Contexte supplémentaire (optionnel)",
      runTest: "Lancer le test", cancel: "Annuler",
      simulating: "Simulation des réponses en cours...", simulatingHint: "Cela peut prendre 15-30 secondes",
      resultsTitle: "Résultats du test synthétique", syntheticPersona: "Profil du participant synthétique", synthetic: "Synthétique",
      question: "Question", type: "Type", answer: "Réponse", close: "Fermer",
    },
    status: {
      draft: "Brouillon",
      published: "Publié",
      archived: "Archivé"
    }
  },
  nb: {
    tutorial: {
      title: "Kom i gang",
      step1Title: "Velkommen til undersøkelsesbyggeren",
      step1Body: "Lag samtalebaserte undersøkelser der en KI stiller smarte oppfølgingsspørsmål og tilpasser seg hver respondent — langt rikere enn et statisk skjema.",
      step2Title: "Legg til spørsmålene dine",
      step2Body: "Legg til spørsmål av alle typer — flervalg, skalaer, fritekst — og dra for å endre rekkefølgen. Bruk sideskift og seksjoner for å forme flyten.",
      step3Title: "Konfigurer KI-en",
      step3Body: "Angi språk, tone og hvor dypt KI-en graver med oppfølging på åpne svar. Du kan også oversette hele undersøkelsen automatisk.",
      step4Title: "Del & analyser",
      step4Body: "Publiser og del lenken din, og se svar og analyser komme inn etter hvert som deltakerne fullfører undersøkelsen.",
      back: "Tilbake",
      next: "Neste",
      done: "Kom i gang",
      skip: "Hopp over",
      help: "Slik fungerer det",
    },
    import: {
      menuLabel: "Importer fil", reviewTitle: "Gå gjennom importert undersøkelse",
      reviewIntro: "Vi laget {{n}} spørsmål fra filen din. Gå gjennom nedenfor, og opprett deretter undersøkelsen — du kan redigere alt i byggeren.",
      cancel: "Avbryt", titleField: "Undersøkelsestittel", descriptionField: "Beskrivelse",
      reportTitle: "Importmerknader", preview: "Spørsmål", pageBreak: "Sideskift",
      sectionBreak: "Seksjon", required: "påkrevd", create: "Opprett undersøkelse",
      parseFailed: "Kunne ikke importere denne filen.", createFailed: "Kunne ikke opprette undersøkelsen.",
      uploadTitle: "Importer en undersøkelse", parsing: "Leser filen din…",
      dropHere: "Dra en fil hit, eller klikk for å bla",
      dropHint: "Opptil 25 MB. Vi lager et utkast du kan gå gjennom og redigere.",
      supported: "Støttede formater",
      formatNote: "AI gjør Word og PDF om til en undersøkelse — den gjengir et eksisterende skjema eller lager et utkast fra notatene dine. Qualtrics .qsf og QualiTaTi .qlt tolkes nøyaktig.",
      exportDefinition: "Last ned undersøkelse", exportWord: "Last ned som Word (.docx)", exportQlt: "Last ned som QualiTaTi (.qlt)", exportFailed: "Eksport mislyktes.",
    },
    translations: {
      menuLabel: "Oversettelser", title: "Oversettelser av spørreundersøkelsen",
      intro: "Legg til et språk for å oversette hele undersøkelsen automatisk med AI, og gå deretter gjennom og rediger hver tekst. Respondentene velger språk ved start; tomme felt faller tilbake til standardspråket ({{lang}}).",
      source: "kilde", addLanguage: "Legg til språk",
      empty: "Ingen språk ennå. Klikk «Legg til språk» for å lage en AI-oversettelse.",
      surveyInfo: "Undersøkelsesinfo", fieldTitle: "Tittel", fieldDescription: "Beskrivelse",
      fieldThankYouTitle: "Takketittel", fieldThankYouMessage: "Takkemelding",
      fieldPrompt: "Spørsmål", options: "Alternativer", columns: "Skalakolonner",
      retranslate: "Oversett på nytt", close: "Lukk", save: "Lagre",
      translated: "Automatisk oversettelse fullført. Gå gjennom og rediger nedenfor.", translateFailed: "Oversettelsen mislyktes.",
      saved: "Oversettelse lagret.", saveFailed: "Lagring mislyktes.",
      confirmDelete: "Fjerne dette språket og alle oversettelsene?", deleteFailed: "Sletting mislyktes.",
      confirmRetranslate: "Oversette på nytt fra bunnen? Dette overskriver endringene dine for dette språket.",
      loadFailed: "Kunne ikke laste oversettelser.",
    },
    hub: {
      emptyTitle: "Lag ditt første skjema",
      emptyBody: "Start fra bunnen av, generer ett med KI, velg en mal, eller importer en Word-, PDF- eller Qualtrics-fil.",
      emptyNoResults: "Ingen skjemaer samsvarer med filtrene dine.",
      clearFilters: "Fjern filtre",
      title: "Skjemaer",
      createProject: "Opprett skjemaprosjekt",
      manageSurveys: "Administrer skjemaer",
      createDialogTitle: "Nytt skjemaprosjekt",
      projectName: "Prosjektnavn",
      projectOutline: "Prosjektbeskrivelse",
      interviewType: "Intervjutype",
      interviewLanguage: "Intervjuspråk",
      cancel: "Avbryt",
      save: "Lagre",
      createSuccess: "Prosjekt opprettet",
      createError: "Oppretting feilet",
      fillRequired: "Vennligst fyll ut obligatoriske felt",
      loadError: "Kunne ikke laste prosjekter",
      confirmDeleteProject: "Slette dette skjemaprosjektet og alle tilhørende skjemaer? Dette kan ikke angres.",
      projectDeleted: "Prosjekt slettet",
      surveysInProject: "Skjemaer i dette prosjektet",
      noSurveysYet: "Ingen skjemaer ennå",
      manageProjectSurveys: "Administrer skjemaer",
      noProjectsYet: "Ingen skjemaprosjekter opprettet ennå",
      experimentalTitle: "Eksperimentell funksjon",
      experimentalMessage: "Samtaleskjema er et eksperimentelt verktøy under aktiv utvikling. Noen funksjoner kan endres eller oppføre seg uventet. Din tilbakemelding hjelper oss å forbedre!",
      search: "Søk i undersøkelser",
      filterAll: "Alle",
      filterDraft: "Utkast",
      filterPublished: "Publisert",
      sortNewest: "Nyeste først",
      sortAlpha: "Alfabetisk",
      noResults: "Ingen undersøkelser samsvarer med søket ditt",
      analytics: "Analyse",
      duplicate: "Dupliser",
      duplicateSuccess: "Undersøkelse duplisert",
    },
    list: {
      emptyTitle: "Ingen skjemaer i dette prosjektet ennå",
      emptyBody: "Lag ditt første skjema for å begynne å samle inn svar.",
      fromTemplate: "Start fra mal",
      fromWizard: "Generer med KI",
      fromTemplateTitle: "Start fra en mal",
      fromTemplateSubtitle: "Velg en mal og få et redigerbart undersøkelsesutkast. Vi forhåndsutfyller spørsmål, KI-oppfølginger og fornuftige standardvalg.",
      templatesEmpty: "Ingen maler tilgjengelig akkurat nå.",
      creatingFromTemplate: "Oppretter…",
      wizard: {
        title: "Generer en undersøkelse med KI",
        subtitle: "Fortell Mimi hva du vil lære, så lager hun et redigerbart utkast.",
        generating: "Mimi skriver utkastet…",
        generatingHint: "Tar vanligvis 20-40 sekunder. Vi genererer et tilpasset spørsmålssett med passende KI-oppfølginger.",
        goalLabel: "Hva ønsker du å lære?",
        goalPlaceholder: "F.eks. hvorfor kunder slutter etter første måned – hva som driver beslutningen og hva som kunne endret den.",
        goalHelp: "Spesifikke mål gir bedre undersøkelser. Sikt på 1–3 setninger.",
        audienceLabel: "Hvem skal svare på undersøkelsen?",
        audiencePlaceholder: "F.eks. kunder som avsluttet de siste 30 dagene",
        languageLabel: "Språk",
        depthLabel: "Dybde",
        nQuestionsLabel: "Ønsket antall spørsmål",
        generate: "Generer",
        errorGoalTooShort: "Målet må være minst 10 tegn for at KI-en skal ha noe å jobbe med.",
        errorAudienceTooShort: "Si hvem målgruppen er, slik at KI-en kan tilpasse spørsmålene.",
      },
      title: "Skjema",
      create: "Opprett skjema",
      createDialogTitle: "Nytt skjema",
      titleField: "Tittel",
      descriptionField: "Beskrivelse",
      cancel: "Avbryt",
      save: "Lagre",
      builder: "Bygger",
      export: "Eksporter",
      publish: "Publiser",
      status: "Status",
      linkCopied: "Lenke kopiert",
      publishFirst: "Vennligst publiser undersøkelsen først",
      publishReady: "Publisert",
      publishError: "Publisering feilet",
      createSuccess: "Skjema opprettet",
      createError: "Kunne ikke opprette",
      exportReady: "CSV klar",
      exportError: "Eksport feilet",
      error: "Kunne ikke laste skjemaer",
      copyLink: "Kopier delingslenke",
      downloadCsv: "Last ned CSV",
      deleteSurvey: "Slett skjema",
      publishGetLink: "Publiser og få lenke",
      published: "Skjemaet er publisert — delingslenken din er klar.",
      questionsLabel: "spørsmål",
      responsesLabel: "svar"
    },
    builder: {
      minDwellLabel: "Minste visningstid (sekunder)",
      minDwellHelp: "Respondenter må bli på denne siden så lenge før Neste/Send låses opp. 0 = av.",
      saving: "Lagrer…",
      saved: "Lagret",
      saveFailed: "Kunne ikke lagre endringene",
      aiFollowUp: {
        label: "AI-oppfølging",
        helperNone: "Ingen oppfølging — respondentene svarer og går videre",
        helperLight: "Light — én samtaleoppfølging for å berike svaret",
        helperDeep: "Deep — inntil tre oppfølginger for grundig kvalitativ innsikt",
        optionNone: "Ingen — ingen oppfølging",
        optionLight: "Light — 1 oppfølging",
        optionDeep: "Deep — inntil 3 oppfølginger",
      },
      cancel: "Avbryt",
      surveyMode: {
        label: "Undersøkelsesmodus",
        form: {
          title: "Tradisjonell undersøkelse",
          subtitle: "Standard skjemabasert undersøkelse, uten KI",
        },
        aiFollowUp: {
          title: "Undersøkelse med AI-oppfølging",
          subtitle: "Skjema + AI-oppfølgingsspørsmål på åpne svar",
          hint: "Tips: sett \"AI-oppfølging\"-intensitet på hvert åpent spørsmål nedenfor. Light = 1 oppfølging; Deep = inntil 3.",
        },
        aiInterview: {
          title: "Samtalebasert undersøkelse",
          subtitle: "KI-drevet samtaleintervju, fri dialog",
        },
      },
      preflight: {
        title: "Sjekk før publisering",
        loading: "Sjekker undersøkelsen din…",
        ready: "Klar til publisering",
        readyHint: "Undersøkelsen din bestå alle sjekkene. Respondentene får en ryddig opplevelse.",
        hasErrors: "Rett opp punktene under før du publiserer:",
        warningsOnly: "Du kan publisere, men sjekk gjerne disse tingene først:",
        fallback: "Kunne ikke kjøre helsesjekken — publisering er likevel tillatt.",
        rerun: "Sjekk på nytt",
      },
      apply: 'Bruk',
      backgroundColor: 'Bakgrunn',
      closeAt: 'Stenger kl.',
      copied: 'Kopiert',
      copy: 'Kopier',
      copyEmbed: 'Kopier innbyggingskode',
      downloadQR: 'Last ned QR-kode (PNG)',
      embedCopied: 'Innbyggingskode kopiert',
      embedHelp: 'Lim inn denne HTML-koden på nettstedet ditt for å bygge inn undersøkelsen.',
      fontFamily: 'Skriftstil',
      fontMono: 'Monospace (teknisk)',
      fontRounded: 'Rundet (vennlig)',
      fontSerif: 'Serif (klassisk)',
      fontSystem: 'Systemstandard (moderne)',
      headerImageUrl: 'URL til toppbanner',
      headerImageUrlHelp: 'Bredt bannerbilde øverst. Valgfritt.',
      logoUrl: 'URL til logobilde',
      logoUrlHelp: 'Vises over undersøkelsestittelen. Valgfritt.',
      openAt: 'Åpner kl.',
      passwordActive: 'Et passord er satt. Skriv inn et nytt nedenfor for å endre det, eller tøm for å fjerne det.',
      passwordHint: 'Krev at respondenter skriver inn et passord før de starter.',
      passwordPlaceholder: 'La stå tomt for ingen passord',
      passwordProtection: 'Passordbeskyttelse',
      passwordRemoved: 'Passord fjernet',
      passwordSaved: 'Passord oppdatert',
      pipingChip: 'Sett inn et tidligere svar',
      pipingTooltipBody: 'Klikk på en etikett for å kopiere den, og lim den inn i spørsmålsteksten. Når respondenten ser den, blir den erstattet med deres tidligere svar.',
      pipingTooltipTitle: 'Vis et tidligere svar i dette spørsmålet',
      primaryColor: 'Hovedfarge',
      qrHint: 'Skann med telefonkameraet for å åpne undersøkelsen',
      removePassword: 'Fjern passord',
      resetTheme: 'Tilbakestill til standard',
      sampleQuestionText: 'Slik ser et spørsmål ut.',
      sampleSubmit: 'Send inn',
      sampleSurveyTitle: 'Tittelen på undersøkelsen din',
      saveSchedule: 'Lagre tidsplan',
      schedule: 'Tidsplan',
      scheduleHelp: 'Åpne og lukk undersøkelsen automatisk til bestemte tider. La stå tomt for alltid åpen.',
      scheduleSaved: 'Tidsplan lagret',
      share: 'Del',
      shareOn: 'Del på',
      shareTitle: 'Del undersøkelsen din',
      socialHelp: 'Del undersøkelsen din på sosiale plattformer:',
      tabEmbed: 'Innbygging',
      tabLink: 'Lenke / QR',
      tabProtect: 'Beskyttelse',
      tabSocial: 'Sosialt',
      textColor: 'Tekstfarge',
      themeBranding: 'Tema og merkevare',
      themeBrandingHelp: 'Tilpass utseendet og følelsen av undersøkelsessiden som respondentene ser.',
      themePresets: 'Raske forhåndsinnstillinger',
      themePreview: 'Forhåndsvisning',
      constantSumItems: 'Elementer',
      constantSumItemsHelp: 'Ett element per linje',
      constantSumTotal: 'Totalt',
      constantSumTotalHelp: 'Sum respondentene må fordele (f.eks. 100)',
      emailAutoConfig: 'Valideres som en e-postadresse',
      fileAcceptHelp: 'Tillatte filtyper (kommadelt, f.eks. .pdf,.docx)',
      fileAcceptTypes: 'Godkjente typer',
      fileMaxSize: 'Maks filstørrelse (MB)',
      imageChoiceHelp: 'Én bilde-URL per linje. Valgfrie etiketter samsvarer etter rekkefølge.',
      imageChoiceLabels: 'Etiketter',
      imageChoiceMultiple: 'Tillat flere valg',
      imageChoiceUrls: 'Bilde-URLer',
      phoneAutoConfig: 'Valideres som et telefonnummer',
      ratingMax: 'Høyeste vurdering',
      sectionBreakInfo: 'Kun visuell skillelinje — ingen svar samles inn.',
      thankYouPage: 'Takkeside',
      thankYouRedirectHelp: 'Valgfri URL å omdirigere til etter innsending',
      yesNoAutoConfig: 'Ja / Nei-valg',
      loadError: "Kunne ikke laste skjema",
      saveMeta: "Skjema oppdatert",
      metaSaved: "Skjemadetaljer lagret",
      saveError: "Lagring feilet",
      addError: "Kunne ikke legge til",
      questionAdded: "Spørsmål lagt til",
      saveQuestion: "Spørsmål lagret",
      questionSaved: "Spørsmål lagret",
      questionDeleted: "Spørsmål slettet",
      deleteError: "Sletting feilet",
      reorderError: "Omorganisering feilet",
      confirmDelete: "Slette dette spørsmålet?",
      deleteSurvey: "Slett skjema",
      confirmDeleteSurvey: "Er du sikker på at du vil slette dette skjemaet? Dette kan ikke angres.",
      surveyDeleted: "Skjema slettet",
      surveyFlow: "Spørreskjemaflyt",
      addBlock: "Legg til blokk",
      addRandomizer: "Legg til randomiserer",
      blockPlaceholder: "Navnløs blokk",
      deleteBlock: "Slett blokk",
      confirmDeleteBlock: "Slette denne blokken? Spørsmålene vil bli ufrivillig.",
      randomizer: "Randomiserer",
      randomizerShow: "Vis",
      randomizerOf: "av",
      addVariant: "Legg til variant",
      questionsCount: "{n} spørsmål",
      unassigned: "Ikke tildelte spørsmål",
      noBlocks: "Ingen blokker ennå",
      renameBlock: "Gi nytt navn",
      addQuestion: "Legg til",
      publishCopied: "Publisert og lenke kopiert",
      publishSuccess: "Skjema publisert",
      publishError: "Publisering feilet",
      linkCopied: "Lenke kopiert",
      copyLink: "Kopier lenke",
      questions: "Spørsmål",
      noQuestions: "Ingen spørsmål ennå",
      clickToAdd: "Klikk for å legge til første spørsmål",
      addFirstQuestion: "Legg til spørsmål",
      untitledQuestion: "(Uten tittel)",
      promptPlaceholder: "Skriv inn spørsmålet...",
      surveyTitle: "Skjematittel",
      surveyDescription: "Skjemabeskrivelse",
      noDescription: "Ingen beskrivelse",
      questionType: "Spørsmålstype",
      variableName: "Variabelnavn",
      variableHelp: "snake_case, unikt",
      requiredQuestion: "Påkrevd spørsmål",
      isConversational: "Spør i samtale",
      askForm: "Spør via skjema",
      optionsPlaceholder: "Alternativ 1\nAlternativ 2\nAlternativ 3",
      minValue: "Minimumsverdi",
      maxValue: "Maksimumsverdi",
      saveChanges: "Lagre endringer",
      moreOptions: "flere alternativer",
      moveUp: "Flytt opp",
      moveDown: "Flytt ned",
      moveToBlock: "Flytt til blokk",
      expand: "Utvid",
      collapse: "Skjul",
      delete: "Slett",
      duplicate: "Dupliser",
      duplicateSuccess: "Spørsmål duplisert",
      republish: "Publiser på nytt",
      unpublish: "Avpubliser",
      unpublishing: "Avpubliserer…",
      unpublishSuccess: "Skjema avpublisert. Det er nå et utkast.",
      unpublishError: "Kunne ikke avpublisere skjema",
      unpublishTooltip: "Blokker nye deltakere. Eksisterende svar beholdes; delingslenken bevares for republisering.",
      confirmUnpublish: "Avpublisere dette skjemaet? Nye deltakere blir blokkert. Eksisterende svar beholdes. Du kan republisere senere med samme lenke.",
      meta: "Skjemainformasjon",
      titleField: "Tittel",
      descriptionField: "Beskrivelse",
      descriptionPlaceholder: "Beskriv formålet med denne undersøkelsen...",
      language: "Språk",
      save: "Lagre",
      imageUrl: "Bilde-URL",
      imageUrlHelp: "Valgfritt: Angi en URL for å vise et bilde",
      imagePreview: "Forhåndsvisning",
      imageLoadError: "Kunne ikke laste bilde. Vennligst sjekk URL-en.",
      addImage: "Legg til bilde",
      questionImage: "Spørsmålsbilde",
      removeImage: "Fjern bilde",
      uploadImage: "Last opp bilde",
      uploading: "Laster opp…",
      imageUrlPlaceholder: "Eller lim inn bilde-URL",
      imageHelp: "PNG, JPEG, GIF eller WebP. Maks 2 MB.",
      imageUploadFailed: "Opplasting mislyktes.",
      uploadImages: "Last opp bilder",
      addMoreImages: "Legg til flere",
      urlPasteHint: "Trykk Enter for å legge til",
      removeAllImages: "Fjern alle",
      itemsToRank: "Elementer å rangere",
      itemsToRankHelp: "Skriv inn elementer som skal rangeres, ett per linje",
      bipolarLabels: "Skalaetiketter",
      leftLabel: "Venstre etikett",
      rightLabel: "Høyre etikett",
      surveyLanguage: "Undersøkelsesspråk",
      surveyLanguageHelp: "Velg 'Fleksibel' for å la deltakere velge sitt språk",
      flexible: "Fleksibel (Deltaker velger)",
      publish: "Publiser og kopier lenke",
      editor: "Spørsmålsredaktør",
      selectPrompt: "Velg et spørsmål for å redigere",
      prompt: "Spørsmålstekst",
      variable: "Variabelnavn",
      required: "Obligatorisk",
      options: "Alternativer (ett per linje)",
      optionsHelp: "Skriv ett alternativ per linje",
      min: "Min",
      max: "Maks",
      saveQuestionBtn: "Lagre spørsmål",
      types: {
        image: "Bilde",
        short_text: "Kort svar",
        long_text: "Avsnitt",
        single_choice: "Flervalg",
        multi_choice: "Avmerkingsbokser",
        number: "Tall",
        date: "Dato",
        ranking: "Rangering",
        bipolar_scale: "Bipolar skala",
        info_text: "Informasjonstekst",
        slider: "Skyveknapp",
        dropdown: "Nedtrekksmeny",
        nps: "Net Promoter Score",
        matrix: "Matrise",
        ai_interview: "AI-intervju",
        rating_scale: "Vurderingsskala",
        yes_no: "Ja / Nei",
        email: "E-post",
        phone: "Telefonnummer",
        image_choice: "Bildevalg",
        file_upload: "Filopplasting",
        constant_sum: "Konstant sum",
        section_break: "Seksjonsbrudd",
        page_break: "Sideskift"
      },
      typeDescriptions: {
        short_text: "Kort tekstsvar",
        long_text: "Langt tekstsvar",
        single_choice: "Velg ett alternativ",
        multi_choice: "Velg flere alternativer",
        number: "Numerisk verdi",
        date: "Datovelger",
        ranking: "Ranger elementer",
        bipolar_scale: "Skala mellom to motsetninger",
        info_text: "Vis tekst eller bilder (ingen svar påkrevd)",
        slider: "Skyv for å velge en verdi i et område",
        dropdown: "Velg ett alternativ fra en nedtrekksliste",
        nps: "Vurder fra 0 til 10 (Net Promoter Score)",
        matrix: "Vurder flere elementer på samme skala",
        ai_interview: "Bygg inn en AI-drevet intervjusamtale",
        rating_scale: "Stjernevurdering (f.eks. 1-5 stjerner)",
        yes_no: "Enkelt ja eller nei-spørsmål",
        email: "E-postadresse med validering",
        phone: "Telefonnummer med validering",
        image_choice: "Velg blant bildealternativer",
        file_upload: "Last opp et filvedlegg",
        constant_sum: "Fordel poeng mellom elementer",
        section_break: "Visuell separator mellom seksjoner",
        page_break: "Starter en ny side for respondentene"
      },
      stepValue: "Steg",
      npsAutoConfig: "NPS er automatisk konfigurert som en 0-10 skala. Ingen ekstra konfigurasjon nødvendig.",
      matrixRows: "Radetiketter (en per linje)",
      matrixRowsHelp: "Skriv inn elementene som skal vurderes, ett per linje",
      matrixColumns: "Kolonneetiketter (en per linje)",
      matrixColumnsHelp: "Skriv inn skalaalternativene, ett per linje",
      interviewConfig: {
        title: "AI-intervjuinnstillinger",
        interactionMode: "Interaksjonsmodus",
        voiceToVoice: "Tale til tale",
        voiceToText: "Tale til tekst",
        textToText: "Tekst til tekst",
        textToVoice: "Tekst til tale",
        language: "Intervjuspråk",
        maxDuration: "Maks varighet (minutter)",
        outline: "Intervjuskisse / Instruksjoner",
        outlinePlaceholder: "Beskriv hva AI-en skal spørre om...",
        outlineHelp: "AI-intervjueren vil følge denne skissen for å lede samtalen.",
      },
      defaultPrompt: "Nytt spørsmål",
      metaSaved: "Skjemadetaljer lagret",
      questionSaved: "Spørsmål lagret",
      confirmDelete: "Slett dette spørsmålet?",
      publishSuccess: "Skjema publisert",
      noQuestions: "Ingen spørsmål ennå",
      clickToAdd: "Klikk for å legge til første spørsmål",
      addFirstQuestion: "Legg til spørsmål",
      untitledQuestion: "(Uten tittel)",
      promptPlaceholder: "Skriv inn spørsmålet...",
      surveyTitle: "Skjematittel",
      surveyDescription: "Skjemabeskrivelse",
      noDescription: "Ingen beskrivelse",
      questionType: "Spørsmålstype",
      variableName: "Variabelnavn",
      requiredQuestion: "Påkrevd spørsmål",
      optionsPlaceholder: "Alternativ 1\nAlternativ 2\nAlternativ 3",
      minValue: "Minimumsverdi",
      maxValue: "Maksimumsverdi",
      saveChanges: "Lagre endringer",
      moreOptions: "flere alternativer",
      moveUp: "Flytt opp",
      moveDown: "Flytt ned",
      moveToBlock: "Flytt til blokk",
      expand: "Utvid",
      collapse: "Skjul",
      delete: "Slett",
      duplicate: "Dupliser",
      duplicateSuccess: "Spørsmål duplisert",
      republish: "Publiser på nytt",
      unpublish: "Avpubliser",
      unpublishing: "Avpubliserer…",
      unpublishSuccess: "Skjema avpublisert. Det er nå et utkast.",
      unpublishError: "Kunne ikke avpublisere skjema",
      unpublishTooltip: "Blokker nye deltakere. Eksisterende svar beholdes; delingslenken bevares for republisering.",
      confirmUnpublish: "Avpublisere dette skjemaet? Nye deltakere blir blokkert. Eksisterende svar beholdes. Du kan republisere senere med samme lenke.",
      preview: "Forhåndsvisning",
      previewMode: "Forhåndsvisningsmodus",
      conditions: "Betingelser",
      addCondition: "Legg til betingelse",
      removeCondition: "Fjern",
      conditionLogicAnd: "OG (alle må stemme)",
      conditionLogicOr: "ELLER (minst én må stemme)",
      conditionQuestion: "Hvis spørsmål",
      conditionOperator: "Operator",
      conditionValue: "Verdi",
      operatorEquals: "er lik",
      operatorNotEquals: "er ikke lik",
      operatorContains: "inneholder",
      operatorGreaterThan: "er større enn",
      operatorLessThan: "er mindre enn",
      operatorIsEmpty: "er tom",
      operatorIsNotEmpty: "er ikke tom",
      pipingHelp: "Tips: Bruk ${variable_name} for å sette inn et tidligere svar",
      availableVariables: "Tilgjengelige: {{variables}}",
      randomizeQuestions: "Tilfeldig rekkefølge på spørsmål",
      randomizeOptions: "Tilfeldig rekkefølge på alternativer",
      thankYouSettings: "Takkeside",
      thankYouTitle: "Tittel",
      thankYouMessage: "Melding",
      thankYouRedirect: "Videresend-URL (valgfritt)",
      maxResponses: "Maks svar (valgfritt)",
      maxResponsesHelp: "La stå tomt for ubegrenset",
      deliveryMode: "Leveringsmodus",
      deliveryConversational: "Samtalebasert (AI-intervju)",
      deliveryForm: "Standardskjema",
      deliveryModeHelp: "Samtalebasert modus bruker AI til å gjennomføre intervjuer. Standardskjema viser alle spørsmål på en gang.",
      testWithSynthetic: "Test med syntetisk menneske",
      fromLibrary: "Fra bibliotek",
      fromLibraryHint: "Kopier fra ferdige maler og pakker",
      templateLibrary: "Malgalleri",
      templates: "Maler",
      bundles: "Pakker",
      allTemplates: "Alle maler",
      myTemplates: "Mine maler",
      allCategories: "Alle kategorier",
      selectTemplate: "Velg en mal",
      templateName: "Malnavn",
      category: "Kategori",
      search: "Søk",
      preview: "Forhåndsvisning",
      inserting: "Setter inn...",
      insertTemplate: "Sett inn mal",
      insertBundle: "Sett inn pakke",
      noTemplates: "Ingen maler funnet",
      noBundles: "Ingen pakker funnet",
      templateInsertedSuccess: "Mal satt inn",
      bundleInsertedSuccess: "Pakke satt inn",
      templateInsertError: "Feil ved innsetting av mal",
      cancel: "Avbryt",
      skipLogic: "Hopplogikk",
      skipLogicHelp: "Vis dette spørsmålet bare når betingelsene er oppfylt",
      displayLogic: "Visningslogikk",
      displayLogicHelp: "Vis dette spørsmålet bare når tidligere svar samsvarer med disse betingelsene",
      conditionIf: "Hvis",
      logicTab: "Logikk",
      noLogicYet: "Ingen hopplogikk konfigurert",
      noLogicHint: "Klikk på et spørsmål og bla ned til Hopplogikk-seksjonen for å legge til betingelser.",
      logicSummaryPrefix: "Vis dette spørsmålet når",
      settings: "Undersøkelsesinnstillinger"
    },
    export: {
      loadError: "Kunne ikke laste",
      description: "Last ned innsendte svar som CSV.",
      button: "Last ned CSV",
      success: "Eksport klar",
      error: "Eksport feilet",
      xlsx: "Last ned XLSX"
    },
    analytics: {
      filterScopeNote: "Dette datofilteret begrenser bare svarlisten og krysstabellene. Sammendragskortene, diagrammene og AI-sammendraget viser alltid alle svar.",
      clearFilter: 'Fjern filter',
      columnQuestion: 'Kolonnespørsmål',
      crossTab: 'Krysstabell',
      crossTabHelp: 'Sammenlign hvordan svarene på ett spørsmål fordeler seg på et annet.',
      crossTabNeedTwo: 'Velg to spørsmål å sammenligne.',
      crossTabPickTwo: 'Velg et rad- og kolonnespørsmål',
      crossTabTitle: 'Krysstabulering',
      duration: 'Varighet',
      filterBy: 'Filtrer etter',
      from: 'Fra',
      individualResponses: 'Enkeltsvar',
      language: 'Språk',
      next: 'Neste',
      noAnswer: 'Ingen svar',
      noResponsesYet: 'Ingen svar ennå',
      deleteResponse: 'Slett svar',
      confirmDeleteResponse: 'Slette dette svaret permanent? Dette kan ikke angres.',
      responseDeleted: 'Svar slettet.',
      deleteFailed: 'Kunne ikke slette svaret.',
      download: 'Last ned',
      downloadCsv: 'Last ned CSV',
      downloadXlsx: 'Last ned Excel (XLSX)',
      exportFailed: 'Eksport mislyktes.',
      prev: 'Forrige',
      respondent: 'Respondent',
      rowQuestion: 'Radspørsmål',
      sampleResponses: 'Eksempelsvar',
      submittedAt: 'Sendt inn',
      to: 'Til',
      viewResponses: 'Vis svar',
      wordCloud: 'Ordsky',
      title: "Undersøkelsesanalyse",
      totalResponses: "Totalt antall svar",
      completionRate: "Fullføringsgrad",
      avgDuration: "Gj.sn. varighet",
      minutes: "minutter",
      responsesOverTime: "Svar over tid",
      questionBreakdown: "Spørsmålsanalyse",
      responses: "svar",
      noData: "Ingen svar ennå",
      refresh: "Oppdater",
      detractors: "Kritikere (0-6)",
      passives: "Passive (7-8)",
      promoters: "Promotører (9-10)"
    },
    start: {
      untitledSurvey: "Undersøkelse uten tittel",
      timeEstimate: "Omtrent {{minutes}} {{unit}}",
      questionCount: "{{count}} {{unit}}",
      minute: "minutt",
      minutes: "minutter",
      question: "spørsmål",
      questions: "spørsmål",
      privacyNote: "Svarene dine er konfidensielle",
      startButton: "Start undersøkelsen",
      selectLanguageFirst: "Velg et språk for å fortsette.",
      title: "Start skjema",
      subtitle: "Start det samtalebaserte skjemaet.",
      button: "Start",
      loading: "Starter undersøkelse...",
      error: "Kunne ikke starte sesjon",
      selectLanguage: "Velg ditt språk",
      selectLanguageSubtitle: "Velg språket for din undersøkelse",
      surveyFull: "Denne undersøkelsen har nådd sin svarmaksgrense"
    },
    session: {
      dwellWait: "Du kan fortsette om {{n}} s",
      redirectingIn: "Omdirigerer om {seconds}…",
      continueNow: "Fortsett nå",
      cancelRedirect: "Avbryt",
      redirectCancelled: "Automatisk omdirigering avbrutt.",
      continueToSite: "Fortsett til siden",
      loadError: "Kunne ikke laste sesjon",
      connected: "Koblet til",
      wsError: "Chat-tilkobling feilet",
      answersSaved: "Svar lagret",
      saveError: "Lagring feilet",
      submitted: "Sendt inn",
      submitError: "Innlevering feilet",
      progress: "Fremdrift: {{answered}} / {{total}} påkrevd",
      placeholder: "Skriv svaret ditt",
      send: "Send",
      verification: "Verifisering",
      required: "Påkrevd",
      saveEdits: "Lagre endringer",
      submit: "Godkjenn og send",
      loading: "Laster...",
      processing: "Behandler svaret ditt...",
      stopRecording: "Stopp opptak",
      pressToSpeak: "Trykk for å snakke",
      reviewSubmit: "Se gjennom og send",
      formMode: "Standardskjema",
      conversationalMode: "AI-intervju",
      voicePlayback: "Stemme",
      switchToForm: "Bytt til skjema",
      switchToConversational: "Bytt til AI-intervju",
      answerMore: "Svar på {{count}} flere påkrevde spørsmål",
      transcribeFailed: "Kunne ikke transkribere lyd. Prøv igjen.",
      audioProcessError: "Lydbehandling feilet: {{error}}",
      micError: "Får ikke tilgang til mikrofon: {{error}}",
      rankingPlaceholder: "Skriv rekkefølge (f.eks. A > B > C)",
      thankYouTitle: "Takk!",
      thankYouMessage: "Dine svar er sendt inn. Vi setter pris på din tid og tilbakemelding.",
      returnHome: "Tilbake til hjemmesiden",
      status_in_progress: "Pågår",
      pageOf: "Side {current} av {total}",
      nextPage: "Neste",
      previousPage: "Tilbake",
      status_submitted: "Sendt inn",
      status_awaiting_verification: "Under gjennomgang",
      previewBanner: "[FORHÅNDSVISNING] Dette er en forhåndsvisning. Svar vil ikke bli lagret i analysen.",
      redirecting: "Omdirigerer...",
      progressLabel: "Fremdrift",
      autoSaving: "Lagrer…",
      autoSaved: "Lagret",
      saveAndContinue: "Lagre og fortsett senere",
      saveAndContinueTooltip: "Lagre fremdriften din og få en lenke for å fortsette senere",
      saveAndContinueTitle: "Lagre fremdriften din",
      saveAndContinueBody: "Svarene dine er lagret. Bokmerk denne lenken eller send den til deg selv på e-post for å fortsette der du slapp – fra hvilken som helst enhet.",
      copyLink: "Kopier lenke",
      linkCopied: "Lenken er kopiert til utklippstavlen",
      close: "Lukk",
      alreadySubmittedWarning: "Våre registreringer viser at du kan ha sendt inn denne undersøkelsen allerede. Du kan fortsatt fortsette, men dupliserte svar kan bli ekskludert.",
      connectionError: "Tilkoblingsfeil",
      reconnecting: "Kobler til assistenten på nytt...",
      connectionLost: "Tilkoblingen er brutt. Vennligst oppdater siden.",
      typeHere: "Skriv svaret ditt her...",
      send: "Send",
      requiredField: "påkrevd",
      fieldRequired: "{label} er påkrevd",
      invalidEmail: "Vennligst oppgi en gyldig e-postadresse",
      invalidPhone: "Vennligst oppgi et gyldig telefonnummer",
      invalidNumber: "Vennligst oppgi et gyldig tall",
      sumMustEqual: "Verdiene må summere til {total} (nå: {sum})",
      npsNotLikely: "Ikke sannsynlig i det hele tatt",
      npsExtremely: "Svært sannsynlig",
      chooseFile: "Velg fil",
      chooseFileDrag: "Velg fil eller dra og slipp",
      fileSelected: "Fil valgt",
      selectedPrefix: "Valgt:",
      acceptedPrefix: "Godtatt:",
      fileTooLarge: "Filstørrelsen må være mindre enn {size} MB",
      constantSumInstruction: "Fordel totalt {total} poeng på elementene nedenfor.",
      remaining: "Gjenstår:",
      pageBreakLabel: "Sideskift",
      noFormConfigured: "Ingen skjema konfigurert",
      noFormConfiguredBody: "Prosjekteieren har ikke satt opp et tilpasset skjema ennå.",
      unsupportedField: "Felttype som ikke støttes: {type}",
      imageNotConfigured: "Bilde-URL er ikke konfigurert",
      videoNotConfigured: "Video-URL er ikke konfigurert",
      dragToReorder: "Dra for å endre rekkefølge",
      moveUp: "Flytt opp",
      moveDown: "Flytt ned",
      requiredSummary: "Vennligst svar på de uthevede spørsmålene før du fortsetter.",
      yes: "Ja",
      no: "Nei",
      optionN: "Alternativ {n}",
      surveyImageAlt: "Informasjon om undersøkelsen",
      interview: {
        title: "AI-intervju",
        description: "Dette spørsmålet inkluderer et kort AI-intervju.",
        start: "Start intervju",
        starting: "Starter intervju...",
        ending: "Lagrer intervju...",
        completed: "Intervju fullført",
        end: "Avslutt",
        stopRecording: "Stopp opptak",
        tapToSpeak: "Trykk for å snakke",
        typePlaceholder: "Skriv ditt svar...",
      },
    },
    languages: {
      en: "Engelsk",
      zh: "Kinesisk",
      fr: "Fransk",
      de: "Tysk",
      es: "Spansk",
      nb: "Norsk",
      nl: "Nederlandsk",
      pt: "Portugisisk",
      ja: "Japansk",
      ar: "Arabisk",
      flexible: "Fleksibel (Deltaker velger)"
    },
    synthetic: {
      dialogTitle: "Test med syntetisk deltaker",
      dialogSubtitle: "Konfigurer den demografiske profilen til den simulerte deltakeren",
      age: "Alder", gender: "Kjønn", male: "Mann", female: "Kvinne", nonBinary: "Ikke-binær", preferNotToSay: "Foretrekker å ikke si",
      nationality: "Nasjonalitet", ethnicity: "Etnisitet", education: "Utdanning",
      highSchool: "Videregående", bachelors: "Bachelorgrad", masters: "Mastergrad", phd: "Doktorgrad", vocational: "Yrkesfag", other: "Annet",
      occupation: "Yrke", personality: "Personlighetstrekk", additionalContext: "Tilleggskontekst (valgfritt)",
      runTest: "Kjør test", cancel: "Avbryt",
      simulating: "Simulerer deltakersvar...", simulatingHint: "Dette kan ta 15-30 sekunder",
      resultsTitle: "Syntetiske testresultater", syntheticPersona: "Syntetisk deltakerprofil", synthetic: "Syntetisk",
      question: "Spørsmål", type: "Type", answer: "Svar", close: "Lukk",
    },
    status: {
      draft: "Utkast",
      published: "Publisert",
      archived: "Arkivert"
    }
  },
  nl: {
    tutorial: {
      title: "Aan de slag",
      step1Title: "Welkom bij de enquêtebouwer",
      step1Body: "Bouw conversationele enquêtes waarin een AI slimme vervolgvragen stelt en zich aanpast aan elke respondent — veel rijker dan een statisch formulier.",
      step2Title: "Voeg je vragen toe",
      step2Body: "Voeg vragen van elk type toe — meerkeuze, schalen, open tekst — en sleep om ze opnieuw te ordenen. Gebruik pagina-einden en secties om de flow vorm te geven.",
      step3Title: "Configureer de AI",
      step3Body: "Stel de taal, toon en de diepgang in waarmee de AI doorvraagt op open antwoorden. Je kunt ook de hele enquête automatisch vertalen.",
      step4Title: "Delen & analyseren",
      step4Body: "Publiceer en deel je link en bekijk hoe reacties en analyses binnenkomen terwijl deelnemers de enquête voltooien.",
      back: "Terug",
      next: "Volgende",
      done: "Aan de slag",
      skip: "Overslaan",
      help: "Hoe het werkt",
    },
    import: {
      menuLabel: "Bestand importeren", reviewTitle: "Geïmporteerde enquête controleren",
      reviewIntro: "We hebben {{n}} vragen uit je bestand opgesteld. Controleer hieronder en maak daarna de enquête — je kunt alles in de builder bewerken.",
      cancel: "Annuleren", titleField: "Enquêtetitel", descriptionField: "Beschrijving",
      reportTitle: "Importnotities", preview: "Vragen", pageBreak: "Pagina-einde",
      sectionBreak: "Sectie", required: "verplicht", create: "Enquête maken",
      parseFailed: "Kan dit bestand niet importeren.", createFailed: "Kan de enquête niet maken.",
      uploadTitle: "Een enquête importeren", parsing: "Je bestand wordt gelezen…",
      dropHere: "Sleep een bestand hierheen, of klik om te bladeren",
      dropHint: "Tot 25 MB. We maken een concept dat je kunt controleren en bewerken.",
      supported: "Ondersteunde formaten",
      formatNote: "AI zet Word en PDF om in een enquête — het neemt een bestaande vragenlijst over of stelt er een op uit je notities. Qualtrics .qsf en QualiTaTi .qlt worden exact verwerkt.",
      exportDefinition: "Enquête downloaden", exportWord: "Downloaden als Word (.docx)", exportQlt: "Downloaden als QualiTaTi (.qlt)", exportFailed: "Exporteren mislukt.",
    },
    translations: {
      menuLabel: "Vertalingen", title: "Vertalingen van enquête",
      intro: "Voeg een taal toe om de hele enquête automatisch met AI te vertalen en controleer en bewerk daarna elke tekst. Respondenten kiezen hun taal aan het begin; lege velden vallen terug op de standaardtaal ({{lang}}).",
      source: "bron", addLanguage: "Taal toevoegen",
      empty: "Nog geen talen. Klik op «Taal toevoegen» om een AI-vertaling te maken.",
      surveyInfo: "Enquête-info", fieldTitle: "Titel", fieldDescription: "Beschrijving",
      fieldThankYouTitle: "Bedanktitel", fieldThankYouMessage: "Bedankbericht",
      fieldPrompt: "Vraag", options: "Opties", columns: "Schaalkolommen",
      retranslate: "Opnieuw vertalen", close: "Sluiten", save: "Opslaan",
      translated: "Automatische vertaling voltooid. Controleer en bewerk hieronder.", translateFailed: "Vertaling mislukt.",
      saved: "Vertaling opgeslagen.", saveFailed: "Opslaan mislukt.",
      confirmDelete: "Deze taal en alle vertalingen verwijderen?", deleteFailed: "Verwijderen mislukt.",
      confirmRetranslate: "Helemaal opnieuw vertalen? Dit overschrijft je bewerkingen voor deze taal.",
      loadFailed: "Vertalingen laden mislukt.",
    },
    hub: {
      emptyTitle: "Maak je eerste enquête",
      emptyBody: "Begin vanaf nul, genereer er een met AI, kies een sjabloon of importeer een Word-, PDF- of Qualtrics-bestand.",
      emptyNoResults: "Geen enquêtes voldoen aan je filters.",
      clearFilters: "Filters wissen",
      title: "Enquêtes",
      createProject: "Enquêteproject aanmaken",
      manageSurveys: "Enquêtes beheren",
      createDialogTitle: "Nieuw enquêteproject",
      projectName: "Projectnaam",
      projectOutline: "Projectbeschrijving",
      interviewType: "Interviewtype",
      interviewLanguage: "Interviewtaal",
      cancel: "Annuleren",
      save: "Opslaan",
      createSuccess: "Project aangemaakt",
      createError: "Aanmaken mislukt",
      fillRequired: "Vul de verplichte velden in",
      loadError: "Kan projecten niet laden",
      confirmDeleteProject: "Wilt u dit surveyproject en alle bijbehorende surveys verwijderen? Dit kan niet ongedaan worden gemaakt.",
      projectDeleted: "Project succesvol verwijderd",
      surveysInProject: "Surveys in dit project",
      noSurveysYet: "Nog geen surveys",
      manageProjectSurveys: "Beheer surveys",
      noProjectsYet: "Nog geen surveyprojecten aangemaakt",
      experimentalTitle: "Experimentele functie",
      experimentalMessage: "De Conversationele Enquête is een experimenteel hulpmiddel dat actief wordt ontwikkeld. Sommige functies kunnen veranderen of zich onverwacht gedragen. Uw feedback helpt ons verbeteren!",
      search: "Enquêtes zoeken",
      filterAll: "Alle",
      filterDraft: "Concept",
      filterPublished: "Gepubliceerd",
      sortNewest: "Nieuwste eerst",
      sortAlpha: "Alfabetisch",
      noResults: "Geen enquêtes komen overeen met uw zoekopdracht",
      analytics: "Analyse",
      duplicate: "Dupliceren",
      duplicateSuccess: "Enquête gedupliceerd",
    },
    list: {
      emptyTitle: "Nog geen enquêtes in dit project",
      emptyBody: "Maak je eerste enquête om antwoorden te verzamelen.",
      fromTemplate: "Beginnen met een sjabloon",
      fromWizard: "Genereren met AI",
      fromTemplateTitle: "Beginnen met een sjabloon",
      fromTemplateSubtitle: "Kies een sjabloon en krijg een bewerkbaar enquêteconcept. We vullen vragen, AI-vervolgvragen en een passende standaardmodus alvast in.",
      templatesEmpty: "Er zijn nu geen sjablonen beschikbaar.",
      creatingFromTemplate: "Aanmaken…",
      wizard: {
        title: "Een enquête genereren met AI",
        subtitle: "Vertel Mimi wat je wilt leren en ze stelt een bewerkbare enquête op.",
        generating: "Mimi stelt je enquête op…",
        generatingHint: "Duurt meestal 20-40 seconden. We genereren een set vragen op maat met passende AI-vervolgvragen.",
        goalLabel: "Wat wil je leren?",
        goalPlaceholder: "bv. Waarom klanten in de eerste maand vertrekken — wat de beslissing drijft en wat dat had kunnen veranderen.",
        goalHelp: "Specifieke doelen leveren betere enquêtes op. Mik op 1-3 zinnen.",
        audienceLabel: "Wie gaat deze enquête invullen?",
        audiencePlaceholder: "bv. Klanten die de afgelopen 30 dagen hebben opgezegd",
        languageLabel: "Taal",
        depthLabel: "Diepte",
        nQuestionsLabel: "Beoogd aantal vragen",
        generate: "Genereren",
        errorGoalTooShort: "Doel moet minstens 10 tekens zijn zodat de AI er iets mee kan.",
        errorAudienceTooShort: "Vertel ons wie de doelgroep is, dan kan de AI de vragen aanpassen.",
      },
      title: "Enquête",
      create: "Nieuwe survey",
      createDialogTitle: "Nieuwe survey",
      titleField: "Titel",
      descriptionField: "Beschrijving",
      cancel: "Annuleren",
      save: "Opslaan",
      builder: "Builder",
      export: "Exporteren",
      publish: "Publiceren",
      status: "Status",
      linkCopied: "Link gekopieerd",
      publishFirst: "Publiceer de survey eerst",
      publishReady: "Gepubliceerd",
      publishError: "Publiceren mislukt",
      createSuccess: "Survey aangemaakt",
      createError: "Aanmaken mislukt",
      exportReady: "CSV klaar",
      exportError: "Export mislukt",
      error: "Laden mislukt",
      copyLink: "Kopieer deellink",
      downloadCsv: "Download CSV",
      deleteSurvey: "Survey verwijderen",
      publishGetLink: "Publiceren en link ophalen",
      published: "Enquête gepubliceerd — je deellink is klaar.",
      questionsLabel: "vragen",
      responsesLabel: "reacties"
    },
    builder: {
      minDwellLabel: "Minimale weergavetijd (seconden)",
      minDwellHelp: "Respondenten moeten zo lang op deze pagina blijven voordat Volgende/Verzenden ontgrendelt. 0 = uit.",
      saving: "Opslaan…",
      saved: "Opgeslagen",
      saveFailed: "Kan wijzigingen niet opslaan",
      aiFollowUp: {
        label: "AI-vervolgvraag",
        helperNone: "Geen vervolgvraag — respondenten antwoorden en gaan verder",
        helperLight: "Light — één conversationele vervolgvraag om het antwoord te verrijken",
        helperDeep: "Deep — tot drie vervolgvragen voor diepgaand kwalitatief inzicht",
        optionNone: "Geen — geen vervolgvraag",
        optionLight: "Light — 1 vervolgvraag",
        optionDeep: "Deep — tot 3 vervolgvragen",
      },
      cancel: "Annuleren",
      surveyMode: {
        label: "Enquête-modus",
        form: {
          title: "Traditionele enquête",
          subtitle: "Standaard formuliergebaseerde enquête, zonder AI",
        },
        aiFollowUp: {
          title: "Enquête met AI-vervolgvragen",
          subtitle: "Formulier + AI-vervolgvragen op open antwoorden",
          hint: "Tip: stel \"AI-vervolgvraag\"-intensiteit in op elke open vraag hieronder. Light = 1 vervolg; Deep = tot 3.",
        },
        aiInterview: {
          title: "Conversationele enquête",
          subtitle: "AI-gestuurd conversationeel interview, vrije dialoog",
        },
      },
      preflight: {
        title: "Controle voor publicatie",
        loading: "Je enquête wordt gecontroleerd…",
        ready: "Klaar om te publiceren",
        readyHint: "Je enquête heeft alle controles doorstaan. Respondenten zien een schone ervaring.",
        hasErrors: "Los de onderstaande punten op voor je publiceert:",
        warningsOnly: "Je kunt publiceren, maar controleer eerst even het volgende:",
        fallback: "Kon de gezondheidscontrole niet uitvoeren — publiceren is toch toegestaan.",
        rerun: "Opnieuw controleren",
      },
      pipingChip: 'Voeg een eerder antwoord in',
      pipingTooltipTitle: 'Toon een eerder antwoord in deze vraag',
      pipingTooltipBody: 'Klik op een tag om deze te kopiëren en plak hem in je vraagtekst. Wanneer de respondent dit ziet, wordt het zijn eerdere antwoord.',
      constantSumItems: 'Items',
      constantSumItemsHelp: 'Eén item per regel',
      constantSumTotal: 'Totaal',
      constantSumTotalHelp: 'Som die respondenten moeten verdelen (bijv. 100)',
      emailAutoConfig: 'Wordt gevalideerd als e-mailadres',
      fileAcceptHelp: 'Toegestane bestandstypen (komma-gescheiden, bijv. .pdf,.docx)',
      fileAcceptTypes: 'Geaccepteerde types',
      fileMaxSize: 'Max. bestandsgrootte (MB)',
      imageChoiceHelp: 'Eén afbeeldings-URL per regel. Optionele labels komen op volgorde overeen.',
      imageChoiceLabels: 'Labels',
      imageChoiceMultiple: 'Meerdere selecties toestaan',
      imageChoiceUrls: 'Afbeeldings-URL\'s',
      phoneAutoConfig: 'Wordt gevalideerd als telefoonnummer',
      ratingMax: 'Maximale beoordeling',
      sectionBreakInfo: 'Alleen visueel scheidingsteken — geen antwoord verzameld.',
      thankYouPage: 'Bedankpagina',
      thankYouRedirectHelp: 'Optionele URL om naartoe te sturen na verzending',
      yesNoAutoConfig: 'Ja / Nee-keuze',
      loadError: "Kan survey niet laden",
      saveMeta: "Survey bijgewerkt",
      metaSaved: "Survey details opgeslagen",
      saveError: "Opslaan mislukt",
      addError: "Toevoegen mislukt",
      questionAdded: "Vraag toegevoegd",
      saveQuestion: "Vraag opgeslagen",
      questionSaved: "Vraag opgeslagen",
      questionDeleted: "Vraag verwijderd",
      deleteError: "Verwijderen mislukt",
      reorderError: "Herschikking mislukt",
      confirmDelete: "Deze vraag verwijderen?",
      deleteSurvey: "Survey verwijderen",
      confirmDeleteSurvey: "Weet u zeker dat u deze survey wilt verwijderen? Dit kan niet ongedaan worden gemaakt.",
      surveyDeleted: "Survey succesvol verwijderd",
      surveyFlow: "Enquêtestroom",
      addBlock: "Blok toevoegen",
      addRandomizer: "Randomizer toevoegen",
      blockPlaceholder: "Naamloos blok",
      deleteBlock: "Blok verwijderen",
      confirmDeleteBlock: "Dit blok verwijderen? De vragen worden niet-toegewezen.",
      randomizer: "Randomizer",
      randomizerShow: "Toon",
      randomizerOf: "van",
      addVariant: "Variant toevoegen",
      questionsCount: "{n} vraag/vragen",
      unassigned: "Niet-toegewezen vragen",
      noBlocks: "Nog geen blokken",
      renameBlock: "Hernoemen",
      addQuestion: "Toevoegen",
      publishCopied: "Gepubliceerd en link gekopieerd",
      publishSuccess: "Survey gepubliceerd",
      publishError: "Publiceren mislukt",
      linkCopied: "Link gekopieerd",
      copyLink: "Kopieer link",
      questions: "Vragen",
      noQuestions: "Nog geen vragen",
      clickToAdd: "Klik om uw eerste vraag toe te voegen",
      addFirstQuestion: "Vraag toevoegen",
      untitledQuestion: "(Ongetitelde vraag)",
      promptPlaceholder: "Voer uw vraag in...",
      surveyTitle: "Survey titel",
      surveyDescription: "Survey beschrijving",
      noDescription: "Geen beschrijving",
      questionType: "Vraagtype",
      variableName: "Variabelenaam",
      variableHelp: "snake_case, uniek",
      requiredQuestion: "Verplichte vraag",
      isConversational: "Vraag via gesprek",
      askForm: "Vraag via formulier",
      optionsPlaceholder: "Optie 1\nOptie 2\nOptie 3",
      minValue: "Minimumwaarde",
      maxValue: "Maximumwaarde",
      saveChanges: "Wijzigingen opslaan",
      moreOptions: "meer opties",
      moveUp: "Omhoog",
      moveDown: "Omlaag",
      moveToBlock: "Verplaats naar blok",
      expand: "Uitvouwen",
      collapse: "Inklappen",
      delete: "Verwijderen",
      duplicate: "Dupliceren",
      duplicateSuccess: "Vraag gedupliceerd",
      republish: "Opnieuw publiceren",
      unpublish: "Publicatie ongedaan maken",
      unpublishing: "Bezig met ongedaan maken…",
      unpublishSuccess: "Survey gedepubliceerd. Het is nu een concept.",
      unpublishError: "Publicatie ongedaan maken mislukt",
      unpublishTooltip: "Nieuwe deelnemers blokkeren. Bestaande antwoorden blijven behouden; de deellink blijft bewaard voor opnieuw publiceren.",
      confirmUnpublish: "Publicatie van deze survey ongedaan maken? Nieuwe deelnemers worden geblokkeerd. Bestaande antwoorden blijven behouden. U kunt later opnieuw publiceren met dezelfde link.",
      meta: "Survey info",
      titleField: "Titel",
      descriptionField: "Beschrijving",
      descriptionPlaceholder: "Beschrijf het doel van deze survey...",
      language: "Taal",
      save: "Opslaan",
      imageUrl: "Afbeeldings-URL",
      imageUrlHelp: "Optioneel: Voer een URL in om een afbeelding weer te geven",
      imagePreview: "Voorbeeld",
      imageLoadError: "Kan afbeelding niet laden. Controleer de URL.",
      addImage: "Afbeelding toevoegen",
      questionImage: "Vraagafbeelding",
      removeImage: "Afbeelding verwijderen",
      uploadImage: "Afbeelding uploaden",
      uploading: "Uploaden…",
      imageUrlPlaceholder: "Of plak afbeeldings-URL",
      imageHelp: "PNG, JPEG, GIF of WebP. Max 2 MB.",
      imageUploadFailed: "Upload mislukt.",
      uploadImages: "Afbeeldingen uploaden",
      addMoreImages: "Meer toevoegen",
      urlPasteHint: "Druk op Enter om toe te voegen",
      removeAllImages: "Alles verwijderen",
      itemsToRank: "Items om te rangschikken",
      itemsToRankHelp: "Voer items in om te rangschikken, één per regel",
      bipolarLabels: "Schaallabels",
      leftLabel: "Linkerlabel",
      rightLabel: "Rechterlabel",
      surveyLanguage: "Survey taal",
      surveyLanguageHelp: "Selecteer 'Flexibel' om deelnemers hun taal te laten kiezen",
      flexible: "Flexibel (Deelnemer kiest)",
      publish: "Publiceer & Kopieer link",
      editor: "Vragen-editor",
      selectPrompt: "Selecteer een vraag om te bewerken",
      prompt: "Vraagtekst",
      variable: "Variabelenaam",
      required: "Verplicht",
      options: "Opties (één per regel)",
      optionsHelp: "Voer één optie per regel in",
      min: "Min",
      max: "Max",
      saveQuestionBtn: "Sla vraag op",
      types: {
        image: "Afbeelding",
        short_text: "Kort antwoord",
        long_text: "Alinea",
        single_choice: "Meerkeuze",
        multi_choice: "Selectievakjes",
        number: "Getal",
        date: "Datum",
        ranking: "Rangschikking",
        bipolar_scale: "Bipolaire schaal",
        info_text: "Informatietekst",
        slider: "Schuifregelaar",
        dropdown: "Uitklapmenu",
        nps: "Net Promoter Score",
        matrix: "Matrix",
        ai_interview: "AI-interview",
        rating_scale: "Beoordelingsschaal",
        yes_no: "Ja / Nee",
        email: "E-mail",
        phone: "Telefoonnummer",
        image_choice: "Afbeeldingskeuze",
        file_upload: "Bestand uploaden",
        constant_sum: "Constante som",
        section_break: "Sectieonderbreking",
        page_break: "Pagina-einde"
      },
      typeDescriptions: {
        short_text: "Kort tekstantwoord",
        long_text: "Lang tekstantwoord",
        single_choice: "Kies één optie",
        multi_choice: "Selecteer meerdere opties",
        number: "Numerieke waarde",
        date: "Datumkiezer",
        ranking: "Items rangschikken",
        bipolar_scale: "Schaal tussen twee tegenpolen",
        info_text: "Toon tekst of afbeeldingen (geen antwoord vereist)",
        slider: "Schuif om een waarde in een bereik te selecteren",
        dropdown: "Selecteer een optie uit een uitklapmenu",
        nps: "Beoordeel van 0 tot 10 (Net Promoter Score)",
        matrix: "Beoordeel meerdere items op dezelfde schaal",
        ai_interview: "Een AI-gestuurd interviewgesprek insluiten",
        rating_scale: "Sterrenbeoordeling (bijv. 1-5 sterren)",
        yes_no: "Eenvoudige ja of nee vraag",
        email: "E-mailadres met validatie",
        phone: "Telefoonnummer met validatie",
        image_choice: "Selecteer uit afbeeldingsopties",
        file_upload: "Upload een bestandsbijlage",
        constant_sum: "Verdeel punten over items",
        section_break: "Visuele scheiding tussen secties",
        page_break: "Begint een nieuwe pagina voor respondenten"
      },
      stepValue: "Stap",
      npsAutoConfig: "NPS wordt automatisch geconfigureerd als een 0-10 schaal. Geen extra configuratie nodig.",
      matrixRows: "Rijlabels (een per regel)",
      matrixRowsHelp: "Voer de te beoordelen items in, een per regel",
      matrixColumns: "Kolomlabels (een per regel)",
      matrixColumnsHelp: "Voer de schaalopties in, een per regel",
      interviewConfig: {
        title: "AI-interviewinstellingen",
        interactionMode: "Interactiemodus",
        voiceToVoice: "Spraak naar spraak",
        voiceToText: "Spraak naar tekst",
        textToText: "Tekst naar tekst",
        textToVoice: "Tekst naar spraak",
        language: "Interviewtaal",
        maxDuration: "Maximale duur (minuten)",
        outline: "Interviewschets / Instructies",
        outlinePlaceholder: "Beschrijf wat de AI moet vragen...",
        outlineHelp: "De AI-interviewer volgt deze schets om het gesprek te leiden.",
      },
      defaultPrompt: "Nieuwe vraag",
      metaSaved: "Survey details opgeslagen",
      questionSaved: "Vraag opgeslagen",
      confirmDelete: "Deze vraag verwijderen?",
      publishSuccess: "Survey gepubliceerd",
      noQuestions: "Nog geen vragen",
      clickToAdd: "Klik om uw eerste vraag toe te voegen",
      addFirstQuestion: "Vraag toevoegen",
      untitledQuestion: "(Ongetitelde vraag)",
      promptPlaceholder: "Voer uw vraag in...",
      surveyTitle: "Survey titel",
      surveyDescription: "Survey beschrijving",
      noDescription: "Geen beschrijving",
      questionType: "Vraagtype",
      variableName: "Variabelenaam",
      requiredQuestion: "Verplichte vraag",
      optionsPlaceholder: "Optie 1\nOptie 2\nOptie 3",
      minValue: "Minimumwaarde",
      maxValue: "Maximumwaarde",
      saveChanges: "Wijzigingen opslaan",
      moreOptions: "meer opties",
      moveUp: "Omhoog",
      moveDown: "Omlaag",
      moveToBlock: "Verplaats naar blok",
      expand: "Uitvouwen",
      collapse: "Inklappen",
      delete: "Verwijderen",
      duplicate: "Dupliceren",
      duplicateSuccess: "Vraag gedupliceerd",
      republish: "Opnieuw publiceren",
      unpublish: "Publicatie ongedaan maken",
      unpublishing: "Bezig met ongedaan maken…",
      unpublishSuccess: "Survey gedepubliceerd. Het is nu een concept.",
      unpublishError: "Publicatie ongedaan maken mislukt",
      unpublishTooltip: "Nieuwe deelnemers blokkeren. Bestaande antwoorden blijven behouden; de deellink blijft bewaard voor opnieuw publiceren.",
      confirmUnpublish: "Publicatie van deze survey ongedaan maken? Nieuwe deelnemers worden geblokkeerd. Bestaande antwoorden blijven behouden. U kunt later opnieuw publiceren met dezelfde link.",
      preview: "Voorbeeld",
      previewMode: "Voorbeeldmodus",
      conditions: "Voorwaarden",
      addCondition: "Voorwaarde toevoegen",
      removeCondition: "Verwijderen",
      conditionLogicAnd: "EN (alles moet overeenkomen)",
      conditionLogicOr: "OF (minstens één moet overeenkomen)",
      conditionQuestion: "Als vraag",
      conditionOperator: "Operator",
      conditionValue: "Waarde",
      operatorEquals: "is gelijk aan",
      operatorNotEquals: "is niet gelijk aan",
      operatorContains: "bevat",
      operatorGreaterThan: "is groter dan",
      operatorLessThan: "is kleiner dan",
      operatorIsEmpty: "is leeg",
      operatorIsNotEmpty: "is niet leeg",
      pipingHelp: "Tip: Gebruik ${variable_name} om een eerder antwoord in te voegen",
      availableVariables: "Beschikbaar: {{variables}}",
      // Share & Distribution (Phase 6)
      share: "Delen",
      shareTitle: "Uw enquête delen",
      tabLink: "Link / QR",
      tabEmbed: "Insluiten",
      tabSocial: "Sociale media",
      tabProtect: "Beschermen",
      copy: "Kopiëren",
      copied: "Gekopieerd",
      linkCopied: "Link gekopieerd",
      qrHint: "Scan met een telefoonkamera om de enquête te openen",
      downloadQR: "QR-code downloaden (PNG)",
      embedHelp: "Plak deze HTML op uw website om de enquête in te sluiten.",
      copyEmbed: "Code insluiten kopiëren",
      embedCopied: "Insluitcode gekopieerd",
      preview: "Voorbeeld",
      socialHelp: "Deel uw enquête op sociale platforms:",
      shareOn: "Delen op",
      passwordProtection: "Wachtwoordbeveiliging",
      passwordHint: "Vereisen dat respondenten een wachtwoord invoeren voordat ze beginnen.",
      passwordActive: "Een wachtwoord is ingesteld. Voer hieronder een nieuw in om het te wijzigen, of laat het leeg om te verwijderen.",
      passwordPlaceholder: "Laat leeg voor geen wachtwoord",
      passwordSaved: "Wachtwoord bijgewerkt",
      passwordRemoved: "Wachtwoord verwijderd",
      removePassword: "Wachtwoord verwijderen",
      apply: "Toepassen",
      schedule: "Schema",
      scheduleHelp: "Enquête automatisch op specifieke tijden openen en sluiten. Laat leeg voor altijd geopend.",
      openAt: "Opent op",
      closeAt: "Sluit op",
      saveSchedule: "Schema opslaan",
      scheduleSaved: "Schema opgeslagen",
      saveError: "Opslaan mislukt",
      // Theming (Phase 5)
      themeBranding: "Thema & Branding",
      themeBrandingHelp: "Pas het uiterlijk van de enquêtepagina aan die respondenten zien.",
      themePresets: "Snelle voorinstellingen",
      primaryColor: "Primaire kleur",
      backgroundColor: "Achtergrond",
      textColor: "Tekstkleur",
      fontFamily: "Letterstijl",
      fontSystem: "Systeemstandaard (modern)",
      fontSerif: "Serif (klassiek)",
      fontRounded: "Afgerond (vriendelijk)",
      fontMono: "Monospace (technisch)",
      logoUrl: "Logo-afbeeldings-URL",
      logoUrlHelp: "Weergegeven boven de enquêtetitel. Optioneel.",
      headerImageUrl: "URL van headerbanner",
      headerImageUrlHelp: "Brede bannerafbeelding aan de bovenkant. Optioneel.",
      themePreview: "Voorbeeld",
      sampleSurveyTitle: "Uw enquêtetitel",
      sampleQuestionText: "Dit is hoe een vraag eruitziet.",
      sampleSubmit: "Verzenden",
      resetTheme: "Naar standaard herstellen",
      randomizeQuestions: "Willekeurige vraagvolgorde",
      randomizeOptions: "Willekeurige optievolgorde",
      thankYouSettings: "Bedankpagina",
      thankYouTitle: "Titel",
      thankYouMessage: "Bericht",
      thankYouRedirect: "Redirect URL (optioneel)",
      maxResponses: "Max reacties (optioneel)",
      maxResponsesHelp: "Laat leeg voor onbeperkt",
      deliveryMode: "Afleveringsmodus",
      deliveryConversational: "Conversatie (AI-interview)",
      deliveryForm: "Standaardformulier",
      deliveryModeHelp: "Conversatiemodus gebruikt AI om interviews af te nemen. Standaardformulier toont alle vragen tegelijk.",
      testWithSynthetic: "Test met synthetisch mens",
      fromLibrary: "Uit bibliotheek",
      fromLibraryHint: "Kopieer uit kant-en-klare sjablonen en bundels",
      templateLibrary: "Sjabloonbibliotheek",
      templates: "Sjablonen",
      bundles: "Bundels",
      allTemplates: "Alle sjablonen",
      myTemplates: "Mijn sjablonen",
      allCategories: "Alle categorieën",
      selectTemplate: "Selecteer een sjabloon",
      templateName: "Sjabloonnaam",
      category: "Categorie",
      search: "Zoeken",
      preview: "Voorbeeld",
      inserting: "Invoegen...",
      insertTemplate: "Sjabloon invoegen",
      insertBundle: "Bundel invoegen",
      noTemplates: "Geen sjablonen gevonden",
      noBundles: "Geen bundels gevonden",
      templateInsertedSuccess: "Sjabloon ingevoegd",
      bundleInsertedSuccess: "Bundel ingevoegd",
      templateInsertError: "Sjabloon invoegen mislukt",
      cancel: "Annuleren",
      skipLogic: "Overslaan logica",
      skipLogicHelp: "Toon deze vraag alleen wanneer aan de voorwaarden is voldaan",
      displayLogic: "Weergavelogica",
      displayLogicHelp: "Toon deze vraag alleen wanneer eerdere antwoorden aan deze voorwaarden voldoen",
      conditionIf: "Als",
      logicTab: "Logica",
      noLogicYet: "Geen overslaanlogica geconfigureerd",
      noLogicHint: "Klik op een vraag en scroll naar het gedeelte Overslaan logica om voorwaarden toe te voegen.",
      logicSummaryPrefix: "Toon deze vraag wanneer",
      settings: "Enquête-instellingen"
    },
    export: {
      loadError: "Kan survey niet laden",
      description: "Download ingediende antwoorden als CSV.",
      button: "Download CSV",
      success: "Export klaar",
      error: "Export mislukt",
      xlsx: "Download XLSX"
    },
    analytics: {
      filterScopeNote: "Dit datumfilter beperkt alleen de antwoordlijst en kruistabellen. De overzichtskaarten, grafieken en AI-samenvatting tonen altijd alle antwoorden.",
      clearFilter: 'Filter wissen',
      columnQuestion: 'Kolomvraag',
      crossTab: 'Kruistabel',
      crossTabHelp: 'Vergelijk hoe antwoorden op één vraag zich verhouden tot een andere.',
      crossTabNeedTwo: 'Kies twee vragen om te vergelijken.',
      crossTabPickTwo: 'Selecteer een rij- en kolomvraag',
      crossTabTitle: 'Kruistabulatie',
      duration: 'Duur',
      filterBy: 'Filteren op',
      from: 'Van',
      individualResponses: 'Individuele antwoorden',
      language: 'Taal',
      next: 'Volgende',
      noAnswer: 'Geen antwoord',
      noResponsesYet: 'Nog geen antwoorden',
      deleteResponse: 'Antwoord verwijderen',
      confirmDeleteResponse: 'Dit antwoord permanent verwijderen? Dit kan niet ongedaan worden gemaakt.',
      responseDeleted: 'Antwoord verwijderd.',
      deleteFailed: 'Antwoord verwijderen mislukt.',
      download: 'Downloaden',
      downloadCsv: 'CSV downloaden',
      downloadXlsx: 'Excel (XLSX) downloaden',
      exportFailed: 'Exporteren mislukt.',
      prev: 'Vorige',
      respondent: 'Respondent',
      rowQuestion: 'Rijvraag',
      sampleResponses: 'Voorbeeldantwoorden',
      submittedAt: 'Ingediend op',
      to: 'Tot',
      viewResponses: 'Antwoorden bekijken',
      wordCloud: 'Woordwolk',
      title: "Survey Analyse",
      totalResponses: "Totaal aantal reacties",
      completionRate: "Voltooiingspercentage",
      avgDuration: "Gem. duur",
      minutes: "minuten",
      responsesOverTime: "Reacties in de tijd",
      questionBreakdown: "Vraaganalyse",
      responses: "reacties",
      noData: "Nog geen reacties",
      refresh: "Vernieuwen",
      detractors: "Criticasters (0-6)",
      passives: "Passieven (7-8)",
      promoters: "Promotors (9-10)"
    },
    start: {
      untitledSurvey: "Naamloze enquête",
      timeEstimate: "Ongeveer {{minutes}} {{unit}}",
      questionCount: "{{count}} {{unit}}",
      minute: "minuut",
      minutes: "minuten",
      question: "vraag",
      questions: "vragen",
      privacyNote: "Je antwoorden blijven vertrouwelijk",
      startButton: "Enquête starten",
      selectLanguageFirst: "Kies een taal om door te gaan.",
      title: "Start survey",
      subtitle: "Start de conversatie survey.",
      button: "Start",
      loading: "Survey starten...",
      error: "Sessiestart mislukt",
      selectLanguage: "Selecteer uw taal",
      selectLanguageSubtitle: "Kies de taal voor uw survey sessie",
      surveyFull: "Deze enquête heeft de maximale responslimiet bereikt"
    },
    session: {
      dwellWait: "Je kunt over {{n}}s verder",
      redirectingIn: "Doorsturen over {seconds}…",
      continueNow: "Nu doorgaan",
      cancelRedirect: "Annuleren",
      redirectCancelled: "Automatische doorverwijzing geannuleerd.",
      continueToSite: "Doorgaan naar de site",
      loadError: "Kan sessie niet laden",
      connected: "Verbonden",
      wsError: "Chatverbinding mislukt",
      answersSaved: "Antwoorden opgeslagen",
      saveError: "Opslaan mislukt",
      submitted: "Ingediend",
      submitError: "Indienen mislukt",
      progress: "Voortgang: {{answered}} / {{total}} verplicht",
      placeholder: "Typ je antwoord",
      send: "Versturen",
      verification: "Verificatie",
      required: "Verplicht",
      saveEdits: "Wijzigingen opslaan",
      submit: "Bevestigen & indienen",
      loading: "Laden...",
      processing: "Je antwoord wordt verwerkt...",
      stopRecording: "Opname stoppen",
      pressToSpeak: "Druk om te spreken",
      reviewSubmit: "Controleren & indienen",
      formMode: "Standaardformulier",
      conversationalMode: "AI-interview",
      voicePlayback: "Stem",
      switchToForm: "Naar formulier",
      switchToConversational: "Naar AI-interview",
      answerMore: "Beantwoord nog {{count}} verplichte vraag/vraagstukken",
      transcribeFailed: "Audio kon niet worden getranscribeerd. Probeer het opnieuw.",
      audioProcessError: "Audioprocessing mislukt: {{error}}",
      micError: "Geen toegang tot microfoon: {{error}}",
      rankingPlaceholder: "Vul rangorde in (bijv. A > B > C)",
      thankYouTitle: "Bedankt!",
      thankYouMessage: "Uw antwoorden zijn succesvol ingediend. We waarderen uw tijd en feedback.",
      returnHome: "Terug naar home",
      status_in_progress: "Bezig",
      pageOf: "Pagina {current} van {total}",
      nextPage: "Volgende",
      previousPage: "Terug",
      status_submitted: "Ingediend",
      status_awaiting_verification: "In beoordeling",
      previewBanner: "[VOORBEELDMODUS] Dit is een voorbeeld. Antwoorden worden niet opgeslagen in de analyses.",
      redirecting: "Doorsturen...",
      progressLabel: "Voortgang",
      autoSaving: "Opslaan…",
      autoSaved: "Opgeslagen",
      saveAndContinue: "Opslaan en later doorgaan",
      saveAndContinueTooltip: "Sla je voortgang op en krijg een link om later verder te gaan",
      saveAndContinueTitle: "Je voortgang opslaan",
      saveAndContinueBody: "Je antwoorden zijn opgeslagen. Maak een bladwijzer van deze link of mail hem naar jezelf om verder te gaan waar je gebleven was — vanaf elk apparaat.",
      copyLink: "Link kopiëren",
      linkCopied: "Link gekopieerd naar klembord",
      close: "Sluiten",
      alreadySubmittedWarning: "Uit onze gegevens blijkt dat je deze enquête mogelijk al hebt ingediend. Je kunt doorgaan, maar dubbele antwoorden kunnen worden uitgesloten.",
      connectionError: "Verbindingsfout",
      reconnecting: "Opnieuw verbinden met assistent...",
      connectionLost: "Verbinding verbroken. Vernieuw de pagina.",
      typeHere: "Typ hier uw antwoord...",
      send: "Verzenden",
      requiredField: "verplicht",
      fieldRequired: "{label} is verplicht",
      invalidEmail: "Voer een geldig e-mailadres in",
      invalidPhone: "Voer een geldig telefoonnummer in",
      invalidNumber: "Voer een geldig getal in",
      sumMustEqual: "De waarden moeten optellen tot {total} (huidig: {sum})",
      npsNotLikely: "Helemaal niet waarschijnlijk",
      npsExtremely: "Uiterst waarschijnlijk",
      chooseFile: "Bestand kiezen",
      chooseFileDrag: "Kies een bestand of sleep het hierheen",
      fileSelected: "Bestand geselecteerd",
      selectedPrefix: "Geselecteerd:",
      acceptedPrefix: "Geaccepteerd:",
      fileTooLarge: "De bestandsgrootte moet kleiner zijn dan {size} MB",
      constantSumInstruction: "Verdeel in totaal {total} punten over de onderstaande items.",
      remaining: "Resterend:",
      pageBreakLabel: "Pagina-einde",
      noFormConfigured: "Geen formulier geconfigureerd",
      noFormConfiguredBody: "De projecteigenaar heeft nog geen aangepast formulier ingesteld.",
      unsupportedField: "Niet-ondersteund veldtype: {type}",
      imageNotConfigured: "Afbeeldings-URL niet geconfigureerd",
      videoNotConfigured: "Video-URL niet geconfigureerd",
      dragToReorder: "Sleep om volgorde te wijzigen",
      moveUp: "Omhoog verplaatsen",
      moveDown: "Omlaag verplaatsen",
      requiredSummary: "Beantwoord de gemarkeerde vraag/vragen voordat u verdergaat.",
      yes: "Ja",
      no: "Nee",
      optionN: "Optie {n}",
      surveyImageAlt: "Enquête-informatie",
      interview: {
        title: "AI-interview",
        description: "Deze vraag bevat een kort AI-interview.",
        start: "Start interview",
        starting: "Interview starten...",
        ending: "Interview opslaan...",
        completed: "Interview voltooid",
        end: "Beëindigen",
        stopRecording: "Stop opname",
        tapToSpeak: "Tik om te spreken",
        typePlaceholder: "Typ uw antwoord...",
      },
    },
    languages: {
      en: "Engels",
      zh: "Chinees",
      fr: "Frans",
      de: "Duits",
      es: "Spaans",
      nb: "Noors",
      nl: "Nederlands",
      pt: "Portugees",
      ja: "Japans",
      ar: "Arabisch",
      flexible: "Flexibel (Deelnemer kiest)"
    },
    synthetic: {
      dialogTitle: "Test met synthetische deelnemer",
      dialogSubtitle: "Configureer het demografische profiel van uw gesimuleerde deelnemer",
      age: "Leeftijd", gender: "Geslacht", male: "Man", female: "Vrouw", nonBinary: "Non-binair", preferNotToSay: "Zeg ik liever niet",
      nationality: "Nationaliteit", ethnicity: "Etniciteit", education: "Opleiding",
      highSchool: "Middelbare school", bachelors: "Bachelor", masters: "Master", phd: "Doctoraat", vocational: "Beroepsopleiding", other: "Anders",
      occupation: "Beroep", personality: "Persoonlijkheidskenmerken", additionalContext: "Extra context (optioneel)",
      runTest: "Test uitvoeren", cancel: "Annuleren",
      simulating: "Deelnemersantwoorden simuleren...", simulatingHint: "Dit kan 15-30 seconden duren",
      resultsTitle: "Synthetische testresultaten", syntheticPersona: "Synthetisch deelnemersprofiel", synthetic: "Synthetisch",
      question: "Vraag", type: "Type", answer: "Antwoord", close: "Sluiten",
    },
    status: {
      draft: "Concept",
      published: "Gepubliceerd",
      archived: "Gearchiveerd"
    }
  },
  de: {
    tutorial: {
      title: "Erste Schritte",
      step1Title: "Willkommen im Umfrage-Builder",
      step1Body: "Erstelle dialogbasierte Umfragen, bei denen eine KI clevere Nachfragen stellt und sich an jede befragte Person anpasst — weit reichhaltiger als ein statisches Formular.",
      step2Title: "Füge deine Fragen hinzu",
      step2Body: "Füge Fragen jeder Art hinzu — Multiple Choice, Skalen, Freitext — und ordne sie per Drag-and-drop neu an. Nutze Seitenumbrüche und Abschnitte, um den Ablauf zu gestalten.",
      step3Title: "Konfiguriere die KI",
      step3Body: "Lege Sprache, Tonfall und fest, wie tief die KI mit Nachfragen auf offene Antworten eingeht. Du kannst auch die gesamte Umfrage automatisch übersetzen.",
      step4Title: "Teilen & analysieren",
      step4Body: "Veröffentliche und teile deinen Link und beobachte, wie Antworten und Auswertungen eintreffen, während die Teilnehmenden die Umfrage abschließen.",
      back: "Zurück",
      next: "Weiter",
      done: "Loslegen",
      skip: "Überspringen",
      help: "So funktioniert es",
    },
    import: {
      menuLabel: "Datei importieren", reviewTitle: "Importierte Umfrage prüfen",
      reviewIntro: "Wir haben {{n}} Fragen aus Ihrer Datei erstellt. Prüfen Sie unten und erstellen Sie dann die Umfrage — im Builder können Sie alles bearbeiten.",
      cancel: "Abbrechen", titleField: "Umfragetitel", descriptionField: "Beschreibung",
      reportTitle: "Importhinweise", preview: "Fragen", pageBreak: "Seitenumbruch",
      sectionBreak: "Abschnitt", required: "erforderlich", create: "Umfrage erstellen",
      parseFailed: "Diese Datei konnte nicht importiert werden.", createFailed: "Die Umfrage konnte nicht erstellt werden.",
      uploadTitle: "Umfrage importieren", parsing: "Datei wird gelesen…",
      dropHere: "Datei hierher ziehen oder zum Durchsuchen klicken",
      dropHint: "Bis 25 MB. Wir erstellen einen Entwurf, den Sie prüfen und bearbeiten können.",
      supported: "Unterstützte Formate",
      formatNote: "Die KI macht aus Word und PDF eine Umfrage — sie übernimmt einen vorhandenen Fragebogen oder erstellt einen Entwurf aus Ihren Notizen. Qualtrics .qsf und QualiTaTi .qlt werden exakt eingelesen.",
      exportDefinition: "Umfrage herunterladen", exportWord: "Als Word (.docx) herunterladen", exportQlt: "Als QualiTaTi (.qlt) herunterladen", exportFailed: "Export fehlgeschlagen.",
    },
    translations: {
      menuLabel: "Übersetzungen", title: "Umfrage-Übersetzungen",
      intro: "Fügen Sie eine Sprache hinzu, um die gesamte Umfrage automatisch mit KI zu übersetzen, und überprüfen und bearbeiten Sie dann jeden Text. Befragte wählen ihre Sprache zu Beginn; leere Felder greifen auf die Standardsprache ({{lang}}) zurück.",
      source: "Quelle", addLanguage: "Sprache hinzufügen",
      empty: "Noch keine Sprachen. Klicken Sie auf «Sprache hinzufügen», um eine KI-Übersetzung zu erstellen.",
      surveyInfo: "Umfrage-Info", fieldTitle: "Titel", fieldDescription: "Beschreibung",
      fieldThankYouTitle: "Dank-Titel", fieldThankYouMessage: "Dank-Nachricht",
      fieldPrompt: "Frage", options: "Optionen", columns: "Skalenspalten",
      retranslate: "Neu übersetzen", close: "Schließen", save: "Speichern",
      translated: "Automatische Übersetzung abgeschlossen. Unten prüfen und bearbeiten.", translateFailed: "Übersetzung fehlgeschlagen.",
      saved: "Übersetzung gespeichert.", saveFailed: "Speichern fehlgeschlagen.",
      confirmDelete: "Diese Sprache und alle Übersetzungen entfernen?", deleteFailed: "Löschen fehlgeschlagen.",
      confirmRetranslate: "Komplett neu übersetzen? Dies überschreibt Ihre Bearbeitungen für diese Sprache.",
      loadFailed: "Übersetzungen konnten nicht geladen werden.",
    },
    hub: {
      emptyTitle: "Erstellen Sie Ihre erste Umfrage",
      emptyBody: "Beginnen Sie von vorne, generieren Sie eine mit KI, wählen Sie eine Vorlage oder importieren Sie eine Word-, PDF- oder Qualtrics-Datei.",
      emptyNoResults: "Keine Umfragen entsprechen Ihren Filtern.",
      clearFilters: "Filter zurücksetzen",
      title: "Umfragen",
      createProject: "Umfrageprojekt erstellen",
      manageSurveys: "Umfragen verwalten",
      createDialogTitle: "Neues Umfrageprojekt",
      projectName: "Projektname",
      projectOutline: "Projektbeschreibung",
      interviewType: "Interview-Typ",
      interviewLanguage: "Interview-Sprache",
      cancel: "Abbrechen",
      save: "Speichern",
      createSuccess: "Projekt erstellt",
      createError: "Projekterstellung fehlgeschlagen",
      fillRequired: "Bitte füllen Sie die Pflichtfelder aus",
      loadError: "Laden der Projekte fehlgeschlagen",
      confirmDeleteProject: "Dieses Umfrageprojekt und alle seine Umfragen löschen? Dies kann nicht rückgängig gemacht werden.",
      projectDeleted: "Projekt erfolgreich gelöscht",
      surveysInProject: "Umfragen in diesem Projekt",
      noSurveysYet: "Noch keine Umfragen",
      manageProjectSurveys: "Umfragen verwalten",
      noProjectsYet: "Noch keine Umfrageprojekte erstellt",
      experimentalTitle: "Experimentelle Funktion",
      experimentalMessage: "Die Konversationsumfrage ist ein experimentelles Werkzeug in aktiver Entwicklung. Einige Funktionen können sich ändern oder unerwartet verhalten. Ihr Feedback hilft uns bei der Verbesserung!",
      search: "Umfragen suchen",
      filterAll: "Alle",
      filterDraft: "Entwurf",
      filterPublished: "Veröffentlicht",
      sortNewest: "Neueste zuerst",
      sortAlpha: "Alphabetisch",
      noResults: "Keine Umfragen entsprechen Ihrer Suche",
      analytics: "Analyse",
      duplicate: "Duplizieren",
      duplicateSuccess: "Umfrage dupliziert",
    },
    list: {
      emptyTitle: "Noch keine Umfragen in diesem Projekt",
      emptyBody: "Erstellen Sie Ihre erste Umfrage, um Antworten zu sammeln.",
      fromTemplate: "Mit Vorlage beginnen",
      fromWizard: "Mit KI generieren",
      fromTemplateTitle: "Mit einer Vorlage beginnen",
      fromTemplateSubtitle: "Wähle eine Vorlage und erhalte einen bearbeitbaren Umfrageentwurf. Wir füllen Fragen, KI-Nachfragen und einen sinnvollen Standardmodus vor.",
      templatesEmpty: "Aktuell sind keine Vorlagen verfügbar.",
      creatingFromTemplate: "Wird erstellt…",
      wizard: {
        title: "Eine Umfrage mit KI generieren",
        subtitle: "Sag Mimi, was du herausfinden möchtest, und sie entwirft eine bearbeitbare Umfrage.",
        generating: "Mimi entwirft deine Umfrage…",
        generatingHint: "Dauert normalerweise 20-40 Sekunden. Wir erstellen ein passendes Frageset mit angemessenen KI-Nachfragen.",
        goalLabel: "Was möchtest du herausfinden?",
        goalPlaceholder: "z. B. Warum Kunden im ersten Monat abspringen — was die Entscheidung treibt und was sie geändert hätte.",
        goalHelp: "Spezifische Ziele führen zu besseren Umfragen. 1-3 Sätze sind ideal.",
        audienceLabel: "Wer nimmt an dieser Umfrage teil?",
        audiencePlaceholder: "z. B. Kund:innen, die in den letzten 30 Tagen gekündigt haben",
        languageLabel: "Sprache",
        depthLabel: "Tiefe",
        nQuestionsLabel: "Zielanzahl an Fragen",
        generate: "Generieren",
        errorGoalTooShort: "Das Ziel muss mindestens 10 Zeichen lang sein, damit die KI etwas zu arbeiten hat.",
        errorAudienceTooShort: "Beschreibe die Zielgruppe, damit die KI passende Fragen stellen kann.",
      },
      title: "Umfrage",
      create: "Umfrage erstellen",
      createDialogTitle: "Neue Umfrage",
      titleField: "Titel",
      descriptionField: "Beschreibung",
      cancel: "Abbrechen",
      save: "Speichern",
      status: "Status",
      builder: "Bearbeiten",
      export: "Exportieren",
      publish: "Veröffentlichen",
      createSuccess: "Umfrage erstellt",
      createError: "Erstellung fehlgeschlagen",
      publishReady: "Veröffentlicht",
      publishError: "Veröffentlichung fehlgeschlagen",
      linkCopied: "Link kopiert",
      publishFirst: "Bitte veröffentlichen Sie die Umfrage zuerst",
      error: "Laden der Umfragen fehlgeschlagen",
      copyLink: "Link kopieren",
      downloadCsv: "CSV herunterladen",
      deleteSurvey: "Umfrage löschen",
      exportReady: "CSV bereit",
      exportError: "Export fehlgeschlagen",
      publishGetLink: "Veröffentlichen und Link erhalten",
      published: "Umfrage veröffentlicht — Ihr Freigabelink ist bereit.",
      questionsLabel: "Fragen",
      responsesLabel: "Antworten"
    },
    builder: {
      minDwellLabel: "Mindestanzeigedauer (Sekunden)",
      minDwellHelp: "Teilnehmer müssen so lange auf dieser Seite bleiben, bevor Weiter/Senden freigeschaltet wird. 0 = aus.",
      saving: "Wird gespeichert…",
      saved: "Gespeichert",
      saveFailed: "Änderungen konnten nicht gespeichert werden",
      aiFollowUp: {
        label: "KI-Nachfrage",
        helperNone: "Keine Nachfrage — Befragte antworten und gehen weiter",
        helperLight: "Light — eine dialogische Nachfrage, um die Antwort anzureichern",
        helperDeep: "Deep — bis zu drei Nachfragen für tiefgehende qualitative Einblicke",
        optionNone: "Keine — keine Nachfrage",
        optionLight: "Light — 1 Nachfrage",
        optionDeep: "Deep — bis zu 3 Nachfragen",
      },
      cancel: "Abbrechen",
      surveyMode: {
        label: "Umfrage-Modus",
        form: {
          title: "Klassische Umfrage",
          subtitle: "Standard-Umfrage als Formular, ohne KI",
        },
        aiFollowUp: {
          title: "Umfrage mit KI-Nachfragen",
          subtitle: "Formular + KI-Nachfragen zu offenen Antworten",
          hint: "Tipp: Stelle die „KI-Nachfrage“-Intensität für jede offene Frage unten ein. Light = 1 Nachfrage; Deep = bis zu 3.",
        },
        aiInterview: {
          title: "Konversationelle Umfrage",
          subtitle: "KI-gesteuertes Konversationsinterview, freier Dialog",
        },
      },
      preflight: {
        title: "Vor-Veröffentlichungs-Check",
        loading: "Deine Umfrage wird geprüft…",
        ready: "Bereit zum Veröffentlichen",
        readyHint: "Deine Umfrage hat alle Prüfungen bestanden. Befragte erleben eine saubere Umfrage.",
        hasErrors: "Behebe folgende Punkte vor der Veröffentlichung:",
        warningsOnly: "Du kannst veröffentlichen, aber prüfe noch Folgendes:",
        fallback: "Health-Check konnte nicht ausgeführt werden — Veröffentlichen ist dennoch erlaubt.",
        rerun: "Erneut prüfen",
      },
      pipingChip: 'Frühere Antwort einfügen',
      pipingTooltipTitle: 'Eine frühere Antwort in dieser Frage anzeigen',
      pipingTooltipBody: 'Klicke auf einen Tag, um ihn zu kopieren, und füge ihn in deinen Fragetext ein. Wenn der Befragte die Frage sieht, wird der Tag durch seine frühere Antwort ersetzt.',
      constantSumItems: 'Elemente',
      constantSumItemsHelp: 'Ein Element pro Zeile',
      constantSumTotal: 'Gesamtsumme',
      constantSumTotalHelp: 'Summe, die Teilnehmer verteilen müssen (z. B. 100)',
      emailAutoConfig: 'Wird als E-Mail-Adresse validiert',
      fileAcceptHelp: 'Erlaubte Dateitypen (kommagetrennt, z. B. .pdf,.docx)',
      fileAcceptTypes: 'Akzeptierte Typen',
      fileMaxSize: 'Maximale Dateigröße (MB)',
      imageChoiceHelp: 'Eine Bild-URL pro Zeile. Optionale Beschriftungen werden nach Reihenfolge zugeordnet.',
      imageChoiceLabels: 'Beschriftungen',
      imageChoiceMultiple: 'Mehrfachauswahl erlauben',
      imageChoiceUrls: 'Bild-URLs',
      phoneAutoConfig: 'Wird als Telefonnummer validiert',
      ratingMax: 'Höchste Bewertung',
      sectionBreakInfo: 'Nur visuelle Trennung — keine Antwort wird erfasst.',
      thankYouPage: 'Danke-Seite',
      thankYouRedirectHelp: 'Optionale URL, zu der nach dem Absenden weitergeleitet wird',
      yesNoAutoConfig: 'Ja / Nein-Auswahl',
      loadError: "Umfrage konnte nicht geladen werden",
      saveMeta: "Umfrage aktualisiert",
      metaSaved: "Umfragedetails gespeichert",
      saveError: "Speichern fehlgeschlagen",
      addError: "Frage hinzufügen fehlgeschlagen",
      questionAdded: "Frage hinzugefügt",
      saveQuestion: "Frage gespeichert",
      questionSaved: "Frage erfolgreich gespeichert",
      questionDeleted: "Frage gelöscht",
      deleteError: "Löschen fehlgeschlagen",
      reorderError: "Neuordnung fehlgeschlagen",
      confirmDelete: "Diese Frage löschen?",
      deleteSurvey: "Umfrage löschen",
      confirmDeleteSurvey: "Möchten Sie diese Umfrage wirklich löschen? Dies kann nicht rückgängig gemacht werden.",
      surveyDeleted: "Umfrage erfolgreich gelöscht",
      surveyFlow: "Umfragefluss",
      addBlock: "Block hinzufügen",
      addRandomizer: "Randomizer hinzufügen",
      blockPlaceholder: "Unbenannter Block",
      deleteBlock: "Block löschen",
      confirmDeleteBlock: "Diesen Block löschen? Die Fragen werden nicht zugewiesen.",
      randomizer: "Randomizer",
      randomizerShow: "Zeige",
      randomizerOf: "von",
      addVariant: "Variante hinzufügen",
      questionsCount: "{n} Frage(n)",
      unassigned: "Nicht zugewiesene Fragen",
      noBlocks: "Noch keine Blöcke",
      renameBlock: "Umbenennen",
      addQuestion: "Hinzufügen",
      publishCopied: "Veröffentlicht und Link kopiert",
      publishSuccess: "Umfrage erfolgreich veröffentlicht",
      publishError: "Veröffentlichung fehlgeschlagen",
      linkCopied: "Link in Zwischenablage kopiert",
      questions: "Fragen",
      noQuestions: "Noch keine Fragen",
      clickToAdd: "Klicken Sie auf die Schaltfläche unten, um Ihre erste Frage hinzuzufügen",
      addFirstQuestion: "Frage hinzufügen",
      untitledQuestion: "(Unbenannte Frage)",
      promptPlaceholder: "Geben Sie Ihre Frage hier ein...",
      surveyTitle: "Umfragetitel",
      surveyDescription: "Umfragebeschreibung",
      noDescription: "Keine Beschreibung",
      questionType: "Fragetyp",
      variableName: "Variablenname",
      variableHelp: "Verwenden Sie snake_case; muss eindeutig sein",
      requiredQuestion: "Pflichtfrage",
      isConversational: "Konversativ fragen",
      askForm: "Über Formular fragen",
      optionsPlaceholder: "Option 1\nOption 2\nOption 3",
      minValue: "Minimalwert",
      maxValue: "Maximalwert",
      saveChanges: "Änderungen speichern",
      moreOptions: "weitere Optionen",
      moveUp: "Nach oben",
      moveDown: "Nach unten",
      moveToBlock: "In Block verschieben",
      expand: "Erweitern",
      collapse: "Einklappen",
      delete: "Löschen",
      duplicate: "Duplizieren",
      duplicateSuccess: "Frage dupliziert",
      republish: "Neu veröffentlichen",
      unpublish: "Veröffentlichung aufheben",
      unpublishing: "Veröffentlichung wird aufgehoben…",
      unpublishSuccess: "Umfrage zurück in Entwurf. Veröffentlichung aufgehoben.",
      unpublishError: "Aufhebung der Veröffentlichung fehlgeschlagen",
      unpublishTooltip: "Neue Teilnehmer blockieren. Vorhandene Antworten bleiben erhalten; der Freigabelink bleibt für die Neuveröffentlichung erhalten.",
      confirmUnpublish: "Veröffentlichung dieser Umfrage aufheben? Neue Teilnehmer werden blockiert. Vorhandene Antworten bleiben erhalten. Sie können später mit demselben Link erneut veröffentlichen.",
      copyLink: "Link kopieren",
      meta: "Umfrageinformationen",
      titleField: "Titel",
      descriptionField: "Beschreibung",
      descriptionPlaceholder: "Beschreiben Sie den Zweck dieser Umfrage...",
      language: "Sprache",
      save: "Speichern",
      imageUrl: "Bild-URL",
      imageUrlHelp: "Optional: Geben Sie eine URL ein, um ein Bild anzuzeigen",
      imagePreview: "Vorschau",
      imageLoadError: "Bild konnte nicht geladen werden. Bitte überprüfen Sie die URL.",
      addImage: "Bild hinzufügen",
      questionImage: "Fragebild",
      removeImage: "Bild entfernen",
      uploadImage: "Bild hochladen",
      uploading: "Hochladen…",
      imageUrlPlaceholder: "Oder Bild-URL einfügen",
      imageHelp: "PNG, JPEG, GIF oder WebP. Max. 2 MB.",
      imageUploadFailed: "Upload fehlgeschlagen.",
      uploadImages: "Bilder hochladen",
      addMoreImages: "Mehr hinzufügen",
      urlPasteHint: "Enter drücken zum Hinzufügen",
      removeAllImages: "Alle entfernen",
      itemsToRank: "Zu bewertende Elemente",
      itemsToRankHelp: "Geben Sie die zu bewertenden Elemente ein, eins pro Zeile",
      bipolarLabels: "Skalenbeschriftungen",
      leftLabel: "Linke Beschriftung",
      rightLabel: "Rechte Beschriftung",
      surveyLanguage: "Umfragesprache",
      surveyLanguageHelp: "Wählen Sie 'Flexibel', damit Teilnehmer ihre Sprache wählen können",
      flexible: "Flexibel (Teilnehmer wählt)",
      publish: "Veröffentlichen & Link kopieren",
      editor: "Fragen-Editor",
      selectPrompt: "Frage zum Bearbeiten auswählen",
      prompt: "Fragetext",
      variable: "Variablenname",
      required: "Erforderlich",
      options: "Optionen (eine pro Zeile)",
      optionsHelp: "Geben Sie eine Option pro Zeile ein",
      min: "Min",
      max: "Max",
      saveQuestionBtn: "Frage speichern",
      types: {
        image: "Bild",
        short_text: "Kurze Antwort",
        long_text: "Absatz",
        single_choice: "Multiple Choice",
        multi_choice: "Kontrollkästchen",
        number: "Zahl",
        date: "Datum",
        ranking: "Rangfolge",
        bipolar_scale: "Bipolare Skala",
        info_text: "Informationstext",
        slider: "Schieberegler",
        dropdown: "Dropdown-Menü",
        nps: "Net Promoter Score",
        matrix: "Matrix",
        ai_interview: "KI-Interview",
        rating_scale: "Bewertungsskala",
        yes_no: "Ja / Nein",
        email: "E-Mail",
        phone: "Telefonnummer",
        image_choice: "Bildauswahl",
        file_upload: "Datei hochladen",
        constant_sum: "Konstantsumme",
        section_break: "Abschnittswechsel",
        page_break: "Seitenumbruch"
      },
      typeDescriptions: {
        short_text: "Kurze Textantwort",
        long_text: "Lange Textantwort",
        single_choice: "Eine Option wählen",
        multi_choice: "Mehrere Optionen auswählen",
        number: "Numerischer Wert",
        date: "Datumsauswahl",
        ranking: "Elemente in Reihenfolge ordnen",
        bipolar_scale: "Skala zwischen zwei Gegensätzen",
        info_text: "Text oder Bilder anzeigen (keine Antwort erforderlich)",
        slider: "Schieben Sie, um einen Wert auszuwählen",
        dropdown: "Eine Option aus einem Dropdown-Menü auswählen",
        nps: "Bewertung von 0 bis 10 (Net Promoter Score)",
        matrix: "Mehrere Elemente auf derselben Skala bewerten",
        ai_interview: "Ein KI-gesteuertes Interviewgespräch einbetten",
        rating_scale: "Sternebewertung (z.B. 1-5 Sterne)",
        yes_no: "Einfache Ja-oder-Nein-Frage",
        email: "E-Mail-Adresse mit Validierung",
        phone: "Telefonnummer mit Validierung",
        image_choice: "Aus Bildoptionen auswählen",
        file_upload: "Dateianhang hochladen",
        constant_sum: "Punkte auf Elemente verteilen",
        section_break: "Visueller Trenner zwischen Abschnitten",
        page_break: "Beginnt eine neue Seite für die Befragten"
      },
      stepValue: "Schritt",
      npsAutoConfig: "NPS wird automatisch als 0-10 Skala konfiguriert. Keine weitere Konfiguration erforderlich.",
      matrixRows: "Zeilenbeschriftungen (eine pro Zeile)",
      matrixRowsHelp: "Geben Sie die zu bewertenden Elemente ein, eines pro Zeile",
      matrixColumns: "Spaltenbeschriftungen (eine pro Zeile)",
      matrixColumnsHelp: "Geben Sie die Skalenoptionen ein, eine pro Zeile",
      interviewConfig: {
        title: "KI-Interview-Einstellungen",
        interactionMode: "Interaktionsmodus",
        voiceToVoice: "Sprache zu Sprache",
        voiceToText: "Sprache zu Text",
        textToText: "Text zu Text",
        textToVoice: "Text zu Sprache",
        language: "Interviewsprache",
        maxDuration: "Maximale Dauer (Minuten)",
        outline: "Interviewleitfaden / Anweisungen",
        outlinePlaceholder: "Beschreiben Sie, wonach die KI fragen soll...",
        outlineHelp: "Der KI-Interviewer wird diesem Leitfaden folgen, um das Gespräch zu führen.",
      },
      defaultPrompt: "Neue Frage",
      preview: "Vorschau",
      previewMode: "Vorschaumodus",
      conditions: "Bedingungen",
      addCondition: "Bedingung hinzufügen",
      removeCondition: "Entfernen",
      conditionLogicAnd: "UND (alle müssen übereinstimmen)",
      conditionLogicOr: "ODER (mindestens eine muss übereinstimmen)",
      conditionQuestion: "Wenn Frage",
      conditionOperator: "Operator",
      conditionValue: "Wert",
      operatorEquals: "ist gleich",
      operatorNotEquals: "ist nicht gleich",
      operatorContains: "enthält",
      operatorGreaterThan: "ist größer als",
      operatorLessThan: "ist kleiner als",
      operatorIsEmpty: "ist leer",
      operatorIsNotEmpty: "ist nicht leer",
      pipingHelp: "Tipp: Verwenden Sie ${variable_name}, um eine vorherige Antwort einzufügen",
      availableVariables: "Verfügbar: {{variables}}",
      // Share & Distribution (Phase 6)
      share: "Teilen",
      shareTitle: "Ihre Umfrage teilen",
      tabLink: "Link / QR",
      tabEmbed: "Einbetten",
      tabSocial: "Soziale Medien",
      tabProtect: "Schutz",
      copy: "Kopieren",
      copied: "Kopiert",
      linkCopied: "Link kopiert",
      qrHint: "Mit der Telefonkamera scannen, um die Umfrage zu öffnen",
      downloadQR: "QR-Code herunterladen (PNG)",
      embedHelp: "Fügen Sie diesen HTML-Code auf Ihrer Website ein, um die Umfrage einzubetten.",
      copyEmbed: "Einbettungscode kopieren",
      embedCopied: "Einbettungscode kopiert",
      preview: "Vorschau",
      socialHelp: "Teilen Sie Ihre Umfrage in sozialen Netzwerken:",
      shareOn: "Teilen auf",
      passwordProtection: "Passwortschutz",
      passwordHint: "Verlangen Sie von den Befragten, ein Passwort einzugeben, bevor sie beginnen.",
      passwordActive: "Ein Passwort ist gesetzt. Geben Sie unten ein neues ein, um es zu ändern, oder lassen Sie es leer, um es zu entfernen.",
      passwordPlaceholder: "Leer lassen für kein Passwort",
      passwordSaved: "Passwort aktualisiert",
      passwordRemoved: "Passwort entfernt",
      removePassword: "Passwort entfernen",
      apply: "Anwenden",
      schedule: "Zeitplan",
      scheduleHelp: "Öffnen und schließen Sie Ihre Umfrage automatisch zu bestimmten Zeiten. Leer lassen für immer offen.",
      openAt: "Öffnet um",
      closeAt: "Schließt um",
      saveSchedule: "Zeitplan speichern",
      scheduleSaved: "Zeitplan gespeichert",
      saveError: "Speichern fehlgeschlagen",
      // Theming (Phase 5)
      themeBranding: "Design & Branding",
      themeBrandingHelp: "Passen Sie das Aussehen der Umfrageschreite an, die Befragte sehen.",
      themePresets: "Schnellvoreinstellungen",
      primaryColor: "Primärfarbe",
      backgroundColor: "Hintergrund",
      textColor: "Textfarbe",
      fontFamily: "Schriftstil",
      fontSystem: "Systemstandard (modern)",
      fontSerif: "Serif (klassisch)",
      fontRounded: "Abgerundet (freundlich)",
      fontMono: "Monospace (technisch)",
      logoUrl: "Logo-Bild-URL",
      logoUrlHelp: "Oben über dem Umfragetitel angezeigt. Optional.",
      headerImageUrl: "URL des Kopfzeilenbild",
      headerImageUrlHelp: "Breites Bannerbild an der Spitze. Optional.",
      themePreview: "Vorschau",
      sampleSurveyTitle: "Ihr Umfragetitel",
      sampleQuestionText: "Hier ist ein Beispiel für eine Frage.",
      sampleSubmit: "Absenden",
      resetTheme: "Auf Standard zurücksetzen",
      randomizeQuestions: "Fragenreihenfolge zufällig",
      randomizeOptions: "Optionsreihenfolge zufällig",
      thankYouSettings: "Dankeseite",
      thankYouTitle: "Titel",
      thankYouMessage: "Nachricht",
      thankYouRedirect: "Weiterleitungs-URL (optional)",
      maxResponses: "Max. Antworten (optional)",
      maxResponsesHelp: "Leer lassen für unbegrenzt",
      deliveryMode: "Bereitstellungsmodus",
      deliveryConversational: "Konversation (KI-Interview)",
      deliveryForm: "Standardformular",
      deliveryModeHelp: "Der Konversationsmodus nutzt KI für Interviews. Der Standardformularmodus zeigt alle Fragen auf einmal an.",
      testWithSynthetic: "Mit synthetischem Menschen testen",
      fromLibrary: "Aus Bibliothek",
      fromLibraryHint: "Aus vorgefertigten Vorlagen und Paketen kopieren",
      templateLibrary: "Vorlagenbibliothek",
      templates: "Vorlagen",
      bundles: "Pakete",
      allTemplates: "Alle Vorlagen",
      myTemplates: "Meine Vorlagen",
      allCategories: "Alle Kategorien",
      selectTemplate: "Wählen Sie eine Vorlage",
      templateName: "Vorlagenname",
      category: "Kategorie",
      search: "Suchen",
      preview: "Vorschau",
      inserting: "Wird eingefügt...",
      insertTemplate: "Vorlage einfügen",
      insertBundle: "Paket einfügen",
      noTemplates: "Keine Vorlagen gefunden",
      noBundles: "Keine Pakete gefunden",
      templateInsertedSuccess: "Vorlage eingefügt",
      bundleInsertedSuccess: "Paket eingefügt",
      templateInsertError: "Fehler beim Einfügen der Vorlage",
      cancel: "Abbrechen",
      skipLogic: "Sprunglogik",
      skipLogicHelp: "Diese Frage nur anzeigen, wenn die Bedingungen erfüllt sind",
      displayLogic: "Anzeigelogik",
      displayLogicHelp: "Diese Frage nur anzeigen, wenn vorherige Antworten diesen Bedingungen entsprechen",
      conditionIf: "Wenn",
      logicTab: "Logik",
      noLogicYet: "Keine Sprunglogik konfiguriert",
      noLogicHint: "Klicken Sie auf eine Frage und scrollen Sie zum Abschnitt Sprunglogik, um Bedingungen hinzuzufügen.",
      logicSummaryPrefix: "Diese Frage anzeigen, wenn",
      settings: "Umfrageeinstellungen"
    },
    export: {
      loadError: "Umfrage konnte nicht geladen werden",
      description: "Eingereichte Antworten als CSV herunterladen.",
      button: "CSV herunterladen",
      success: "Export bereit",
      error: "Export fehlgeschlagen",
      xlsx: "XLSX herunterladen"
    },
    analytics: {
      filterScopeNote: "Dieser Datumsfilter schränkt nur die Antwortliste und die Kreuztabellen ein. Die Übersichtskarten, Diagramme und die KI-Zusammenfassung zeigen immer alle Antworten.",
      clearFilter: 'Filter löschen',
      columnQuestion: 'Spaltenfrage',
      crossTab: 'Kreuztabelle',
      crossTabHelp: 'Vergleiche, wie sich Antworten einer Frage nach einer anderen aufschlüsseln.',
      crossTabNeedTwo: 'Wähle zwei Fragen zum Vergleich.',
      crossTabPickTwo: 'Wähle eine Zeilen- und eine Spaltenfrage',
      crossTabTitle: 'Kreuztabellierung',
      duration: 'Dauer',
      filterBy: 'Filtern nach',
      from: 'Von',
      individualResponses: 'Einzelantworten',
      language: 'Sprache',
      next: 'Weiter',
      noAnswer: 'Keine Antwort',
      noResponsesYet: 'Noch keine Antworten',
      deleteResponse: 'Antwort löschen',
      confirmDeleteResponse: 'Diese Antwort dauerhaft löschen? Dies kann nicht rückgängig gemacht werden.',
      responseDeleted: 'Antwort gelöscht.',
      deleteFailed: 'Antwort konnte nicht gelöscht werden.',
      download: 'Herunterladen',
      downloadCsv: 'CSV herunterladen',
      downloadXlsx: 'Excel (XLSX) herunterladen',
      exportFailed: 'Export fehlgeschlagen.',
      prev: 'Zurück',
      respondent: 'Teilnehmer',
      rowQuestion: 'Zeilenfrage',
      sampleResponses: 'Beispielantworten',
      submittedAt: 'Eingereicht am',
      to: 'Bis',
      viewResponses: 'Antworten ansehen',
      wordCloud: 'Wortwolke',
      title: "Umfrageanalyse",
      totalResponses: "Gesamtantworten",
      completionRate: "Abschlussrate",
      avgDuration: "Durchschn. Dauer",
      minutes: "Minuten",
      responsesOverTime: "Antworten im Zeitverlauf",
      questionBreakdown: "Fragenanalyse",
      responses: "Antworten",
      noData: "Noch keine Antworten",
      refresh: "Aktualisieren",
      detractors: "Kritiker (0-6)",
      passives: "Passive (7-8)",
      promoters: "Promotoren (9-10)"
    },
    start: {
      untitledSurvey: "Unbenannte Umfrage",
      timeEstimate: "Etwa {{minutes}} {{unit}}",
      questionCount: "{{count}} {{unit}}",
      minute: "Minute",
      minutes: "Minuten",
      question: "Frage",
      questions: "Fragen",
      privacyNote: "Ihre Antworten sind vertraulich",
      startButton: "Umfrage starten",
      selectLanguageFirst: "Bitte wähle eine Sprache, um fortzufahren.",
      title: "Umfrage starten",
      subtitle: "Starten Sie die Konversationsumfrage.",
      button: "Starten",
      loading: "Umfrage wird gestartet...",
      error: "Sitzung konnte nicht gestartet werden",
      selectLanguage: "Wählen Sie Ihre Sprache",
      selectLanguageSubtitle: "Wählen Sie die Sprache für Ihre Umfragesitzung",
      surveyFull: "Diese Umfrage hat ihr Antwortlimit erreicht"
    },
    session: {
      dwellWait: "Sie können in {{n}} s fortfahren",
      redirectingIn: "Weiterleitung in {seconds}…",
      continueNow: "Jetzt fortfahren",
      cancelRedirect: "Abbrechen",
      redirectCancelled: "Automatische Weiterleitung abgebrochen.",
      continueToSite: "Zur Webseite fortfahren",
      loadError: "Sitzung konnte nicht geladen werden",
      connected: "Verbunden mit Assistent",
      wsError: "Chat-Verbindung fehlgeschlagen",
      answersSaved: "Antworten gespeichert",
      saveError: "Speichern fehlgeschlagen",
      submitted: "Eingereicht",
      submitError: "Einreichen fehlgeschlagen",
      progress: "Fortschritt: {{answered}} / {{total}} erforderlich",
      placeholder: "Geben Sie Ihre Antwort ein",
      send: "Senden",
      verification: "Überprüfung",
      required: "Erforderlich",
      saveEdits: "Änderungen speichern",
      submit: "Bestätigen & Einreichen",
      loading: "Lädt...",
      processing: "Verarbeite Ihre Antwort...",
      stopRecording: "Aufnahme beenden",
      pressToSpeak: "Drücken Sie zum Sprechen",
      reviewSubmit: "Überprüfen & Einreichen",
      formMode: "Standardformular",
      conversationalMode: "KI-Interview",
      voicePlayback: "Stimme",
      switchToForm: "Zum Formular wechseln",
      switchToConversational: "Zum KI-Interview wechseln",
      answerMore: "Beantworten Sie noch {{count}} erforderliche Frage(n)",
      transcribeFailed: "Audio konnte nicht transkribiert werden. Bitte versuchen Sie es erneut.",
      audioProcessError: "Audioverarbeitung fehlgeschlagen: {{error}}",
      micError: "Kein Zugriff auf Mikrofon: {{error}}",
      rankingPlaceholder: "Geben Sie die Rangfolge ein (z.B. A > B > C)",
      thankYouTitle: "Vielen Dank!",
      thankYouMessage: "Ihre Antworten wurden erfolgreich übermittelt. Wir schätzen Ihre Zeit und Ihr Feedback.",
      returnHome: "Zurück zur Startseite",
      status_in_progress: "In Bearbeitung",
      pageOf: "Seite {current} von {total}",
      nextPage: "Weiter",
      previousPage: "Zurück",
      status_submitted: "Eingereicht",
      status_awaiting_verification: "In Prüfung",
      previewBanner: "[VORSCHAUMODUS] Dies ist eine Vorschau. Antworten werden nicht in den Analysen gespeichert.",
      redirecting: "Weiterleitung...",
      progressLabel: "Fortschritt",
      autoSaving: "Speichern…",
      autoSaved: "Gespeichert",
      saveAndContinue: "Speichern und später fortfahren",
      saveAndContinueTooltip: "Speichere deinen Fortschritt und erhalte einen Link, um später fortzufahren",
      saveAndContinueTitle: "Fortschritt speichern",
      saveAndContinueBody: "Deine Antworten wurden gespeichert. Setze ein Lesezeichen für diesen Link oder sende ihn dir per E-Mail, um auf jedem Gerät dort weiterzumachen, wo du aufgehört hast.",
      copyLink: "Link kopieren",
      linkCopied: "Link in die Zwischenablage kopiert",
      close: "Schließen",
      alreadySubmittedWarning: "Unseren Aufzeichnungen zufolge hast du diese Umfrage möglicherweise bereits abgesendet. Du kannst trotzdem fortfahren, doppelte Antworten können jedoch ausgeschlossen werden.",
      connectionError: "Verbindungsfehler",
      reconnecting: "Verbindung zum Assistenten wird wiederhergestellt...",
      connectionLost: "Verbindung verloren. Bitte Seite aktualisieren.",
      typeHere: "Geben Sie hier Ihre Antwort ein...",
      send: "Senden",
      requiredField: "erforderlich",
      fieldRequired: "{label} ist erforderlich",
      invalidEmail: "Bitte geben Sie eine gültige E-Mail-Adresse ein",
      invalidPhone: "Bitte geben Sie eine gültige Telefonnummer ein",
      invalidNumber: "Bitte geben Sie eine gültige Zahl ein",
      sumMustEqual: "Die Werte müssen sich zu {total} summieren (aktuell: {sum})",
      npsNotLikely: "Überhaupt nicht wahrscheinlich",
      npsExtremely: "Äußerst wahrscheinlich",
      chooseFile: "Datei auswählen",
      chooseFileDrag: "Datei auswählen oder hierher ziehen",
      fileSelected: "Datei ausgewählt",
      selectedPrefix: "Ausgewählt:",
      acceptedPrefix: "Akzeptiert:",
      fileTooLarge: "Die Dateigröße muss kleiner als {size} MB sein",
      constantSumInstruction: "Verteilen Sie insgesamt {total} Punkte auf die untenstehenden Elemente.",
      remaining: "Verbleibend:",
      pageBreakLabel: "Seitenumbruch",
      noFormConfigured: "Kein Formular konfiguriert",
      noFormConfiguredBody: "Der Projektinhaber hat noch kein benutzerdefiniertes Formular eingerichtet.",
      unsupportedField: "Nicht unterstützter Feldtyp: {type}",
      imageNotConfigured: "Bild-URL nicht konfiguriert",
      videoNotConfigured: "Video-URL nicht konfiguriert",
      dragToReorder: "Zum Neuanordnen ziehen",
      moveUp: "Nach oben",
      moveDown: "Nach unten",
      requiredSummary: "Bitte beantworten Sie die hervorgehobene(n) Frage(n), bevor Sie fortfahren.",
      yes: "Ja",
      no: "Nein",
      optionN: "Option {n}",
      surveyImageAlt: "Umfrageinformationen",
      interview: {
        title: "KI-Interview",
        description: "Diese Frage enthält ein kurzes KI-Interview.",
        start: "Interview starten",
        starting: "Interview wird gestartet...",
        ending: "Interview wird gespeichert...",
        completed: "Interview abgeschlossen",
        end: "Beenden",
        stopRecording: "Aufnahme stoppen",
        tapToSpeak: "Tippen zum Sprechen",
        typePlaceholder: "Geben Sie Ihre Antwort ein...",
      },
    },
    languages: {
      en: "Englisch",
      zh: "Chinesisch",
      fr: "Französisch",
      de: "Deutsch",
      es: "Spanisch",
      nb: "Norwegisch",
      nl: "Niederländisch",
      pt: "Portugiesisch",
      ja: "Japanisch",
      ar: "Arabisch",
      flexible: "Flexibel (Teilnehmer wählt)"
    },
    synthetic: {
      dialogTitle: "Test mit synthetischem Teilnehmer",
      dialogSubtitle: "Konfigurieren Sie das demografische Profil Ihres simulierten Teilnehmers",
      age: "Alter", gender: "Geschlecht", male: "Männlich", female: "Weiblich", nonBinary: "Nicht-binär", preferNotToSay: "Keine Angabe",
      nationality: "Nationalität", ethnicity: "Ethnizität", education: "Bildung",
      highSchool: "Gymnasium", bachelors: "Bachelorabschluss", masters: "Masterabschluss", phd: "Doktortitel", vocational: "Berufsausbildung", other: "Sonstige",
      occupation: "Beruf", personality: "Persönlichkeitsmerkmale", additionalContext: "Zusätzlicher Kontext (optional)",
      runTest: "Test starten", cancel: "Abbrechen",
      simulating: "Teilnehmerantworten werden simuliert...", simulatingHint: "Dies kann 15-30 Sekunden dauern",
      resultsTitle: "Synthetische Testergebnisse", syntheticPersona: "Synthetisches Teilnehmerprofil", synthetic: "Synthetisch",
      question: "Frage", type: "Typ", answer: "Antwort", close: "Schließen",
    },
    status: {
      draft: "Entwurf",
      published: "Veröffentlicht",
      archived: "Archiviert"
    }
  },
  es: {
    tutorial: {
      title: "Primeros pasos",
      step1Title: "Bienvenido al creador de encuestas",
      step1Body: "Crea encuestas conversacionales en las que una IA hace preguntas de seguimiento inteligentes y se adapta a cada participante — mucho más ricas que un formulario estático.",
      step2Title: "Añade tus preguntas",
      step2Body: "Añade preguntas de cualquier tipo — opción múltiple, escalas, texto abierto — y arrástralas para reordenarlas. Usa saltos de página y secciones para dar forma al flujo.",
      step3Title: "Configura la IA",
      step3Body: "Define el idioma, el tono y con qué profundidad la IA repregunta en las respuestas abiertas. También puedes traducir toda la encuesta automáticamente.",
      step4Title: "Comparte y analiza",
      step4Body: "Publica y comparte tu enlace, y observa cómo llegan las respuestas y las analíticas a medida que los participantes completan la encuesta.",
      back: "Atrás",
      next: "Siguiente",
      done: "Empezar",
      skip: "Omitir",
      help: "Cómo funciona",
    },
    import: {
      menuLabel: "Importar archivo", reviewTitle: "Revisar encuesta importada",
      reviewIntro: "Redactamos {{n}} preguntas a partir de tu archivo. Revísalas abajo y luego crea la encuesta — podrás editarla por completo en el editor.",
      cancel: "Cancelar", titleField: "Título de la encuesta", descriptionField: "Descripción",
      reportTitle: "Notas de importación", preview: "Preguntas", pageBreak: "Salto de página",
      sectionBreak: "Sección", required: "obligatorio", create: "Crear encuesta",
      parseFailed: "No se pudo importar este archivo.", createFailed: "No se pudo crear la encuesta.",
      uploadTitle: "Importar una encuesta", parsing: "Leyendo tu archivo…",
      dropHere: "Arrastra un archivo aquí, o haz clic para explorar",
      dropHint: "Hasta 25 MB. Redactamos una encuesta que podrás revisar y editar.",
      supported: "Formatos compatibles",
      formatNote: "La IA convierte Word y PDF en una encuesta: transcribe un cuestionario existente o redacta uno a partir de tus notas. Qualtrics .qsf y QualiTaTi .qlt se analizan con exactitud.",
      exportDefinition: "Descargar encuesta", exportWord: "Descargar como Word (.docx)", exportQlt: "Descargar como QualiTaTi (.qlt)", exportFailed: "La exportación falló.",
    },
    translations: {
      menuLabel: "Traducciones", title: "Traducciones de la encuesta",
      intro: "Añade un idioma para traducir toda la encuesta automáticamente con IA y luego revisa y edita cada texto. Los encuestados eligen su idioma al inicio; cualquier campo vacío usa el idioma predeterminado ({{lang}}).",
      source: "origen", addLanguage: "Añadir idioma",
      empty: "Aún no hay idiomas. Haz clic en «Añadir idioma» para crear una traducción con IA.",
      surveyInfo: "Información de la encuesta", fieldTitle: "Título", fieldDescription: "Descripción",
      fieldThankYouTitle: "Título de agradecimiento", fieldThankYouMessage: "Mensaje de agradecimiento",
      fieldPrompt: "Pregunta", options: "Opciones", columns: "Columnas de escala",
      retranslate: "Volver a traducir", close: "Cerrar", save: "Guardar",
      translated: "Traducción automática completada. Revisa y edita abajo.", translateFailed: "La traducción falló.",
      saved: "Traducción guardada.", saveFailed: "Error al guardar.",
      confirmDelete: "¿Eliminar este idioma y todas sus traducciones?", deleteFailed: "Error al eliminar.",
      confirmRetranslate: "¿Volver a traducir desde cero? Esto sobrescribirá tus ediciones para este idioma.",
      loadFailed: "Error al cargar las traducciones.",
    },
    hub: {
      emptyTitle: "Crea tu primera encuesta",
      emptyBody: "Empieza desde cero, genera una con IA, elige una plantilla o importa un archivo de Word, PDF o Qualtrics.",
      emptyNoResults: "Ninguna encuesta coincide con tus filtros.",
      clearFilters: "Borrar filtros",
      title: "Encuestas",
      createProject: "Crear proyecto de encuesta",
      manageSurveys: "Gestionar encuestas",
      createDialogTitle: "Nuevo proyecto de encuesta",
      projectName: "Nombre del proyecto",
      projectOutline: "Descripción del proyecto",
      interviewType: "Tipo de entrevista",
      interviewLanguage: "Idioma de entrevista",
      cancel: "Cancelar",
      save: "Guardar",
      createSuccess: "Proyecto creado",
      createError: "Error al crear proyecto",
      fillRequired: "Por favor complete los campos requeridos",
      loadError: "Error al cargar proyectos",
      confirmDeleteProject: "¿Eliminar este proyecto de encuesta y todas sus encuestas? Esto no se puede deshacer.",
      projectDeleted: "Proyecto eliminado exitosamente",
      surveysInProject: "Encuestas en este proyecto",
      noSurveysYet: "Aún no hay encuestas",
      manageProjectSurveys: "Gestionar encuestas",
      noProjectsYet: "Aún no se han creado proyectos de encuesta",
      experimentalTitle: "Función experimental",
      experimentalMessage: "La Encuesta Conversacional es una herramienta experimental en desarrollo activo. Algunas funciones pueden cambiar o comportarse de manera inesperada. ¡Sus comentarios nos ayudan a mejorar!",
      search: "Buscar encuestas",
      filterAll: "Todas",
      filterDraft: "Borrador",
      filterPublished: "Publicadas",
      sortNewest: "Más recientes",
      sortAlpha: "Alfabético",
      noResults: "Ninguna encuesta coincide con su búsqueda",
      analytics: "Analítica",
      duplicate: "Duplicar",
      duplicateSuccess: "Encuesta duplicada",
    },
    list: {
      emptyTitle: "Aún no hay encuestas en este proyecto",
      emptyBody: "Crea tu primera encuesta para empezar a recopilar respuestas.",
      fromTemplate: "Empezar desde plantilla",
      fromWizard: "Generar con IA",
      fromTemplateTitle: "Empezar desde una plantilla",
      fromTemplateSubtitle: "Elige una plantilla y obtén un borrador de encuesta editable. Preconfiguramos preguntas, repreguntas de IA y un modo por defecto adecuado.",
      templatesEmpty: "Por ahora no hay plantillas disponibles.",
      creatingFromTemplate: "Creando…",
      wizard: {
        title: "Generar una encuesta con IA",
        subtitle: "Dile a Mimi qué quieres aprender y redactará una encuesta que puedas editar.",
        generating: "Mimi está redactando tu encuesta…",
        generatingHint: "Suele tardar de 20 a 40 segundos. Estamos generando un set de preguntas con repreguntas de IA adecuadas.",
        goalLabel: "¿Qué quieres aprender?",
        goalPlaceholder: "p. ej., por qué los clientes se van tras el primer mes — qué motiva la decisión y qué la habría cambiado.",
        goalHelp: "Objetivos específicos producen mejores encuestas. Apunta a 1-3 oraciones.",
        audienceLabel: "¿Quién responderá esta encuesta?",
        audiencePlaceholder: "p. ej., clientes que cancelaron en los últimos 30 días",
        languageLabel: "Idioma",
        depthLabel: "Profundidad",
        nQuestionsLabel: "Nº objetivo de preguntas",
        generate: "Generar",
        errorGoalTooShort: "El objetivo debe tener al menos 10 caracteres para que la IA tenga con qué trabajar.",
        errorAudienceTooShort: "Indica la audiencia para que la IA adapte las preguntas.",
      },
      title: "Encuesta",
      create: "Crear encuesta",
      createDialogTitle: "Nueva encuesta",
      titleField: "Título",
      descriptionField: "Descripción",
      cancel: "Cancelar",
      save: "Guardar",
      status: "Estado",
      builder: "Editor",
      export: "Exportar",
      publish: "Publicar",
      createSuccess: "Encuesta creada",
      createError: "Error al crear",
      publishReady: "Publicado",
      publishError: "Error al publicar",
      linkCopied: "Enlace copiado",
      publishFirst: "Por favor publique la encuesta primero",
      exportReady: "CSV listo",
      exportError: "Error de exportación",
      error: "Error al cargar encuestas",
      copyLink: "Copiar enlace",
      downloadCsv: "Descargar CSV",
      deleteSurvey: "Eliminar encuesta",
      publishGetLink: "Publicar y obtener enlace",
      published: "Encuesta publicada: tu enlace para compartir está listo.",
      questionsLabel: "preguntas",
      responsesLabel: "respuestas"
    },
    builder: {
      minDwellLabel: "Tiempo mínimo de visualización (segundos)",
      minDwellHelp: "Los encuestados deben permanecer en esta página este tiempo antes de que se active Siguiente/Enviar. 0 = desactivado.",
      saving: "Guardando…",
      saved: "Guardado",
      saveFailed: "No se pudieron guardar los cambios",
      aiFollowUp: {
        label: "Repregunta de IA",
        helperNone: "Sin repregunta — los participantes responden y avanzan",
        helperLight: "Light — una repregunta conversacional para enriquecer la respuesta",
        helperDeep: "Deep — hasta tres repreguntas para insight cualitativo en profundidad",
        optionNone: "Ninguna — sin repregunta",
        optionLight: "Light — 1 repregunta",
        optionDeep: "Deep — hasta 3 repreguntas",
      },
      cancel: "Cancelar",
      surveyMode: {
        label: "Modo de encuesta",
        form: {
          title: "Encuesta tradicional",
          subtitle: "Encuesta estándar basada en formulario, sin IA",
        },
        aiFollowUp: {
          title: "Encuesta con repreguntas de IA",
          subtitle: "Formulario + repreguntas de IA en respuestas abiertas",
          hint: "Consejo: ajusta la intensidad de \"Repregunta de IA\" en cada pregunta abierta. Light = 1 repregunta; Deep = hasta 3.",
        },
        aiInterview: {
          title: "Encuesta conversacional",
          subtitle: "Entrevista conversacional dirigida por IA, diálogo libre",
        },
      },
      preflight: {
        title: "Comprobación previa a la publicación",
        loading: "Comprobando tu encuesta…",
        ready: "Listo para publicar",
        readyHint: "Tu encuesta superó todas las comprobaciones. Los participantes verán una experiencia impecable.",
        hasErrors: "Corrige los problemas antes de publicar:",
        warningsOnly: "Puedes publicar, pero revisa estos detalles:",
        fallback: "No se pudo ejecutar la comprobación — se permite publicar igualmente.",
        rerun: "Volver a comprobar",
      },
      pipingChip: 'Insertar una respuesta anterior',
      pipingTooltipTitle: 'Mostrar una respuesta anterior dentro de esta pregunta',
      pipingTooltipBody: 'Haz clic en una etiqueta para copiarla y pégala en el texto de tu pregunta. Cuando el encuestado la vea, se convertirá en su respuesta anterior.',
      constantSumItems: 'Elementos',
      constantSumItemsHelp: 'Un elemento por línea',
      constantSumTotal: 'Total',
      constantSumTotalHelp: 'Suma que los encuestados deben repartir (p. ej. 100)',
      emailAutoConfig: 'Se valida como dirección de correo electrónico',
      fileAcceptHelp: 'Tipos de archivo permitidos (separados por comas, p. ej. .pdf,.docx)',
      fileAcceptTypes: 'Tipos aceptados',
      fileMaxSize: 'Tamaño máx. de archivo (MB)',
      imageChoiceHelp: 'Una URL de imagen por línea. Las etiquetas opcionales coinciden por orden.',
      imageChoiceLabels: 'Etiquetas',
      imageChoiceMultiple: 'Permitir selección múltiple',
      imageChoiceUrls: 'URLs de imágenes',
      phoneAutoConfig: 'Se valida como número de teléfono',
      ratingMax: 'Puntuación máxima',
      sectionBreakInfo: 'Solo separador visual — no se recoge respuesta.',
      thankYouPage: 'Página de agradecimiento',
      thankYouRedirectHelp: 'URL opcional a la que redirigir tras el envío',
      yesNoAutoConfig: 'Elección Sí / No',
      loadError: "Error al cargar encuesta",
      saveMeta: "Encuesta actualizada",
      metaSaved: "Detalles de encuesta guardados",
      saveError: "Error al guardar",
      addError: "Error al agregar pregunta",
      questionAdded: "Pregunta agregada",
      saveQuestion: "Pregunta guardada",
      questionSaved: "Pregunta guardada exitosamente",
      questionDeleted: "Pregunta eliminada",
      deleteError: "Error al eliminar",
      reorderError: "Error al reordenar",
      confirmDelete: "¿Eliminar esta pregunta?",
      deleteSurvey: "Eliminar encuesta",
      confirmDeleteSurvey: "¿Está seguro de que desea eliminar esta encuesta? Esto no se puede deshacer.",
      surveyDeleted: "Encuesta eliminada exitosamente",
      surveyFlow: "Flujo de encuesta",
      addBlock: "Añadir bloque",
      addRandomizer: "Añadir aleatorizador",
      blockPlaceholder: "Bloque sin título",
      deleteBlock: "Eliminar bloque",
      confirmDeleteBlock: "¿Eliminar este bloque? Sus preguntas quedarán sin asignar.",
      randomizer: "Aleatorizador",
      randomizerShow: "Mostrar",
      randomizerOf: "de",
      addVariant: "Añadir variante",
      questionsCount: "{n} pregunta(s)",
      unassigned: "Preguntas sin asignar",
      noBlocks: "Sin bloques aún",
      renameBlock: "Renombrar",
      addQuestion: "Agregar",
      publishCopied: "Publicado y enlace copiado",
      publishSuccess: "Encuesta publicada exitosamente",
      publishError: "Error al publicar",
      linkCopied: "Enlace copiado al portapapeles",
      questions: "Preguntas",
      noQuestions: "Aún no hay preguntas",
      clickToAdd: "Haga clic en el botón de abajo para agregar su primera pregunta",
      addFirstQuestion: "Agregar pregunta",
      untitledQuestion: "(Pregunta sin título)",
      promptPlaceholder: "Ingrese su pregunta aquí...",
      surveyTitle: "Título de la encuesta",
      surveyDescription: "Descripción de la encuesta",
      noDescription: "Sin descripción",
      questionType: "Tipo de pregunta",
      variableName: "Nombre de variable",
      variableHelp: "Use snake_case; debe ser único",
      requiredQuestion: "Pregunta obligatoria",
      isConversational: "Preguntar conversacionalmente",
      askForm: "Preguntar por formulario",
      optionsPlaceholder: "Opción 1\nOpción 2\nOpción 3",
      minValue: "Valor mínimo",
      maxValue: "Valor máximo",
      saveChanges: "Guardar cambios",
      moreOptions: "más opciones",
      moveUp: "Mover arriba",
      moveDown: "Mover abajo",
      moveToBlock: "Mover al bloque",
      expand: "Expandir",
      collapse: "Contraer",
      delete: "Eliminar",
      duplicate: "Duplicar",
      duplicateSuccess: "Pregunta duplicada",
      republish: "Volver a publicar",
      unpublish: "Despublicar",
      unpublishing: "Despublicando…",
      unpublishSuccess: "Encuesta despublicada. Ahora es un borrador.",
      unpublishError: "Error al despublicar la encuesta",
      unpublishTooltip: "Bloquear nuevos participantes. Las respuestas existentes se conservan; el enlace de compartir se mantiene para volver a publicar.",
      confirmUnpublish: "¿Despublicar esta encuesta? Se bloquearán los nuevos participantes. Las respuestas existentes se conservan. Puede volver a publicarla más tarde con el mismo enlace.",
      copyLink: "Copiar enlace",
      meta: "Información de encuesta",
      titleField: "Título",
      descriptionField: "Descripción",
      descriptionPlaceholder: "Describa el propósito de esta encuesta...",
      language: "Idioma",
      save: "Guardar",
      imageUrl: "URL de imagen",
      imageUrlHelp: "Opcional: Ingrese una URL para mostrar una imagen",
      imagePreview: "Vista previa",
      imageLoadError: "Error al cargar imagen. Por favor verifique la URL.",
      addImage: "Agregar imagen",
      questionImage: "Imagen de pregunta",
      removeImage: "Eliminar imagen",
      uploadImage: "Subir imagen",
      uploading: "Subiendo…",
      imageUrlPlaceholder: "O pegar URL de imagen",
      imageHelp: "PNG, JPEG, GIF o WebP. Máx. 2 MB.",
      imageUploadFailed: "Error al subir.",
      uploadImages: "Subir imágenes",
      addMoreImages: "Agregar más",
      urlPasteHint: "Presiona Enter para agregar",
      removeAllImages: "Eliminar todo",
      itemsToRank: "Elementos a clasificar",
      itemsToRankHelp: "Ingrese los elementos a clasificar, uno por línea",
      bipolarLabels: "Etiquetas de escala",
      leftLabel: "Etiqueta izquierda",
      rightLabel: "Etiqueta derecha",
      surveyLanguage: "Idioma de la encuesta",
      surveyLanguageHelp: "Seleccione 'Flexible' para permitir que los participantes elijan su idioma",
      flexible: "Flexible (El participante elige)",
      publish: "Publicar y Copiar enlace",
      editor: "Editor de preguntas",
      selectPrompt: "Seleccione una pregunta para editar",
      prompt: "Texto de la pregunta",
      variable: "Nombre de variable",
      required: "Requerido",
      options: "Opciones (una por línea)",
      optionsHelp: "Ingrese una opción por línea",
      min: "Mín",
      max: "Máx",
      saveQuestionBtn: "Guardar pregunta",
      types: {
        image: "Imagen",
        short_text: "Respuesta corta",
        long_text: "Párrafo",
        single_choice: "Opción múltiple",
        multi_choice: "Casillas de verificación",
        number: "Número",
        date: "Fecha",
        ranking: "Clasificación",
        bipolar_scale: "Escala bipolar",
        info_text: "Texto informativo",
        slider: "Control deslizante",
        dropdown: "Menú desplegable",
        nps: "Net Promoter Score",
        matrix: "Matriz",
        ai_interview: "Entrevista IA",
        rating_scale: "Escala de valoración",
        yes_no: "Sí / No",
        email: "Correo electrónico",
        phone: "Número de teléfono",
        image_choice: "Elección de imagen",
        file_upload: "Subida de archivo",
        constant_sum: "Suma constante",
        section_break: "Separador de sección",
        page_break: "Salto de página"
      },
      typeDescriptions: {
        short_text: "Respuesta de texto breve",
        long_text: "Respuesta de texto largo",
        single_choice: "Elegir una opción",
        multi_choice: "Seleccionar múltiples opciones",
        number: "Valor numérico",
        date: "Selector de fecha",
        ranking: "Ordenar elementos",
        bipolar_scale: "Escala entre dos opuestos",
        info_text: "Mostrar texto o imágenes (no se requiere respuesta)",
        slider: "Deslizar para seleccionar un valor en un rango",
        dropdown: "Seleccionar una opción de un menú desplegable",
        nps: "Calificar de 0 a 10 (Net Promoter Score)",
        matrix: "Calificar múltiples elementos en la misma escala",
        ai_interview: "Integrar una conversación de entrevista impulsada por IA",
        rating_scale: "Calificación por estrellas (ej. 1-5 estrellas)",
        yes_no: "Pregunta simple de sí o no",
        email: "Dirección de correo con validación",
        phone: "Número de teléfono con validación",
        image_choice: "Seleccionar entre opciones de imagen",
        file_upload: "Subir un archivo adjunto",
        constant_sum: "Distribuir puntos entre elementos",
        section_break: "Separador visual entre secciones",
        page_break: "Inicia una nueva página para los encuestados"
      },
      stepValue: "Paso",
      npsAutoConfig: "El NPS se configura automáticamente como una escala de 0 a 10. No se necesita configuración adicional.",
      matrixRows: "Etiquetas de filas (una por línea)",
      matrixRowsHelp: "Ingrese los elementos a calificar, uno por línea",
      matrixColumns: "Etiquetas de columnas (una por línea)",
      matrixColumnsHelp: "Ingrese las opciones de escala, una por línea",
      interviewConfig: {
        title: "Configuración de entrevista IA",
        interactionMode: "Modo de interacción",
        voiceToVoice: "Voz a Voz",
        voiceToText: "Voz a Texto",
        textToText: "Texto a Texto",
        textToVoice: "Texto a Voz",
        language: "Idioma de la entrevista",
        maxDuration: "Duración máxima (minutos)",
        outline: "Esquema de la entrevista / Instrucciones",
        outlinePlaceholder: "Describe qué debería preguntar la IA...",
        outlineHelp: "El entrevistador IA seguirá este esquema para guiar la conversación.",
      },
      defaultPrompt: "Nueva pregunta",
      preview: "Vista previa",
      previewMode: "Modo de vista previa",
      conditions: "Condiciones",
      addCondition: "Agregar condición",
      removeCondition: "Eliminar",
      conditionLogicAnd: "Y (todas deben coincidir)",
      conditionLogicOr: "O (al menos una debe coincidir)",
      conditionQuestion: "Si la pregunta",
      conditionOperator: "Operador",
      conditionValue: "Valor",
      operatorEquals: "es igual a",
      operatorNotEquals: "no es igual a",
      operatorContains: "contiene",
      operatorGreaterThan: "es mayor que",
      operatorLessThan: "es menor que",
      operatorIsEmpty: "está vacío",
      operatorIsNotEmpty: "no está vacío",
      pipingHelp: "Consejo: Use ${variable_name} para insertar una respuesta anterior",
      availableVariables: "Disponibles: {{variables}}",
      // Share & Distribution (Phase 6)
      share: "Compartir",
      shareTitle: "Compartir su encuesta",
      tabLink: "Enlace / Código QR",
      tabEmbed: "Insertar",
      tabSocial: "Redes sociales",
      tabProtect: "Proteger",
      copy: "Copiar",
      copied: "Copiado",
      linkCopied: "Enlace copiado",
      qrHint: "Escanear con la cámara del teléfono para abrir la encuesta",
      downloadQR: "Descargar código QR (PNG)",
      embedHelp: "Pegue este HTML en su sitio web para incrustar la encuesta.",
      copyEmbed: "Copiar código de inserción",
      embedCopied: "Código de inserción copiado",
      preview: "Previsualizar",
      socialHelp: "Compartir su encuesta en redes sociales:",
      shareOn: "Compartir en",
      passwordProtection: "Protección con contraseña",
      passwordHint: "Requiera que los encuestados ingresen una contraseña antes de comenzar.",
      passwordActive: "Se ha establecido una contraseña. Ingrese una nueva a continuación para cambiarla o déjela en blanco para eliminarla.",
      passwordPlaceholder: "Dejar en blanco para sin contraseña",
      passwordSaved: "Contraseña actualizada",
      passwordRemoved: "Contraseña eliminada",
      removePassword: "Eliminar contraseña",
      apply: "Aplicar",
      schedule: "Programación",
      scheduleHelp: "Abra y cierre automáticamente su encuesta en horarios específicos. Dejar en blanco para siempre abierto.",
      openAt: "Se abre en",
      closeAt: "Se cierra en",
      saveSchedule: "Guardar programación",
      scheduleSaved: "Programación guardada",
      saveError: "Error al guardar",
      // Theming (Phase 5)
      themeBranding: "Tema y marca",
      themeBrandingHelp: "Personalice la apariencia de la página de encuesta que ven los encuestados.",
      themePresets: "Preajustes rápidos",
      primaryColor: "Color primario",
      backgroundColor: "Fondo",
      textColor: "Color del texto",
      fontFamily: "Estilo de fuente",
      fontSystem: "Predeterminado del sistema (moderno)",
      fontSerif: "Serif (clásico)",
      fontRounded: "Redondeado (amigable)",
      fontMono: "Monoespaciado (técnico)",
      logoUrl: "URL de imagen de logotipo",
      logoUrlHelp: "Se muestra encima del título de la encuesta. Opcional.",
      headerImageUrl: "URL de imagen de encabezado",
      headerImageUrlHelp: "Imagen de banner ancho en la parte superior. Opcional.",
      themePreview: "Previsualizar",
      sampleSurveyTitle: "Título de su encuesta",
      sampleQuestionText: "Así es como se ve una pregunta.",
      sampleSubmit: "Enviar",
      resetTheme: "Restablecer a predeterminado",
      randomizeQuestions: "Orden aleatorio de preguntas",
      randomizeOptions: "Orden aleatorio de opciones",
      thankYouSettings: "Página de agradecimiento",
      thankYouTitle: "Título",
      thankYouMessage: "Mensaje",
      thankYouRedirect: "URL de redirección (opcional)",
      maxResponses: "Respuestas máximas (opcional)",
      maxResponsesHelp: "Dejar vacío para ilimitado",
      deliveryMode: "Modo de entrega",
      deliveryConversational: "Conversacional (Entrevista IA)",
      deliveryForm: "Formulario estándar",
      deliveryModeHelp: "El modo conversacional usa IA para realizar entrevistas. El modo formulario estándar muestra todas las preguntas a la vez.",
      testWithSynthetic: "Probar con humano sintético",
      fromLibrary: "De la biblioteca",
      fromLibraryHint: "Copiar desde plantillas y paquetes predefinidos",
      templateLibrary: "Biblioteca de plantillas",
      templates: "Plantillas",
      bundles: "Paquetes",
      allTemplates: "Todas las plantillas",
      myTemplates: "Mis plantillas",
      allCategories: "Todas las categorías",
      selectTemplate: "Seleccione una plantilla",
      templateName: "Nombre de plantilla",
      category: "Categoría",
      search: "Buscar",
      preview: "Vista previa",
      inserting: "Insertando...",
      insertTemplate: "Insertar plantilla",
      insertBundle: "Insertar paquete",
      noTemplates: "No se encontraron plantillas",
      noBundles: "No se encontraron paquetes",
      templateInsertedSuccess: "Plantilla insertada",
      bundleInsertedSuccess: "Paquete insertado",
      templateInsertError: "Error al insertar plantilla",
      cancel: "Cancelar",
      skipLogic: "Lógica de salto",
      skipLogicHelp: "Mostrar esta pregunta solo cuando se cumplan las condiciones",
      displayLogic: "Lógica de visualización",
      displayLogicHelp: "Mostrar esta pregunta solo cuando las respuestas anteriores coincidan con estas condiciones",
      conditionIf: "Si",
      logicTab: "Lógica",
      noLogicYet: "No hay lógica de salto configurada",
      noLogicHint: "Haga clic en una pregunta y desplácese hasta la sección de Lógica de salto para agregar condiciones.",
      logicSummaryPrefix: "Mostrar esta pregunta cuando",
      settings: "Configuración de encuesta"
    },
    export: {
      loadError: "Error al cargar encuesta",
      description: "Descargar respuestas enviadas como CSV.",
      button: "Descargar CSV",
      success: "Exportación lista",
      error: "Error de exportación",
      xlsx: "Descargar XLSX"
    },
    analytics: {
      filterScopeNote: "Este filtro de fechas solo limita la lista de respuestas y las tablas cruzadas. Las tarjetas de resumen, los gráficos y el resumen de IA siempre reflejan todas las respuestas.",
      clearFilter: 'Borrar filtro',
      columnQuestion: 'Pregunta de columna',
      crossTab: 'Tabla cruzada',
      crossTabHelp: 'Compara cómo se distribuyen las respuestas de una pregunta según otra.',
      crossTabNeedTwo: 'Elige dos preguntas para comparar.',
      crossTabPickTwo: 'Selecciona una pregunta de fila y columna',
      crossTabTitle: 'Tabulación cruzada',
      duration: 'Duración',
      filterBy: 'Filtrar por',
      from: 'Desde',
      individualResponses: 'Respuestas individuales',
      language: 'Idioma',
      next: 'Siguiente',
      noAnswer: 'Sin respuesta',
      noResponsesYet: 'Aún no hay respuestas',
      deleteResponse: 'Eliminar respuesta',
      confirmDeleteResponse: '¿Eliminar esta respuesta permanentemente? Esta acción no se puede deshacer.',
      responseDeleted: 'Respuesta eliminada.',
      deleteFailed: 'No se pudo eliminar la respuesta.',
      download: 'Descargar',
      downloadCsv: 'Descargar CSV',
      downloadXlsx: 'Descargar Excel (XLSX)',
      exportFailed: 'La exportación falló.',
      prev: 'Anterior',
      respondent: 'Encuestado',
      rowQuestion: 'Pregunta de fila',
      sampleResponses: 'Ejemplos de respuestas',
      submittedAt: 'Enviado el',
      to: 'Hasta',
      viewResponses: 'Ver respuestas',
      wordCloud: 'Nube de palabras',
      title: "Analítica de encuesta",
      totalResponses: "Respuestas totales",
      completionRate: "Tasa de finalización",
      avgDuration: "Duración promedio",
      minutes: "minutos",
      responsesOverTime: "Respuestas a lo largo del tiempo",
      questionBreakdown: "Desglose por pregunta",
      responses: "respuestas",
      noData: "Aún no hay respuestas",
      refresh: "Actualizar",
      detractors: "Detractores (0-6)",
      passives: "Pasivos (7-8)",
      promoters: "Promotores (9-10)"
    },
    start: {
      untitledSurvey: "Encuesta sin título",
      timeEstimate: "Aproximadamente {{minutes}} {{unit}}",
      questionCount: "{{count}} {{unit}}",
      minute: "minuto",
      minutes: "minutos",
      question: "pregunta",
      questions: "preguntas",
      privacyNote: "Tus respuestas son confidenciales",
      startButton: "Iniciar encuesta",
      selectLanguageFirst: "Selecciona un idioma para continuar.",
      title: "Iniciar encuesta",
      subtitle: "Inicie la encuesta conversacional.",
      button: "Iniciar",
      loading: "Iniciando encuesta...",
      error: "No se pudo iniciar la sesión",
      selectLanguage: "Seleccione su idioma",
      selectLanguageSubtitle: "Elija el idioma para su sesión de encuesta",
      surveyFull: "Esta encuesta ha alcanzado su límite de respuestas"
    },
    session: {
      dwellWait: "Puedes continuar en {{n}} s",
      redirectingIn: "Redirigiendo en {seconds}…",
      continueNow: "Continuar ahora",
      cancelRedirect: "Cancelar",
      redirectCancelled: "Redirección automática cancelada.",
      continueToSite: "Continuar al sitio",
      loadError: "Error al cargar sesión",
      connected: "Conectado al asistente",
      wsError: "Error de conexión de chat",
      answersSaved: "Respuestas guardadas",
      saveError: "Error al guardar",
      submitted: "Enviado",
      submitError: "Error al enviar",
      progress: "Progreso: {{answered}} / {{total}} requeridas",
      placeholder: "Escriba su respuesta",
      send: "Enviar",
      verification: "Verificación",
      required: "Requerido",
      saveEdits: "Guardar ediciones",
      submit: "Aprobar y enviar",
      loading: "Cargando...",
      processing: "Procesando su respuesta...",
      stopRecording: "Detener grabación",
      pressToSpeak: "Presione para hablar",
      reviewSubmit: "Revisar y enviar",
      formMode: "Formulario estándar",
      conversationalMode: "Entrevista IA",
      voicePlayback: "Voz",
      switchToForm: "Cambiar a formulario",
      switchToConversational: "Cambiar a entrevista IA",
      answerMore: "Responda {{count}} pregunta(s) requerida(s) más",
      transcribeFailed: "No se pudo transcribir el audio. Por favor intente de nuevo.",
      audioProcessError: "Error de procesamiento de audio: {{error}}",
      micError: "No se puede acceder al micrófono: {{error}}",
      rankingPlaceholder: "Ingrese el orden de clasificación (ej. A > B > C)",
      thankYouTitle: "¡Gracias!",
      thankYouMessage: "Sus respuestas han sido enviadas exitosamente. Apreciamos su tiempo y comentarios.",
      returnHome: "Volver al inicio",
      status_in_progress: "En progreso",
      pageOf: "Página {current} de {total}",
      nextPage: "Siguiente",
      previousPage: "Atrás",
      status_submitted: "Enviado",
      status_awaiting_verification: "En revisión",
      previewBanner: "[MODO VISTA PREVIA] Esta es una vista previa. Las respuestas no se guardarán en los análisis.",
      redirecting: "Redirigiendo...",
      progressLabel: "Progreso",
      autoSaving: "Guardando…",
      autoSaved: "Guardado",
      saveAndContinue: "Guardar y continuar más tarde",
      saveAndContinueTooltip: "Guarda tu progreso y obtén un enlace para continuar más tarde",
      saveAndContinueTitle: "Guarda tu progreso",
      saveAndContinueBody: "Tus respuestas se han guardado. Marca este enlace o envíatelo por correo electrónico para continuar donde lo dejaste, desde cualquier dispositivo.",
      copyLink: "Copiar enlace",
      linkCopied: "Enlace copiado al portapapeles",
      close: "Cerrar",
      alreadySubmittedWarning: "Nuestros registros muestran que es posible que ya hayas enviado esta encuesta. Puedes continuar, pero las respuestas duplicadas podrían excluirse.",
      connectionError: "Error de conexión",
      reconnecting: "Reconectando con el asistente...",
      connectionLost: "Conexión perdida. Por favor, actualice la página.",
      typeHere: "Escriba su respuesta aquí...",
      send: "Enviar",
      requiredField: "obligatorio",
      fieldRequired: "{label} es obligatorio",
      invalidEmail: "Por favor, introduzca una dirección de correo electrónico válida",
      invalidPhone: "Por favor, introduzca un número de teléfono válido",
      invalidNumber: "Por favor, introduzca un número válido",
      sumMustEqual: "Los valores deben sumar {total} (actual: {sum})",
      npsNotLikely: "Nada probable",
      npsExtremely: "Extremadamente probable",
      chooseFile: "Elegir archivo",
      chooseFileDrag: "Elija un archivo o arrástrelo aquí",
      fileSelected: "Archivo seleccionado",
      selectedPrefix: "Seleccionado:",
      acceptedPrefix: "Aceptado:",
      fileTooLarge: "El tamaño del archivo debe ser inferior a {size} MB",
      constantSumInstruction: "Distribuya un total de {total} puntos entre los elementos a continuación.",
      remaining: "Restante:",
      pageBreakLabel: "Salto de página",
      noFormConfigured: "Ningún formulario configurado",
      noFormConfiguredBody: "El propietario del proyecto aún no ha configurado un formulario personalizado.",
      unsupportedField: "Tipo de campo no admitido: {type}",
      imageNotConfigured: "URL de la imagen no configurada",
      videoNotConfigured: "URL del video no configurada",
      dragToReorder: "Arrastre para reordenar",
      moveUp: "Mover arriba",
      moveDown: "Mover abajo",
      requiredSummary: "Por favor, responda las preguntas resaltadas antes de continuar.",
      yes: "Sí",
      no: "No",
      optionN: "Opción {n}",
      surveyImageAlt: "Información de la encuesta",
      interview: {
        title: "Entrevista IA",
        description: "Esta pregunta incluye una breve entrevista IA.",
        start: "Iniciar entrevista",
        starting: "Iniciando entrevista...",
        ending: "Guardando entrevista...",
        completed: "Entrevista completada",
        end: "Finalizar",
        stopRecording: "Detener grabación",
        tapToSpeak: "Toque para hablar",
        typePlaceholder: "Escriba su respuesta...",
      },
    },
    languages: {
      en: "Inglés",
      zh: "Chino",
      fr: "Francés",
      de: "Alemán",
      es: "Español",
      nb: "Noruego",
      nl: "Neerlandés",
      pt: "Portugués",
      ja: "Japonés",
      ar: "Árabe",
      pt: "Portugués",
      ja: "Japonés",
      ar: "Árabe",
      flexible: "Flexible (El participante elige)"
    },
    synthetic: {
      dialogTitle: "Probar con participante sintético",
      dialogSubtitle: "Configure el perfil demográfico de su participante simulado",
      age: "Edad", gender: "Género", male: "Masculino", female: "Femenino", nonBinary: "No binario", preferNotToSay: "Prefiero no decir",
      nationality: "Nacionalidad", ethnicity: "Etnia", education: "Educación",
      highSchool: "Secundaria", bachelors: "Licenciatura", masters: "Maestría", phd: "Doctorado", vocational: "Formación profesional", other: "Otro",
      occupation: "Ocupación", personality: "Rasgos de personalidad", additionalContext: "Contexto adicional (opcional)",
      runTest: "Ejecutar prueba", cancel: "Cancelar",
      simulating: "Simulando respuestas del participante...", simulatingHint: "Esto puede tomar 15-30 segundos",
      resultsTitle: "Resultados de prueba sintética", syntheticPersona: "Perfil del participante sintético", synthetic: "Sintético",
      question: "Pregunta", type: "Tipo", answer: "Respuesta", close: "Cerrar",
    },
    status: {
      draft: "Borrador",
      published: "Publicado",
      archived: "Archivado"
    }
  },
  pt: {
    tutorial: {
      title: "Primeiros passos",
      step1Title: "Bem-vindo ao criador de pesquisas",
      step1Body: "Crie pesquisas conversacionais em que uma IA faz perguntas de acompanhamento inteligentes e se adapta a cada respondente — muito mais ricas do que um formulário estático.",
      step2Title: "Adicione suas perguntas",
      step2Body: "Adicione perguntas de qualquer tipo — múltipla escolha, escalas, texto aberto — e arraste para reordená-las. Use quebras de página e seções para moldar o fluxo.",
      step3Title: "Configure a IA",
      step3Body: "Defina o idioma, o tom e a profundidade com que a IA investiga com perguntas de acompanhamento nas respostas abertas. Você também pode traduzir toda a pesquisa automaticamente.",
      step4Title: "Compartilhe e analise",
      step4Body: "Publique e compartilhe seu link e acompanhe as respostas e análises chegando à medida que os participantes concluem a pesquisa.",
      back: "Voltar",
      next: "Avançar",
      done: "Começar",
      skip: "Pular",
      help: "Como funciona",
    },
    import: {
      menuLabel: "Importar arquivo", reviewTitle: "Revisar pesquisa importada",
      reviewIntro: "Elaboramos {{n}} perguntas a partir do seu arquivo. Revise abaixo e crie a pesquisa — você poderá editá-la totalmente no construtor.",
      cancel: "Cancelar", titleField: "Título da pesquisa", descriptionField: "Descrição",
      reportTitle: "Notas de importação", preview: "Perguntas", pageBreak: "Quebra de página",
      sectionBreak: "Seção", required: "obrigatório", create: "Criar pesquisa",
      parseFailed: "Não foi possível importar este arquivo.", createFailed: "Não foi possível criar a pesquisa.",
      uploadTitle: "Importar uma pesquisa", parsing: "Lendo seu arquivo…",
      dropHere: "Arraste um arquivo aqui ou clique para procurar",
      dropHint: "Até 25 MB. Criamos um rascunho que você pode revisar e editar.",
      supported: "Formatos compatíveis",
      formatNote: "A IA transforma Word e PDF em uma pesquisa — transcreve um questionário existente ou cria um a partir das suas anotações. Qualtrics .qsf e QualiTaTi .qlt são analisados com exatidão.",
      exportDefinition: "Baixar pesquisa", exportWord: "Baixar como Word (.docx)", exportQlt: "Baixar como QualiTaTi (.qlt)", exportFailed: "Falha na exportação.",
    },
    translations: {
      menuLabel: "Traduções", title: "Traduções da pesquisa",
      intro: "Adicione um idioma para traduzir toda a pesquisa automaticamente com IA e, em seguida, revise e edite cada texto. Os respondentes escolhem o idioma no início; campos vazios usam o idioma padrão ({{lang}}).",
      source: "origem", addLanguage: "Adicionar idioma",
      empty: "Ainda não há idiomas. Clique em «Adicionar idioma» para criar uma tradução com IA.",
      surveyInfo: "Informações da pesquisa", fieldTitle: "Título", fieldDescription: "Descrição",
      fieldThankYouTitle: "Título de agradecimento", fieldThankYouMessage: "Mensagem de agradecimento",
      fieldPrompt: "Pergunta", options: "Opções", columns: "Colunas da escala",
      retranslate: "Traduzir novamente", close: "Fechar", save: "Salvar",
      translated: "Tradução automática concluída. Revise e edite abaixo.", translateFailed: "Falha na tradução.",
      saved: "Tradução salva.", saveFailed: "Falha ao salvar.",
      confirmDelete: "Remover este idioma e todas as suas traduções?", deleteFailed: "Falha ao excluir.",
      confirmRetranslate: "Traduzir do zero novamente? Isso substituirá suas edições para este idioma.",
      loadFailed: "Falha ao carregar as traduções.",
    },
    hub: {
      emptyTitle: "Crie sua primeira pesquisa",
      emptyBody: "Comece do zero, gere uma com IA, escolha um modelo ou importe um arquivo do Word, PDF ou Qualtrics.",
      emptyNoResults: "Nenhuma pesquisa corresponde aos seus filtros.",
      clearFilters: "Limpar filtros",
      title: "Pesquisas",
      createProject: "Criar projeto de pesquisa",
      manageSurveys: "Gerenciar pesquisas",
      createDialogTitle: "Novo projeto de pesquisas",
      projectName: "Nome do projeto",
      projectOutline: "Descrição do projeto",
      interviewType: "Tipo de entrevista",
      interviewLanguage: "Idioma da entrevista",
      cancel: "Cancelar",
      save: "Salvar",
      createSuccess: "Projeto criado",
      createError: "Falha ao criar projeto",
      fillRequired: "Por favor preencha os campos obrigatórios",
      loadError: "Falha ao carregar projetos",
      confirmDeleteProject: "Excluir este projeto de pesquisa e todas as suas pesquisas? Isso não pode ser desfeito.",
      projectDeleted: "Projeto excluído com sucesso",
      surveysInProject: "Pesquisas neste projeto",
      noSurveysYet: "Nenhuma pesquisa ainda",
      manageProjectSurveys: "Gerenciar Pesquisas",
      noProjectsYet: "Nenhum projeto de pesquisa criado ainda",
      experimentalTitle: "Recurso Experimental",
      experimentalMessage: "A Pesquisa Conversacional é uma ferramenta experimental em desenvolvimento ativo.",
      search: "Pesquisar pesquisas",
      filterAll: "Todas",
      filterDraft: "Rascunho",
      filterPublished: "Publicadas",
      sortNewest: "Mais recentes",
      sortAlpha: "Alfabética",
      noResults: "Nenhuma pesquisa corresponde à sua busca",
      analytics: "Analítica",
      duplicate: "Duplicar",
      duplicateSuccess: "Pesquisa duplicada",
    },
    list: {
      emptyTitle: "Ainda não há pesquisas neste projeto",
      emptyBody: "Crie sua primeira pesquisa para começar a coletar respostas.",
      fromTemplate: "Começar com modelo",
      fromWizard: "Gerar com IA",
      fromTemplateTitle: "Começar com um modelo",
      fromTemplateSubtitle: "Escolha um modelo e obtenha um rascunho de pesquisa editável. Já preenchemos perguntas, follow-ups de IA e um modo padrão adequado.",
      templatesEmpty: "Nenhum modelo disponível no momento.",
      creatingFromTemplate: "Criando…",
      wizard: {
        title: "Gerar uma pesquisa com IA",
        subtitle: "Diga à Mimi o que quer aprender e ela rascunhará uma pesquisa editável.",
        generating: "Mimi está rascunhando sua pesquisa…",
        generatingHint: "Normalmente leva de 20 a 40 segundos. Estamos gerando um conjunto de perguntas sob medida com follow-ups de IA adequados.",
        goalLabel: "O que você quer aprender?",
        goalPlaceholder: "ex.: Por que clientes cancelam após o primeiro mês — o que motiva a decisão e o que poderia tê-la mudado.",
        goalHelp: "Objetivos específicos geram pesquisas melhores. Mire em 1-3 frases.",
        audienceLabel: "Quem vai responder esta pesquisa?",
        audiencePlaceholder: "ex.: Clientes que cancelaram nos últimos 30 dias",
        languageLabel: "Idioma",
        depthLabel: "Profundidade",
        nQuestionsLabel: "Nº alvo de perguntas",
        generate: "Gerar",
        errorGoalTooShort: "O objetivo precisa ter ao menos 10 caracteres para a IA trabalhar.",
        errorAudienceTooShort: "Diga qual é o público para a IA adaptar as perguntas.",
      },
      title: "Pesquisa",
      create: "Criar pesquisa",
      createDialogTitle: "Nova pesquisa",
      titleField: "Título",
      descriptionField: "Descrição",
      cancel: "Cancelar",
      save: "Salvar",
      builder: "Construtor",
      export: "Exportar",
      publish: "Publicar",
      status: "Status",
      linkCopied: "Link compartilhado copiado",
      publishFirst: "Por favor publique a pesquisa primeiro",
      publishReady: "Pesquisa publicada",
      publishError: "Falha ao publicar",
      createSuccess: "Pesquisa criada",
      createError: "Falha ao criar",
      exportReady: "CSV pronto",
      exportError: "Falha ao exportar",
      error: "Falha ao carregar pesquisas",
      copyLink: "Copiar link de compartilhamento",
      downloadCsv: "Baixar CSV",
      deleteSurvey: "Excluir pesquisa",
      publishGetLink: "Publicar e obter link",
      published: "Pesquisa publicada — seu link de compartilhamento está pronto.",
      questionsLabel: "perguntas",
      responsesLabel: "respostas"
    },
    builder: {
      minDwellLabel: "Tempo mínimo de exibição (segundos)",
      minDwellHelp: "Os respondentes devem permanecer nesta página esse tempo antes de Avançar/Enviar desbloquear. 0 = desativado.",
      saving: "Salvando…",
      saved: "Salvo",
      saveFailed: "Não foi possível salvar as alterações",
      aiFollowUp: {
        label: "Follow-up de IA",
        helperNone: "Sem follow-up — respondentes respondem e avançam",
        helperLight: "Light — um follow-up conversacional para enriquecer a resposta",
        helperDeep: "Deep — até três follow-ups para insights qualitativos profundos",
        optionNone: "Nenhum — sem follow-up",
        optionLight: "Light — 1 follow-up",
        optionDeep: "Deep — até 3 follow-ups",
      },
      cancel: "Cancelar",
      surveyMode: {
        label: "Modo da pesquisa",
        form: {
          title: "Pesquisa tradicional",
          subtitle: "Pesquisa padrão baseada em formulário, sem IA",
        },
        aiFollowUp: {
          title: "Pesquisa com follow-ups de IA",
          subtitle: "Formulário + follow-ups de IA em respostas abertas",
          hint: "Dica: defina a intensidade de \"Follow-up de IA\" em cada pergunta aberta. Light = 1 follow-up; Deep = até 3.",
        },
        aiInterview: {
          title: "Pesquisa conversacional",
          subtitle: "Entrevista conversacional liderada por IA, diálogo livre",
        },
      },
      preflight: {
        title: "Checagem antes de publicar",
        loading: "Verificando sua pesquisa…",
        ready: "Pronto para publicar",
        readyHint: "Sua pesquisa passou em todas as checagens. Respondentes terão uma experiência limpa.",
        hasErrors: "Corrija os pontos abaixo antes de publicar:",
        warningsOnly: "Você pode publicar, mas vale revisar estes pontos:",
        fallback: "Não foi possível executar a checagem — publicação permitida mesmo assim.",
        rerun: "Reverificar",
      },
      pipingChip: 'Inserir uma resposta anterior',
      pipingTooltipTitle: 'Mostrar uma resposta anterior dentro desta pergunta',
      pipingTooltipBody: 'Clique em uma tag para copiá-la e cole no texto da sua pergunta. Quando o respondente a vir, ela se tornará a resposta anterior dele.',
      constantSumItems: 'Itens',
      constantSumItemsHelp: 'Um item por linha',
      constantSumTotal: 'Total',
      constantSumTotalHelp: 'Soma que os respondentes devem distribuir (ex.: 100)',
      emailAutoConfig: 'Validado como endereço de e-mail',
      fileAcceptHelp: 'Tipos de arquivo permitidos (separados por vírgula, ex.: .pdf,.docx)',
      fileAcceptTypes: 'Tipos aceitos',
      fileMaxSize: 'Tamanho máx. do arquivo (MB)',
      imageChoiceHelp: 'Uma URL de imagem por linha. Rótulos opcionais correspondem pela ordem.',
      imageChoiceLabels: 'Rótulos',
      imageChoiceMultiple: 'Permitir múltiplas seleções',
      imageChoiceUrls: 'URLs de imagens',
      phoneAutoConfig: 'Validado como número de telefone',
      ratingMax: 'Avaliação máxima',
      sectionBreakInfo: 'Apenas separador visual — nenhuma resposta é coletada.',
      thankYouPage: 'Página de agradecimento',
      thankYouRedirectHelp: 'URL opcional para redirecionar após o envio',
      yesNoAutoConfig: 'Escolha Sim / Não',
      loadError: "Falha ao carregar pesquisa", saveMeta: "Pesquisa atualizada", metaSaved: "Detalhes salvos", saveError: "Falha ao salvar", addError: "Falha ao adicionar pergunta", questionAdded: "Pergunta adicionada", saveQuestion: "Pergunta salva", duplicate: "Duplicar", duplicateSuccess: "Pergunta duplicada", preview: "Pré-visualizar", previewMode: "Modo de pré-visualização", moveToBlock: "Mover para bloco",
      types: {
        image: "Imagem",
        short_text: "Resposta curta", long_text: "Parágrafo", single_choice: "Múltipla escolha", multi_choice: "Caixas de seleção", number: "Número", date: "Data", ranking: "Classificação", bipolar_scale: "Escala bipolar", info_text: "Texto informativo",
        slider: "Controle deslizante", dropdown: "Menu suspenso", nps: "Net Promoter Score", matrix: "Matriz",
        ai_interview: "Entrevista IA",
        rating_scale: "Escala de avaliação",
        yes_no: "Sim / Não",
        email: "E-mail",
        phone: "Número de telefone",
        image_choice: "Escolha de imagem",
        file_upload: "Upload de arquivo",
        constant_sum: "Soma constante",
        section_break: "Quebra de seção",
        page_break: "Quebra de página"
      },
      typeDescriptions: {
        short_text: "Resposta de texto breve", long_text: "Resposta de texto longo", single_choice: "Escolher uma opção", multi_choice: "Selecionar múltiplas opções", number: "Valor numérico", date: "Seletor de data", ranking: "Ordenar elementos", bipolar_scale: "Escala entre dois opostos", info_text: "Exibir texto ou imagens (sem resposta necessária)",
        slider: "Deslize para selecionar um valor em um intervalo", dropdown: "Selecione uma opção de um menu suspenso", nps: "Avalie de 0 a 10 (Net Promoter Score)", matrix: "Avalie múltiplos itens na mesma escala",
        ai_interview: "Incorporar uma conversa de entrevista com IA",
        rating_scale: "Classificação por estrelas (ex. 1-5 estrelas)",
        yes_no: "Pergunta simples de sim ou não",
        email: "Endereço de e-mail com validação",
        phone: "Número de telefone com validação",
        image_choice: "Selecionar entre opções de imagem",
        file_upload: "Enviar um arquivo anexo",
        constant_sum: "Distribuir pontos entre itens",
        section_break: "Separador visual entre seções",
        page_break: "Inicia uma nova página para os respondentes"
      },
      stepValue: "Passo", npsAutoConfig: "O NPS é configurado automaticamente como uma escala de 0 a 10. Nenhuma configuração extra necessária.", matrixRows: "Rótulos das linhas (um por linha)", matrixRowsHelp: "Insira os itens a serem avaliados, um por linha", matrixColumns: "Rótulos das colunas (um por linha)", matrixColumnsHelp: "Insira as opções de escala, uma por linha",
      interviewConfig: {
        title: "Configurações da entrevista IA",
        interactionMode: "Modo de interação",
        voiceToVoice: "Voz para Voz",
        voiceToText: "Voz para Texto",
        textToText: "Texto para Texto",
        textToVoice: "Texto para Voz",
        language: "Idioma da entrevista",
        maxDuration: "Duração máxima (minutos)",
        outline: "Roteiro da entrevista / Instruções",
        outlinePlaceholder: "Descreva o que a IA deve perguntar...",
        outlineHelp: "O entrevistador IA seguirá este roteiro para guiar a conversa.",
      },
      conditions: "Condições",
      addCondition: "Adicionar condição",
      removeCondition: "Remover",
      conditionLogicAnd: "E (todas devem corresponder)",
      conditionLogicOr: "OU (pelo menos uma deve corresponder)",
      conditionQuestion: "Se a pergunta",
      conditionOperator: "Operador",
      conditionValue: "Valor",
      operatorEquals: "é igual a",
      operatorNotEquals: "não é igual a",
      operatorContains: "contém",
      operatorGreaterThan: "é maior que",
      operatorLessThan: "é menor que",
      operatorIsEmpty: "está vazio",
      operatorIsNotEmpty: "não está vazio",
      pipingHelp: "Dica: Use ${variable_name} para inserir uma resposta anterior",
      availableVariables: "Disponíveis: {{variables}}",
      // Share & Distribution (Phase 6)
      share: "Compartilhar",
      shareTitle: "Compartilhe sua pesquisa",
      tabLink: "Link / QR",
      tabEmbed: "Incorporar",
      tabSocial: "Redes sociais",
      tabProtect: "Proteger",
      copy: "Copiar",
      copied: "Copiado",
      linkCopied: "Link copiado",
      qrHint: "Escaneie com a câmera do telefone para abrir a pesquisa",
      downloadQR: "Baixar código QR (PNG)",
      embedHelp: "Cole este HTML em seu site para incorporar a pesquisa.",
      copyEmbed: "Copiar código incorporado",
      embedCopied: "Código incorporado copiado",
      preview: "Visualizar",
      socialHelp: "Compartilhe sua pesquisa em plataformas de redes sociais:",
      shareOn: "Compartilhar em",
      passwordProtection: "Proteção por senha",
      passwordHint: "Exigir que os respondentes insiram uma senha antes de começar.",
      passwordActive: "Uma senha está definida. Digite uma nova abaixo para alterá-la ou deixe em branco para remover.",
      passwordPlaceholder: "Deixe em branco para nenhuma senha",
      passwordSaved: "Senha atualizada",
      passwordRemoved: "Senha removida",
      removePassword: "Remover senha",
      apply: "Aplicar",
      schedule: "Cronograma",
      scheduleHelp: "Abra e feche automaticamente sua pesquisa em horários específicos. Deixe em branco para sempre aberto.",
      openAt: "Abre em",
      closeAt: "Fecha em",
      saveSchedule: "Salvar cronograma",
      scheduleSaved: "Cronograma salvo",
      saveError: "Falha ao salvar",
      // Theming (Phase 5)
      themeBranding: "Tema e marca",
      themeBrandingHelp: "Personalize a aparência da página de pesquisa que os respondentes veem.",
      themePresets: "Presets rápidos",
      primaryColor: "Cor primária",
      backgroundColor: "Fundo",
      textColor: "Cor do texto",
      fontFamily: "Estilo de fonte",
      fontSystem: "Padrão do sistema (moderno)",
      fontSerif: "Serif (clássico)",
      fontRounded: "Arredondado (amigável)",
      fontMono: "Monoespaciado (técnico)",
      logoUrl: "URL da imagem do logotipo",
      logoUrlHelp: "Exibido acima do título da pesquisa. Opcional.",
      headerImageUrl: "URL da imagem do cabeçalho",
      headerImageUrlHelp: "Imagem de faixa larga na parte superior. Opcional.",
      themePreview: "Visualizar",
      sampleSurveyTitle: "Título da sua pesquisa",
      sampleQuestionText: "Assim é como uma pergunta se parece.",
      sampleSubmit: "Enviar",
      resetTheme: "Redefinir para padrão",
      randomizeQuestions: "Ordem aleatória das perguntas",
      randomizeOptions: "Ordem aleatória das opções",
      thankYouSettings: "Página de agradecimento",
      thankYouTitle: "Título",
      thankYouMessage: "Mensagem",
      thankYouRedirect: "URL de redirecionamento (opcional)",
      maxResponses: "Respostas máximas (opcional)",
      maxResponsesHelp: "Deixe vazio para ilimitado",
      deliveryMode: "Modo de entrega",
      deliveryConversational: "Conversacional (Entrevista com IA)",
      deliveryForm: "Formulário padrão",
      deliveryModeHelp: "O modo conversacional usa IA para conduzir entrevistas. O modo formulário padrão mostra todas as perguntas de uma vez.",
      testWithSynthetic: "Testar com humano sintético",
      fromLibrary: "Da biblioteca",
      fromLibraryHint: "Copiar de modelos e pacotes pré-configurados",
      templateLibrary: "Biblioteca de modelos",
      templates: "Modelos",
      bundles: "Pacotes",
      allTemplates: "Todos os modelos",
      myTemplates: "Meus modelos",
      allCategories: "Todas as categorias",
      selectTemplate: "Selecione um modelo",
      templateName: "Nome do modelo",
      category: "Categoria",
      search: "Pesquisar",
      preview: "Visualizar",
      inserting: "Inserindo...",
      insertTemplate: "Inserir modelo",
      insertBundle: "Inserir pacote",
      noTemplates: "Nenhum modelo encontrado",
      noBundles: "Nenhum pacote encontrado",
      templateInsertedSuccess: "Modelo inserido",
      bundleInsertedSuccess: "Pacote inserido",
      templateInsertError: "Erro ao inserir modelo",
      cancel: "Cancelar",
      skipLogic: "Lógica de salto",
      skipLogicHelp: "Mostrar esta pergunta apenas quando as condições forem atendidas",
      displayLogic: "Lógica de exibição",
      displayLogicHelp: "Mostrar esta pergunta apenas quando as respostas anteriores corresponderem a estas condições",
      conditionIf: "Se",
      logicTab: "Lógica",
      noLogicYet: "Nenhuma lógica de salto configurada",
      noLogicHint: "Clique em uma pergunta e role até a seção de Lógica de salto para adicionar condições.",
      logicSummaryPrefix: "Mostrar esta pergunta quando",
      settings: "Configurações da pesquisa",
      surveyFlow: "Fluxo da pesquisa",
      addBlock: "Adicionar bloco",
      addRandomizer: "Adicionar randomizador",
      blockPlaceholder: "Bloco sem título",
      deleteBlock: "Excluir bloco",
      confirmDeleteBlock: "Excluir este bloco? Suas perguntas ficarão sem atribuição.",
      randomizer: "Randomizador",
      randomizerShow: "Mostrar",
      randomizerOf: "de",
      addVariant: "Adicionar variante",
      questionsCount: "{n} pergunta(s)",
      unassigned: "Perguntas não atribuídas",
      noBlocks: "Sem blocos ainda",
      renameBlock: "Renomear",
      addQuestion: "Adicionar",
      questionSaved: "Pergunta salva com sucesso",
      questionDeleted: "Pergunta excluída",
      deleteError: "Falha ao excluir",
      reorderError: "Falha ao reordenar",
      confirmDelete: "Excluir esta pergunta?",
      deleteSurvey: "Excluir pesquisa",
      confirmDeleteSurvey: "Tem certeza de que deseja excluir esta pesquisa? Esta ação não pode ser desfeita.",
      surveyDeleted: "Pesquisa excluída com sucesso",
      publishCopied: "Publicado e link copiado",
      publishSuccess: "Pesquisa publicada com sucesso",
      publishError: "Falha ao publicar",
      linkCopied: "Link copiado para a área de transferência",
      questions: "Perguntas",
      noQuestions: "Nenhuma pergunta ainda",
      clickToAdd: "Clique no botão abaixo para adicionar sua primeira pergunta",
      addFirstQuestion: "Adicionar pergunta",
      untitledQuestion: "(Pergunta sem título)",
      promptPlaceholder: "Digite sua pergunta aqui...",
      surveyTitle: "Título da pesquisa",
      surveyDescription: "Descrição da pesquisa",
      noDescription: "Sem descrição",
      questionType: "Tipo de pergunta",
      variableName: "Nome da variável",
      variableHelp: "Use snake_case; deve ser único",
      requiredQuestion: "Pergunta obrigatória",
      isConversational: "Perguntar de forma conversacional",
      askForm: "Perguntar via formulário",
      optionsPlaceholder: "Opção 1\nOpção 2\nOpção 3",
      minValue: "Valor mínimo",
      maxValue: "Valor máximo",
      saveChanges: "Salvar alterações",
      moreOptions: "mais opções",
      moveUp: "Mover para cima",
      moveDown: "Mover para baixo",
      expand: "Expandir",
      collapse: "Recolher",
      delete: "Excluir",
      republish: "Republicar",
      unpublish: "Despublicar",
      unpublishing: "Despublicando…",
      unpublishSuccess: "Pesquisa despublicada. Agora é um rascunho.",
      unpublishError: "Falha ao despublicar a pesquisa",
      unpublishTooltip: "Bloquear novos participantes. As respostas existentes são mantidas; o link de compartilhamento é preservado para republicar.",
      confirmUnpublish: "Despublicar esta pesquisa? Novos participantes serão bloqueados. As respostas existentes são mantidas. Você pode republicar mais tarde com o mesmo link.",
      copyLink: "Copiar link de compartilhamento",
      meta: "Informações da pesquisa",
      titleField: "Título",
      descriptionField: "Descrição",
      descriptionPlaceholder: "Descreva o propósito desta pesquisa...",
      language: "Idioma",
      save: "Salvar",
      imageUrl: "URL da imagem",
      imageUrlHelp: "Opcional: insira uma URL para exibir uma imagem",
      imagePreview: "Pré-visualizar",
      imageLoadError: "Falha ao carregar imagem. Verifique o URL.",
      addImage: "Adicionar imagem",
      questionImage: "Imagem da pergunta",
      removeImage: "Remover imagem",
      uploadImage: "Enviar imagem",
      uploading: "Enviando…",
      imageUrlPlaceholder: "Ou cole a URL da imagem",
      imageHelp: "PNG, JPEG, GIF ou WebP. Máx. 2 MB.",
      imageUploadFailed: "Falha no envio.",
      uploadImages: "Enviar imagens",
      addMoreImages: "Adicionar mais",
      urlPasteHint: "Pressione Enter para adicionar",
      removeAllImages: "Remover tudo",
      itemsToRank: "Itens para classificar",
      itemsToRankHelp: "Insira os itens a serem classificados, um por linha",
      bipolarLabels: "Rótulos da escala",
      leftLabel: "Rótulo esquerdo",
      rightLabel: "Rótulo direito",
      surveyLanguage: "Idioma da pesquisa",
      surveyLanguageHelp: "Selecione 'Flexível' para permitir que os participantes escolham o idioma",
      flexible: "Flexível (O participante escolhe)",
      publish: "Publicar e copiar link",
      editor: "Editor de perguntas",
      selectPrompt: "Selecione uma pergunta para editar",
      prompt: "Texto da pergunta",
      variable: "Nome da variável",
      required: "Obrigatório",
      options: "Opções (uma por linha)",
      optionsHelp: "Insira uma opção por linha",
      min: "Mín",
      max: "Máx",
      saveQuestionBtn: "Salvar pergunta",
      defaultPrompt: "Nova pergunta",
    },
    languages: { en: "Inglês", zh: "Chinês", fr: "Francês", de: "Alemão", es: "Espanhol", nb: "Norueguês", nl: "Holandês", pt: "Português", ja: "Japonês", ar: "Árabe", flexible: "Flexível (O participante escolhe)" },
    synthetic: {
      dialogTitle: "Testar com participante sintético", dialogSubtitle: "Configure o perfil demográfico do seu participante simulado",
      age: "Idade", gender: "Gênero", male: "Masculino", female: "Feminino", nonBinary: "Não-binário", preferNotToSay: "Prefiro não dizer",
      nationality: "Nacionalidade", ethnicity: "Etnia", education: "Educação",
      highSchool: "Ensino médio", bachelors: "Bacharelado", masters: "Mestrado", phd: "Doutorado", vocational: "Profissionalizante", other: "Outro",
      occupation: "Profissão", personality: "Traços de personalidade", additionalContext: "Contexto adicional (opcional)",
      runTest: "Executar teste", cancel: "Cancelar",
      simulating: "Simulando respostas do participante...", simulatingHint: "Isso pode levar 15-30 segundos",
      resultsTitle: "Resultados do teste sintético", syntheticPersona: "Perfil do participante sintético", synthetic: "Sintético",
      question: "Pergunta", type: "Tipo", answer: "Resposta", close: "Fechar",
    },
    status: { draft: "Rascunho", published: "Publicado", archived: "Arquivado" },
    start: { untitledSurvey: "Pesquisa sem título", timeEstimate: "Cerca de {{minutes}} {{unit}}", questionCount: "{{count}} {{unit}}", minute: "minuto", minutes: "minutos", question: "pergunta", questions: "perguntas",
      minute: "minuto",
      minutes: "minutos",
      question: "pergunta",
      questions: "perguntas", privacyNote: "Suas respostas são confidenciais", startButton: "Iniciar pesquisa", selectLanguageFirst: "Selecione um idioma para continuar.", title: "Iniciar pesquisa", subtitle: "Inicie a pesquisa conversacional.", button: "Iniciar", loading: "Iniciando pesquisa...", error: "Não foi possível iniciar a sessão", selectLanguage: "Selecione seu idioma", selectLanguageSubtitle: "Escolha o idioma para sua sessão de pesquisa", surveyFull: "Esta pesquisa atingiu seu limite de respostas" },
    session: {
      dwellWait: "Você pode continuar em {{n}}s", redirectingIn: "Redirecionando em {seconds}…", continueNow: "Continuar agora", cancelRedirect: "Cancelar", redirectCancelled: "Redirecionamento automático cancelado.", continueToSite: "Continuar para o site", loadError: "Falha ao carregar sessão", connected: "Conectado ao assistente", wsError: "Erro de conexão de chat", answersSaved: "Respostas salvas", saveError: "Erro ao salvar", submitted: "Enviado", submitError: "Erro ao enviar", progress: "Progresso: {{answered}} / {{total}} obrigatórias", placeholder: "Digite sua resposta", send: "Enviar", verification: "Verificação", required: "Obrigatório", saveEdits: "Salvar edições", submit: "Aprovar e enviar", loading: "Carregando...", processing: "Processando sua resposta...", stopRecording: "Parar gravação", pressToSpeak: "Pressione para falar", reviewSubmit: "Revisar e enviar", answerMore: "Responda mais {{count}} pergunta(s) obrigatória(s)", transcribeFailed: "Não foi possível transcrever o áudio. Tente novamente.", audioProcessError: "Falha no processamento de áudio: {{error}}", micError: "Não é possível acessar o microfone: {{error}}", rankingPlaceholder: "Insira a ordem de classificação (ex.: A > B > C)", thankYouTitle: "Obrigado!", thankYouMessage: "Suas respostas foram enviadas com sucesso.", returnHome: "Voltar ao início", previewBanner: "[MODO DE PRÉ-VISUALIZAÇÃO] Esta é uma pré-visualização. As respostas não serão salvas nas análises.", redirecting: "Redirecionando...", progressLabel: "Progresso", autoSaving: "Salvando…", autoSaved: "Salvo", saveAndContinue: "Salvar e continuar depois", saveAndContinueTooltip: "Salve seu progresso e receba um link para continuar depois", saveAndContinueTitle: "Salve seu progresso", saveAndContinueBody: "Suas respostas foram salvas. Salve este link nos favoritos ou envie-o por e-mail para continuar de onde parou — em qualquer dispositivo.", copyLink: "Copiar link", linkCopied: "Link copiado para a área de transferência", close: "Fechar", alreadySubmittedWarning: "Nossos registros mostram que você pode já ter enviado esta pesquisa. Você ainda pode continuar, mas respostas duplicadas podem ser excluídas.", connectionError: "Erro de conexão", reconnecting: "Reconectando ao assistente...", connectionLost: "Conexão perdida. Atualize a página.", typeHere: "Digite sua resposta aqui...", send: "Enviar", formMode: "Formulário padrão", conversationalMode: "Entrevista IA", voicePlayback: "Voz", switchToForm: "Mudar para formulário", switchToConversational: "Mudar para entrevista IA", status_in_progress: "Em andamento", pageOf: "Página {current} de {total}", nextPage: "Próximo", previousPage: "Voltar", status_submitted: "Enviado", status_awaiting_verification: "Em revisão", requiredField: "obrigatório", fieldRequired: "{label} é obrigatório", invalidEmail: "Insira um endereço de e-mail válido", invalidPhone: "Insira um número de telefone válido", invalidNumber: "Insira um número válido", sumMustEqual: "Os valores devem somar {total} (atual: {sum})", npsNotLikely: "Nada provável", npsExtremely: "Extremamente provável", chooseFile: "Escolher arquivo", chooseFileDrag: "Escolha um arquivo ou arraste e solte", fileSelected: "Arquivo selecionado", selectedPrefix: "Selecionado:", acceptedPrefix: "Aceito:", fileTooLarge: "O tamanho do arquivo deve ser inferior a {size} MB", constantSumInstruction: "Distribua um total de {total} pontos entre os itens abaixo.", remaining: "Restante:", pageBreakLabel: "Quebra de página", noFormConfigured: "Nenhum formulário configurado", noFormConfiguredBody: "O proprietário do projeto ainda não configurou um formulário personalizado.", unsupportedField: "Tipo de campo não suportado: {type}", imageNotConfigured: "URL da imagem não configurada", videoNotConfigured: "URL do vídeo não configurada", dragToReorder: "Arraste para reordenar", moveUp: "Mover para cima", moveDown: "Mover para baixo", requiredSummary: "Responda à(s) pergunta(s) destacada(s) antes de continuar.", yes: "Sim", no: "Não", optionN: "Opção {n}", surveyImageAlt: "Informações da pesquisa", interview: { title: "Entrevista IA", description: "Esta pergunta inclui uma breve entrevista IA.", start: "Iniciar entrevista", starting: "Iniciando entrevista...", ending: "Salvando entrevista...", completed: "Entrevista concluída", end: "Encerrar", stopRecording: "Parar gravação", tapToSpeak: "Toque para falar", typePlaceholder: "Digite sua resposta..." } },
    export: { loadError: "Erro ao carregar pesquisa", description: "Baixar respostas enviadas como CSV.", button: "Baixar CSV", success: "Exportação pronta", error: "Erro de exportação", xlsx: "Baixar XLSX" },
    analytics: { filterScopeNote: "Este filtro de datas limita apenas a lista de respostas e as tabelas cruzadas. Os cartões de resumo, os gráficos e o resumo de IA sempre refletem todas as respostas.", clearFilter: 'Limpar filtro', columnQuestion: 'Pergunta da coluna', crossTab: 'Tabela cruzada', crossTabHelp: 'Compare como as respostas de uma pergunta se distribuem segundo outra.', crossTabNeedTwo: 'Escolha duas perguntas para comparar.', crossTabPickTwo: 'Selecione uma pergunta de linha e de coluna', crossTabTitle: 'Tabulação cruzada', duration: 'Duração', filterBy: 'Filtrar por', from: 'De', individualResponses: 'Respostas individuais', language: 'Idioma', next: 'Próximo', noAnswer: 'Sem resposta', noResponsesYet: 'Ainda sem respostas', deleteResponse: 'Excluir resposta', confirmDeleteResponse: 'Excluir esta resposta permanentemente? Esta ação não pode ser desfeita.', responseDeleted: 'Resposta excluída.', deleteFailed: 'Falha ao excluir a resposta.', download: 'Baixar', downloadCsv: 'Baixar CSV', downloadXlsx: 'Baixar Excel (XLSX)', exportFailed: 'Falha na exportação.', prev: 'Anterior', respondent: 'Respondente', rowQuestion: 'Pergunta da linha', sampleResponses: 'Exemplos de respostas', submittedAt: 'Enviado em', to: 'Até', viewResponses: 'Ver respostas', wordCloud: 'Nuvem de palavras', title: "Analítica da pesquisa", totalResponses: "Total de respostas", completionRate: "Taxa de conclusão", avgDuration: "Duração média", minutes: "minutos", responsesOverTime: "Respostas ao longo do tempo", questionBreakdown: "Análise por pergunta", responses: "respostas", noData: "Nenhuma resposta ainda", refresh: "Atualizar", detractors: "Detratores (0-6)", passives: "Passivos (7-8)", promoters: "Promotores (9-10)" }
  },
  ja: {
    tutorial: {
      title: "はじめに",
      step1Title: "アンケートビルダーへようこそ",
      step1Body: "AI が的確なフォローアップ質問を投げかけ、回答者ごとに適応する対話型アンケートを作成できます — 静的なフォームよりはるかに豊かです。",
      step2Title: "質問を追加する",
      step2Body: "選択式、尺度、自由記述など、あらゆる種類の質問を追加し、ドラッグして並べ替えられます。改ページやセクションを使って流れを設計しましょう。",
      step3Title: "AI を設定する",
      step3Body: "言語やトーン、自由回答に対して AI がどこまで深く掘り下げて追問するかを設定できます。アンケート全体を自動翻訳することも可能です。",
      step4Title: "共有して分析する",
      step4Body: "公開してリンクを共有すると、参加者がアンケートを完了するにつれて回答と分析が届くのを確認できます。",
      back: "戻る",
      next: "次へ",
      done: "はじめる",
      skip: "スキップ",
      help: "使い方",
    },
    import: {
      menuLabel: "ファイルをインポート", reviewTitle: "インポートしたアンケートを確認",
      reviewIntro: "ファイルから {{n}} 件の設問を作成しました。下で確認してからアンケートを作成してください。ビルダーで自由に編集できます。",
      cancel: "キャンセル", titleField: "アンケートのタイトル", descriptionField: "説明",
      reportTitle: "インポートメモ", preview: "設問", pageBreak: "改ページ",
      sectionBreak: "セクション", required: "必須", create: "アンケートを作成",
      parseFailed: "このファイルをインポートできませんでした。", createFailed: "アンケートを作成できませんでした。",
      uploadTitle: "アンケートをインポート", parsing: "ファイルを読み込んでいます…",
      dropHere: "ファイルをここにドラッグ、またはクリックして選択",
      dropHint: "最大 25 MB。確認・編集できる下書きを作成します。",
      supported: "対応形式",
      formatNote: "AI が Word と PDF をアンケートに変換します。既存のアンケートはそのまま取り込み、メモや概要からは新たに作成します。Qualtrics .qsf と QualiTaTi .qlt は正確に解析されます。",
      exportDefinition: "アンケートをダウンロード", exportWord: "Word (.docx) でダウンロード", exportQlt: "QualiTaTi (.qlt) でダウンロード", exportFailed: "エクスポートに失敗しました。",
    },
    translations: {
      menuLabel: "翻訳", title: "アンケートの翻訳",
      intro: "言語を追加すると AI がアンケート全体を自動翻訳します。その後、各テキストを確認・編集できます。回答者は開始時に言語を選びます。空欄はデフォルト言語（{{lang}}）にフォールバックします。",
      source: "原文", addLanguage: "言語を追加",
      empty: "まだ言語がありません。「言語を追加」をクリックして AI 翻訳を作成してください。",
      surveyInfo: "アンケート情報", fieldTitle: "タイトル", fieldDescription: "説明",
      fieldThankYouTitle: "お礼のタイトル", fieldThankYouMessage: "お礼のメッセージ",
      fieldPrompt: "設問", options: "選択肢", columns: "尺度の列",
      retranslate: "再翻訳", close: "閉じる", save: "保存",
      translated: "自動翻訳が完了しました。下で確認・編集してください。", translateFailed: "翻訳に失敗しました。",
      saved: "翻訳を保存しました。", saveFailed: "保存に失敗しました。",
      confirmDelete: "この言語とすべての翻訳を削除しますか？", deleteFailed: "削除に失敗しました。",
      confirmRetranslate: "最初から再翻訳しますか？この言語の編集内容は上書きされます。",
      loadFailed: "翻訳の読み込みに失敗しました。",
    },
    hub: {
      emptyTitle: "最初の調査を作成",
      emptyBody: "ゼロから作成、AI で生成、テンプレートを選択、または Word、PDF、Qualtrics ファイルをインポートできます。",
      emptyNoResults: "フィルターに一致する調査はありません。",
      clearFilters: "フィルターをクリア",
      title: "調査",
      createProject: "調査プロジェクトを作成",
      manageSurveys: "調査を管理",
      createDialogTitle: "新規調査プロジェクト",
      projectName: "プロジェクト名",
      projectOutline: "プロジェクト概要",
      interviewType: "インタビュータイプ",
      interviewLanguage: "インタビュー言語",
      cancel: "キャンセル",
      save: "保存",
      createSuccess: "プロジェクトを作成しました",
      createError: "プロジェクトの作成に失敗しました",
      fillRequired: "必須フィールドを入力してください",
      loadError: "プロジェクトの読み込みに失敗しました",
      confirmDeleteProject: "この調査プロジェクトとすべての調査を削除しますか？この操作は元に戻せません。",
      projectDeleted: "プロジェクトが正常に削除されました",
      surveysInProject: "このプロジェクトの調査",
      noSurveysYet: "まだ調査はありません",
      manageProjectSurveys: "調査を管理",
      noProjectsYet: "調査プロジェクトはまだ作成されていません",
      experimentalTitle: "実験的機能",
      experimentalMessage: "会話型調査は現在積極的に開発中の実験的ツールです。",
      search: "調査を検索",
      filterAll: "すべて",
      filterDraft: "下書き",
      filterPublished: "公開済み",
      sortNewest: "新しい順",
      sortAlpha: "アルファベット順",
      noResults: "検索に一致する調査はありません",
      analytics: "分析",
      duplicate: "複製",
      duplicateSuccess: "アンケートを複製しました",
    },
    list: {
      emptyTitle: "このプロジェクトにはまだ調査がありません",
      emptyBody: "最初の調査を作成して回答の収集を始めましょう。",
      fromTemplate: "テンプレートから始める",
      fromWizard: "AI で生成",
      fromTemplateTitle: "テンプレートから始める",
      fromTemplateSubtitle: "テンプレートを選ぶと編集可能な下書きが得られます。質問、AI フォローアップ、適切な既定モードを事前に設定します。",
      templatesEmpty: "現在利用できるテンプレートはありません。",
      creatingFromTemplate: "作成中…",
      wizard: {
        title: "AI でアンケートを生成",
        subtitle: "Mimi に学びたいことを伝えれば、編集可能な下書きを作成します。",
        generating: "Mimi がアンケートを下書きしています…",
        generatingHint: "通常 20〜40 秒かかります。適切な AI フォローアップ設定で質問セットを生成しています。",
        goalLabel: "何を知りたいですか？",
        goalPlaceholder: "例：初月以降に顧客が解約する理由——何が決め手か、何があれば防げたか。",
        goalHelp: "具体的な目的ほど良いアンケートになります。1〜3 文を目安に。",
        audienceLabel: "誰が回答しますか？",
        audiencePlaceholder: "例：過去 30 日以内に解約した顧客",
        languageLabel: "言語",
        depthLabel: "深さ",
        nQuestionsLabel: "目標の質問数",
        generate: "生成",
        errorGoalTooShort: "AI が下書きを作れるよう、目的は少なくとも 10 文字必要です。",
        errorAudienceTooShort: "AI が質問を適切に組み立てられるよう、対象を教えてください。",
      },
      title: "調査",
      create: "調査を作成",
      createDialogTitle: "新規調査",
      titleField: "タイトル",
      descriptionField: "説明",
      cancel: "キャンセル",
      save: "保存",
      builder: "ビルダー",
      export: "エクスポート",
      publish: "公開",
      status: "ステータス",
      linkCopied: "共有リンクをコピーしました",
      publishFirst: "まず調査を公開してください",
      publishReady: "調査を公開しました",
      publishError: "公開に失敗しました",
      createSuccess: "調査を作成しました",
      createError: "作成に失敗しました",
      exportReady: "CSVの準備ができました",
      exportError: "エクスポートに失敗しました",
      error: "調査の読み込みに失敗しました",
      copyLink: "共有リンクをコピー",
      downloadCsv: "CSVをダウンロード",
      deleteSurvey: "調査を削除",
      publishGetLink: "公開してリンクを取得",
      published: "調査を公開しました — 共有リンクの準備ができました。",
      questionsLabel: "問",
      responsesLabel: "件の回答"
    },
    builder: {
      minDwellLabel: "最小表示時間（秒）",
      minDwellHelp: "回答者は「次へ/送信」が有効になるまで、この秒数だけこのページに留まる必要があります。0 = 無効。",
      saving: "保存中…",
      saved: "保存しました",
      saveFailed: "変更を保存できませんでした",
      aiFollowUp: {
        label: "AI フォローアップ",
        helperNone: "フォローアップなし — 回答者は答えて次へ進みます",
        helperLight: "Light — 回答を深めるために 1 回の対話型フォローアップ",
        helperDeep: "Deep — 質的に深く掘り下げるため最大 3 回のフォローアップ",
        optionNone: "なし — フォローアップなし",
        optionLight: "Light — 1 回",
        optionDeep: "Deep — 最大 3 回",
      },
      cancel: "キャンセル",
      surveyMode: {
        label: "アンケートモード",
        form: {
          title: "従来型アンケート",
          subtitle: "標準的なフォーム式アンケート（AI なし）",
        },
        aiFollowUp: {
          title: "AI フォローアップ付きアンケート",
          subtitle: "フォーム + 自由回答への AI による追加質問",
          hint: "ヒント: 各自由回答の質問に「AI フォローアップ」の強度を設定してください。Light = 1 回、Deep = 最大 3 回。",
        },
        aiInterview: {
          title: "対話型アンケート",
          subtitle: "AI 主導の対話型インタビュー、自由な対話",
        },
      },
      preflight: {
        title: "公開前チェック",
        loading: "アンケートを確認しています…",
        ready: "公開準備完了",
        readyHint: "全てのチェックに合格しました。回答者はスムーズに体験できます。",
        hasErrors: "公開前に以下の問題を修正してください:",
        warningsOnly: "公開できますが、念のため以下をご確認ください:",
        fallback: "ヘルスチェックを実行できませんでした — そのまま公開できます。",
        rerun: "再チェック",
      },
      pipingChip: '過去の回答を挿入',
      pipingTooltipTitle: 'この質問に過去の回答を表示',
      pipingTooltipBody: 'タグをクリックしてコピーし、質問文に貼り付けます。回答者が見るときには、その人の過去の回答に置き換わります。',
      constantSumItems: '項目',
      constantSumItemsHelp: '1行に1項目',
      constantSumTotal: '合計',
      constantSumTotalHelp: '回答者が割り振る合計値（例：100）',
      emailAutoConfig: 'メールアドレスとして検証します',
      fileAcceptHelp: '許可するファイル形式（カンマ区切り、例：.pdf,.docx）',
      fileAcceptTypes: '受付形式',
      fileMaxSize: '最大ファイルサイズ（MB）',
      imageChoiceHelp: '1行に1つの画像URL。任意のラベルは順番で対応します。',
      imageChoiceLabels: 'ラベル',
      imageChoiceMultiple: '複数選択を許可',
      imageChoiceUrls: '画像URL',
      phoneAutoConfig: '電話番号として検証します',
      ratingMax: '最大評価',
      sectionBreakInfo: '視覚的な区切りのみ — 回答は収集されません。',
      thankYouPage: 'サンクスページ',
      thankYouRedirectHelp: '送信後にリダイレクトする任意のURL',
      yesNoAutoConfig: 'はい / いいえ の選択',
      loadError: "調査の読み込みに失敗しました", saveMeta: "調査を更新しました", metaSaved: "詳細を保存しました", saveError: "保存に失敗しました", addError: "質問の追加に失敗しました", questionAdded: "質問を追加しました", saveQuestion: "質問を保存しました", duplicate: "複製", duplicateSuccess: "質問を複製しました", preview: "プレビュー", previewMode: "プレビューモード", moveToBlock: "ブロックに移動",
      types: {
        image: "画像",
        short_text: "短い回答", long_text: "段落", single_choice: "選択式", multi_choice: "チェックボックス", number: "数値", date: "日付", ranking: "ランキング", bipolar_scale: "二極尺度", info_text: "情報テキスト",
        slider: "スライダー", dropdown: "ドロップダウン", nps: "ネットプロモータースコア", matrix: "マトリクス",
        ai_interview: "AIインタビュー",
        rating_scale: "評価スケール",
        yes_no: "はい / いいえ",
        email: "メールアドレス",
        phone: "電話番号",
        image_choice: "画像選択",
        file_upload: "ファイルアップロード",
        constant_sum: "定数合計",
        section_break: "セクション区切り",
        page_break: "改ページ"
      },
      typeDescriptions: {
        short_text: "短いテキスト回答", long_text: "長いテキスト回答", single_choice: "1つのオプションを選択", multi_choice: "複数のオプションを選択", number: "数値", date: "日付選択", ranking: "項目を順序づけ", bipolar_scale: "2つの対極間の尺度", info_text: "テキストや画像を表示（回答不要）",
        slider: "スライドして範囲内の値を選択", dropdown: "ドロップダウンリストから1つの選択肢を選ぶ", nps: "0から10で評価（ネットプロモータースコア）", matrix: "同じ尺度で複数の項目を評価",
        ai_interview: "AI駆動のインタビュー会話を埋め込む",
        rating_scale: "星評価（例：1〜5つ星）",
        yes_no: "シンプルなはい/いいえの質問",
        email: "バリデーション付きメールアドレス",
        phone: "バリデーション付き電話番号",
        image_choice: "画像オプションから選択",
        file_upload: "ファイル添付をアップロード",
        constant_sum: "項目間でポイントを配分",
        section_break: "セクション間の視覚的区切り",
        page_break: "回答者向けに新しいページを開始します"
      },
      stepValue: "ステップ", npsAutoConfig: "NPSは0-10の尺度として自動的に設定されます。追加の設定は不要です。", matrixRows: "行ラベル（1行に1つ）", matrixRowsHelp: "評価する項目を1行に1つずつ入力してください", matrixColumns: "列ラベル（1行に1つ）", matrixColumnsHelp: "尺度のオプションを1行に1つずつ入力してください",
      interviewConfig: {
        title: "AIインタビュー設定",
        interactionMode: "インタラクションモード",
        voiceToVoice: "音声から音声",
        voiceToText: "音声からテキスト",
        textToText: "テキストからテキスト",
        textToVoice: "テキストから音声",
        language: "インタビュー言語",
        maxDuration: "最大時間（分）",
        outline: "インタビュー概要 / プロンプト",
        outlinePlaceholder: "AIが質問すべき内容を記述してください...",
        outlineHelp: "AIインタビュアーはこの概要に従って会話を導きます。",
      },
      conditions: "条件",
      addCondition: "条件を追加",
      removeCondition: "削除",
      conditionLogicAnd: "かつ（すべて一致）",
      conditionLogicOr: "または（いずれか一致）",
      conditionQuestion: "質問が",
      conditionOperator: "演算子",
      conditionValue: "値",
      operatorEquals: "等しい",
      operatorNotEquals: "等しくない",
      operatorContains: "含む",
      operatorGreaterThan: "より大きい",
      operatorLessThan: "より小さい",
      operatorIsEmpty: "空である",
      operatorIsNotEmpty: "空でない",
      pipingHelp: "ヒント：${variable_name} を使用して前の回答を挿入できます",
      availableVariables: "利用可能：{{variables}}",
      // Share & Distribution (Phase 6)
      share: "共有",
      shareTitle: "アンケートを共有",
      tabLink: "リンク / QR",
      tabEmbed: "埋め込み",
      tabSocial: "ソーシャルメディア",
      tabProtect: "保護",
      copy: "コピー",
      copied: "コピーしました",
      linkCopied: "リンクをコピーしました",
      qrHint: "電話カメラでスキャンしてアンケートを開く",
      downloadQR: "QRコードをダウンロード (PNG)",
      embedHelp: "このHTMLをウェブサイトに貼り付けてアンケートを埋め込む",
      copyEmbed: "埋め込みコードをコピー",
      embedCopied: "埋め込みコードをコピーしました",
      preview: "プレビュー",
      socialHelp: "ソーシャルプラットフォームでアンケートを共有：",
      shareOn: "共有する",
      passwordProtection: "パスワード保護",
      passwordHint: "回答者がアンケートを開始する前にパスワードを入力するように要求します",
      passwordActive: "パスワードが設定されています。下記で新しいパスワードを入力して変更するか、空欄にして削除します",
      passwordPlaceholder: "パスワードなしの場合は空欄のままにしてください",
      passwordSaved: "パスワードを更新しました",
      passwordRemoved: "パスワードを削除しました",
      removePassword: "パスワードを削除",
      apply: "適用",
      schedule: "スケジュール",
      scheduleHelp: "指定した時間に自動的にアンケートを開閉します。常に開いている場合は空欄のままにしてください",
      openAt: "開く時間",
      closeAt: "閉じる時間",
      saveSchedule: "スケジュールを保存",
      scheduleSaved: "スケジュールを保存しました",
      saveError: "保存に失敗しました",
      // Theming (Phase 5)
      themeBranding: "テーマとブランディング",
      themeBrandingHelp: "回答者が見るアンケートページの外観をカスタマイズします",
      themePresets: "クイックプリセット",
      primaryColor: "プライマリカラー",
      backgroundColor: "背景",
      textColor: "テキストの色",
      fontFamily: "フォントスタイル",
      fontSystem: "システムデフォルト（モダン）",
      fontSerif: "セリフ（クラシック）",
      fontRounded: "丸型（フレンドリー）",
      fontMono: "等幅（テクニカル）",
      logoUrl: "ロゴ画像URL",
      logoUrlHelp: "アンケートタイトルの上に表示されます。オプションです",
      headerImageUrl: "ヘッダーバナーURL",
      headerImageUrlHelp: "上部の広いバナー画像。オプションです",
      themePreview: "プレビュー",
      sampleSurveyTitle: "アンケートのタイトル",
      sampleQuestionText: "質問の外観です",
      sampleSubmit: "送信",
      resetTheme: "デフォルトにリセット",
      randomizeQuestions: "質問順序をランダム化",
      randomizeOptions: "選択肢の順序をランダム化",
      thankYouSettings: "お礼ページ",
      thankYouTitle: "タイトル",
      thankYouMessage: "メッセージ",
      thankYouRedirect: "リダイレクトURL（任意）",
      maxResponses: "最大回答数（任意）",
      maxResponsesHelp: "無制限にするには空欄のまま",
      deliveryMode: "配信モード",
      deliveryConversational: "会話型（AIインタビュー）",
      deliveryForm: "標準フォーム",
      deliveryModeHelp: "会話モードはAIを使用してインタビューを行います。標準フォームモードはすべての質問を一度に表示します。",
      testWithSynthetic: "合成人間でテスト",
      fromLibrary: "ライブラリから",
      fromLibraryHint: "定義済みのテンプレートとバンドルからコピー",
      templateLibrary: "テンプレートライブラリ",
      templates: "テンプレート",
      bundles: "バンドル",
      allTemplates: "すべてのテンプレート",
      myTemplates: "マイテンプレート",
      allCategories: "すべてのカテゴリ",
      selectTemplate: "テンプレートを選択",
      templateName: "テンプレート名",
      category: "カテゴリ",
      search: "検索",
      preview: "プレビュー",
      inserting: "挿入中...",
      insertTemplate: "テンプレートを挿入",
      insertBundle: "バンドルを挿入",
      noTemplates: "テンプレートが見つかりません",
      noBundles: "バンドルが見つかりません",
      templateInsertedSuccess: "テンプレートが挿入されました",
      bundleInsertedSuccess: "バンドルが挿入されました",
      templateInsertError: "テンプレートの挿入に失敗しました",
      cancel: "キャンセル",
      skipLogic: "スキップロジック",
      skipLogicHelp: "条件が満たされた場合にのみこの質問を表示",
      displayLogic: "表示ロジック",
      displayLogicHelp: "前の回答がこれらの条件と一致する場合にのみこの質問を表示",
      conditionIf: "もし",
      logicTab: "ロジック",
      noLogicYet: "スキップロジックは設定されていません",
      noLogicHint: "質問をクリックし、スキップロジックセクションまでスクロールして条件を追加してください。",
      logicSummaryPrefix: "この質問を表示する条件",
      settings: "アンケート設定",
      surveyFlow: "サーベイフロー",
      addBlock: "ブロックを追加",
      addRandomizer: "ランダマイザーを追加",
      blockPlaceholder: "無題のブロック",
      deleteBlock: "ブロックを削除",
      confirmDeleteBlock: "このブロックを削除しますか？質問は未割り当てになります。",
      randomizer: "ランダマイザー",
      randomizerShow: "表示",
      randomizerOf: "件中",
      addVariant: "バリアントを追加",
      questionsCount: "{n}問",
      unassigned: "未割り当ての質問",
      noBlocks: "ブロックがありません",
      renameBlock: "名前変更",
      addQuestion: "追加",
      questionSaved: "質問が正常に保存されました",
      questionDeleted: "質問を削除しました",
      deleteError: "削除に失敗しました",
      reorderError: "並び替えに失敗しました",
      confirmDelete: "この質問を削除しますか？",
      deleteSurvey: "調査を削除",
      confirmDeleteSurvey: "この調査を削除してもよろしいですか？この操作は元に戻せません。",
      surveyDeleted: "調査が正常に削除されました",
      publishCopied: "公開してリンクをコピーしました",
      publishSuccess: "調査が正常に公開されました",
      publishError: "公開に失敗しました",
      linkCopied: "リンクをクリップボードにコピーしました",
      questions: "質問",
      noQuestions: "質問がありません",
      clickToAdd: "下のボタンをクリックして最初の質問を追加してください",
      addFirstQuestion: "質問を追加",
      untitledQuestion: "（タイトルなしの質問）",
      promptPlaceholder: "質問を入力してください...",
      surveyTitle: "調査タイトル",
      surveyDescription: "調査の説明",
      noDescription: "説明なし",
      questionType: "質問タイプ",
      variableName: "変数名",
      variableHelp: "snake_case; 一意である必要があります",
      requiredQuestion: "必須の質問",
      isConversational: "会話形式で質問",
      askForm: "フォームで質問",
      optionsPlaceholder: "選択肢1\n選択肢2\n選択肢3",
      minValue: "最小値",
      maxValue: "最大値",
      saveChanges: "変更を保存",
      moreOptions: "その他のオプション",
      moveUp: "上に移動",
      moveDown: "下に移動",
      expand: "展開",
      collapse: "折りたたむ",
      delete: "削除",
      republish: "再公開",
      unpublish: "公開停止",
      unpublishing: "公開停止中…",
      unpublishSuccess: "アンケートを公開停止しました。下書き状態に戻ります。",
      unpublishError: "公開停止に失敗しました",
      unpublishTooltip: "新規参加者をブロックします。既存の回答は保持され、共有リンクは再公開のために維持されます。",
      confirmUnpublish: "このアンケートを公開停止しますか？新規参加者はブロックされます。既存の回答は保持され、後で同じリンクで再公開できます。",
      copyLink: "共有リンクをコピー",
      meta: "調査情報",
      titleField: "タイトル",
      descriptionField: "説明",
      descriptionPlaceholder: "この調査の目的を説明してください...",
      language: "言語",
      save: "保存",
      imageUrl: "画像URL",
      imageUrlHelp: "任意：画像を表示するURLを入力してください",
      imagePreview: "プレビュー",
      imageLoadError: "画像の読み込みに失敗しました。URLを確認してください。",
      addImage: "画像を追加",
      questionImage: "質問の画像",
      removeImage: "画像を削除",
      uploadImage: "画像をアップロード",
      uploading: "アップロード中…",
      imageUrlPlaceholder: "または画像URLを貼り付け",
      imageHelp: "PNG、JPEG、GIF、WebP。最大2MB。",
      imageUploadFailed: "アップロードに失敗しました。",
      uploadImages: "画像をアップロード",
      addMoreImages: "さらに追加",
      urlPasteHint: "Enterキーで追加",
      removeAllImages: "すべて削除",
      itemsToRank: "ランク付けする項目",
      itemsToRankHelp: "ランク付けする項目を1行に1つずつ入力してください",
      bipolarLabels: "尺度ラベル",
      leftLabel: "左ラベル",
      rightLabel: "右ラベル",
      surveyLanguage: "調査言語",
      surveyLanguageHelp: "「フレキシブル」を選択すると参加者が言語を選択できます",
      flexible: "フレキシブル（参加者が選択）",
      publish: "公開してリンクをコピー",
      editor: "質問エディター",
      selectPrompt: "編集する質問を選択してください",
      prompt: "質問テキスト",
      variable: "変数名",
      required: "必須",
      options: "選択肢（1行に1つ）",
      optionsHelp: "1行に1つの選択肢を入力してください",
      min: "最小",
      max: "最大",
      saveQuestionBtn: "質問を保存",
      defaultPrompt: "新しい質問",
    },
    languages: { en: "英語", zh: "中国語", fr: "フランス語", de: "ドイツ語", es: "スペイン語", nb: "ノルウェー語", nl: "オランダ語", pt: "ポルトガル語", ja: "日本語", ar: "アラビア語", flexible: "フレキシブル（参加者が選択）" },
    synthetic: {
      dialogTitle: "合成参加者でテスト", dialogSubtitle: "シミュレートされた参加者の人口統計プロフィールを設定",
      age: "年齢", gender: "性別", male: "男性", female: "女性", nonBinary: "ノンバイナリー", preferNotToSay: "回答しない",
      nationality: "国籍", ethnicity: "民族", education: "学歴",
      highSchool: "高校", bachelors: "学士", masters: "修士", phd: "博士", vocational: "専門学校", other: "その他",
      occupation: "職業", personality: "性格特性", additionalContext: "追加情報（任意）",
      runTest: "テスト実行", cancel: "キャンセル",
      simulating: "参加者の回答をシミュレート中...", simulatingHint: "15〜30秒かかる場合があります",
      resultsTitle: "合成テスト結果", syntheticPersona: "合成参加者プロフィール", synthetic: "合成",
      question: "質問", type: "タイプ", answer: "回答", close: "閉じる",
    },
    status: { draft: "下書き", published: "公開済み", archived: "アーカイブ済み" },
    start: { untitledSurvey: "無題のアンケート", timeEstimate: "約 {{minutes}} {{unit}}", questionCount: "{{count}} {{unit}}", minute: "分", minutes: "分", question: "問", questions: "問",
      minute: "分",
      minutes: "分",
      question: "問",
      questions: "問", privacyNote: "回答は秘密が守られます", startButton: "アンケートを開始", selectLanguageFirst: "続行するには言語を選択してください。", title: "調査を開始", subtitle: "会話型調査を開始します。", button: "開始", loading: "調査を開始中...", error: "セッションを開始できませんでした", selectLanguage: "言語を選択してください", selectLanguageSubtitle: "調査セッションの言語を選択してください", surveyFull: "このアンケートは回答数の上限に達しました" },
    session: {
      dwellWait: "{{n}} 秒後に続行できます", redirectingIn: "{seconds} 秒後にリダイレクトします…", continueNow: "今すぐ続行", cancelRedirect: "キャンセル", redirectCancelled: "自動リダイレクトをキャンセルしました。", continueToSite: "サイトへ進む", loadError: "セッションの読み込みに失敗しました", connected: "アシスタントに接続しました", wsError: "チャット接続エラー", answersSaved: "回答が保存されました", saveError: "保存エラー", submitted: "送信しました", submitError: "送信エラー", progress: "進捗: {{answered}} / {{total}} 必須", placeholder: "回答を入力してください", send: "送信", verification: "確認", required: "必須", saveEdits: "編集を保存", submit: "承認して送信", loading: "読み込み中...", processing: "回答を処理中...", stopRecording: "録音停止", pressToSpeak: "押して話す", reviewSubmit: "確認して送信", answerMore: "あと{{count}}つの必須質問に回答してください", transcribeFailed: "音声を文字起こしできませんでした。再試行してください。", audioProcessError: "音声処理に失敗しました: {{error}}", micError: "マイクにアクセスできません: {{error}}", rankingPlaceholder: "ランキング順序を入力してください（例：A > B > C）", thankYouTitle: "ありがとうございます！", thankYouMessage: "回答が正常に送信されました。", returnHome: "ホームに戻る", previewBanner: "[プレビューモード] これはプレビューです。回答は分析に保存されません。", redirecting: "リダイレクト中...", progressLabel: "進捗", autoSaving: "保存中…", autoSaved: "保存済み", saveAndContinue: "保存して後で続ける", saveAndContinueTooltip: "進捗を保存し、後で続けるためのリンクを取得します", saveAndContinueTitle: "進捗を保存する", saveAndContinueBody: "回答が保存されました。このリンクをブックマークするかメールで自分宛に送信すれば、どの端末からでも続きから再開できます。", copyLink: "リンクをコピー", linkCopied: "リンクをクリップボードにコピーしました", close: "閉じる", alreadySubmittedWarning: "当社の記録によると、このアンケートはすでに送信されている可能性があります。続行は可能ですが、重複した回答は除外されることがあります。", connectionError: "接続エラー", reconnecting: "アシスタントに再接続中...", connectionLost: "接続が切断されました。ページを更新してください。", typeHere: "ここに回答を入力してください...", send: "送信", formMode: "標準フォーム", conversationalMode: "AIインタビュー", voicePlayback: "音声", switchToForm: "フォームに切替", switchToConversational: "AIインタビューに切替", status_in_progress: "進行中", pageOf: "{current} / {total} ページ", nextPage: "次へ", previousPage: "戻る", status_submitted: "送信済み", status_awaiting_verification: "確認中", requiredField: "必須", fieldRequired: "{label} は必須です", invalidEmail: "有効なメールアドレスを入力してください", invalidPhone: "有効な電話番号を入力してください", invalidNumber: "有効な数値を入力してください", sumMustEqual: "合計が {total} になるように入力してください（現在：{sum}）", npsNotLikely: "まったく思わない", npsExtremely: "非常にそう思う", chooseFile: "ファイルを選択", chooseFileDrag: "ファイルを選択またはドラッグ＆ドロップ", fileSelected: "ファイルを選択しました", selectedPrefix: "選択済み：", acceptedPrefix: "対応形式：", fileTooLarge: "ファイルサイズは {size}MB 未満にしてください", constantSumInstruction: "以下の項目に合計 {total} ポイントを割り当ててください。", remaining: "残り：", pageBreakLabel: "改ページ", noFormConfigured: "フォームが設定されていません", noFormConfiguredBody: "プロジェクト所有者はまだカスタムフォームを設定していません。", unsupportedField: "サポートされていない項目タイプ：{type}", imageNotConfigured: "画像URLが設定されていません", videoNotConfigured: "動画URLが設定されていません", dragToReorder: "ドラッグして並べ替え", moveUp: "上へ移動", moveDown: "下へ移動", requiredSummary: "続行する前にハイライトされた質問にご回答ください。", yes: "はい", no: "いいえ", optionN: "選択肢 {n}", surveyImageAlt: "アンケート情報", interview: { title: "AIインタビュー", description: "この質問には短いAIインタビューが含まれています。", start: "インタビューを開始", starting: "インタビューを開始中...", ending: "インタビューを保存中...", completed: "インタビュー完了", end: "終了", stopRecording: "録音停止", tapToSpeak: "タップして話す", typePlaceholder: "回答を入力..." } },
    export: { loadError: "調査の読み込みエラー", description: "送信された回答をCSVとしてダウンロードします。", button: "CSVをダウンロード", success: "エクスポート準備完了", error: "エクスポートエラー", xlsx: "XLSXをダウンロード" },
    analytics: { filterScopeNote: "この日付フィルターは回答リストとクロス集計のみを絞り込みます。概要カード、グラフ、AI 要約は常にすべての回答を反映します。", clearFilter: 'フィルターをクリア', columnQuestion: '列の質問', crossTab: 'クロス集計', crossTabHelp: 'ある質問の回答が別の質問でどう分かれるかを比較します。', crossTabNeedTwo: '比較する2つの質問を選択してください。', crossTabPickTwo: '行と列の質問を選択してください', crossTabTitle: 'クロス集計表', duration: '所要時間', filterBy: '絞り込み', from: '開始', individualResponses: '個別の回答', language: '言語', next: '次へ', noAnswer: '回答なし', noResponsesYet: 'まだ回答がありません', deleteResponse: '回答を削除', confirmDeleteResponse: 'この回答を完全に削除しますか？この操作は元に戻せません。', responseDeleted: '回答を削除しました。', deleteFailed: '回答の削除に失敗しました。', download: 'ダウンロード', downloadCsv: 'CSV をダウンロード', downloadXlsx: 'Excel (XLSX) をダウンロード', exportFailed: 'エクスポートに失敗しました。', prev: '前へ', respondent: '回答者', rowQuestion: '行の質問', sampleResponses: '回答サンプル', submittedAt: '送信日時', to: '終了', viewResponses: '回答を表示', wordCloud: 'ワードクラウド', title: "調査分析", totalResponses: "総回答数", completionRate: "完了率", avgDuration: "平均所要時間", minutes: "分", responsesOverTime: "時系列の回答", questionBreakdown: "質問別分析", responses: "件の回答", noData: "まだ回答がありません", refresh: "更新", detractors: "批判者 (0-6)", passives: "中立者 (7-8)", promoters: "推奨者 (9-10)" }
  },
  ar: {
    tutorial: {
      title: "البدء",
      step1Title: "مرحبًا بك في منشئ الاستطلاعات",
      step1Body: "أنشئ استطلاعات حوارية يطرح فيها الذكاء الاصطناعي أسئلة متابعة ذكية ويتكيّف مع كل مشارك — أغنى بكثير من نموذج ثابت.",
      step2Title: "أضف أسئلتك",
      step2Body: "أضف أسئلة من أي نوع — اختيار من متعدد، مقاييس، نص مفتوح — واسحبها لإعادة ترتيبها. استخدم فواصل الصفحات والأقسام لتشكيل المسار.",
      step3Title: "اضبط الذكاء الاصطناعي",
      step3Body: "حدّد اللغة والنبرة ومدى عمق متابعة الذكاء الاصطناعي بالأسئلة على الإجابات المفتوحة. يمكنك أيضًا ترجمة الاستطلاع بالكامل تلقائيًا.",
      step4Title: "شارك وحلّل",
      step4Body: "انشر رابطك وشاركه، ثم تابع وصول الردود والتحليلات مع إكمال المشاركين للاستطلاع.",
      back: "رجوع",
      next: "التالي",
      done: "ابدأ",
      skip: "تخطٍّ",
      help: "كيف يعمل",
    },
    import: {
      menuLabel: "استيراد ملف", reviewTitle: "مراجعة الاستطلاع المستورد",
      reviewIntro: "أنشأنا {{n}} سؤالاً من ملفك. راجعها بالأسفل ثم أنشئ الاستطلاع — يمكنك تحريره بالكامل في المُنشئ.",
      cancel: "إلغاء", titleField: "عنوان الاستطلاع", descriptionField: "الوصف",
      reportTitle: "ملاحظات الاستيراد", preview: "الأسئلة", pageBreak: "فاصل صفحة",
      sectionBreak: "قسم", required: "مطلوب", create: "إنشاء الاستطلاع",
      parseFailed: "تعذّر استيراد هذا الملف.", createFailed: "تعذّر إنشاء الاستطلاع.",
      uploadTitle: "استيراد استطلاع", parsing: "جارٍ قراءة ملفك…",
      dropHere: "اسحب ملفًا إلى هنا، أو انقر للتصفح",
      dropHint: "حتى 25 ميغابايت. ننشئ مسودة يمكنك مراجعتها وتحريرها.",
      supported: "الصيغ المدعومة",
      formatNote: "يحوّل الذكاء الاصطناعي ملفات Word وPDF إلى استبيان — فينسخ استبيانًا موجودًا أو يصيغ واحدًا من ملاحظاتك. ويُحلَّل Qualtrics .qsf وQualiTaTi .qlt بدقة.",
      exportDefinition: "تنزيل الاستطلاع", exportWord: "تنزيل كملف Word (.docx)", exportQlt: "تنزيل كملف QualiTaTi (.qlt)", exportFailed: "فشل التصدير.",
    },
    translations: {
      menuLabel: "الترجمات", title: "ترجمات الاستطلاع",
      intro: "أضف لغة لترجمة الاستطلاع بالكامل تلقائيًا بالذكاء الاصطناعي، ثم راجع وحرّر كل نص. يختار المشاركون لغتهم في البداية؛ وأي نص فارغ يعود إلى اللغة الافتراضية ({{lang}}).",
      source: "المصدر", addLanguage: "إضافة لغة",
      empty: "لا توجد لغات بعد. انقر على «إضافة لغة» لإنشاء ترجمة بالذكاء الاصطناعي.",
      surveyInfo: "معلومات الاستطلاع", fieldTitle: "العنوان", fieldDescription: "الوصف",
      fieldThankYouTitle: "عنوان الشكر", fieldThankYouMessage: "رسالة الشكر",
      fieldPrompt: "السؤال", options: "الخيارات", columns: "أعمدة المقياس",
      retranslate: "إعادة الترجمة", close: "إغلاق", save: "حفظ",
      translated: "اكتملت الترجمة التلقائية. راجع وحرّر بالأسفل.", translateFailed: "فشلت الترجمة.",
      saved: "تم حفظ الترجمة.", saveFailed: "فشل الحفظ.",
      confirmDelete: "إزالة هذه اللغة وكل ترجماتها؟", deleteFailed: "فشل الحذف.",
      confirmRetranslate: "إعادة الترجمة من البداية؟ سيؤدي ذلك إلى استبدال تعديلاتك لهذه اللغة.",
      loadFailed: "فشل تحميل الترجمات.",
    },
    hub: {
      emptyTitle: "أنشئ أول استطلاع لك",
      emptyBody: "ابدأ من الصفر، أو أنشئ استطلاعًا بالذكاء الاصطناعي، أو اختر قالبًا، أو استورد ملف Word أو PDF أو Qualtrics.",
      emptyNoResults: "لا توجد استطلاعات تطابق عوامل التصفية.",
      clearFilters: "مسح عوامل التصفية",
      title: "الاستطلاعات",
      createProject: "إنشاء مشروع استطلاع",
      manageSurveys: "إدارة الاستطلاعات",
      createDialogTitle: "مشروع استطلاع جديد",
      projectName: "اسم المشروع",
      projectOutline: "ملخص المشروع",
      interviewType: "نوع المقابلة",
      interviewLanguage: "لغة المقابلة",
      cancel: "إلغاء",
      save: "حفظ",
      createSuccess: "تم إنشاء المشروع",
      createError: "فشل إنشاء المشروع",
      fillRequired: "يرجى ملء الحقول المطلوبة",
      loadError: "فشل تحميل المشاريع",
      confirmDeleteProject: "حذف مشروع الاستطلاع وجميع استطلاعاته؟ لا يمكن التراجع عن هذا.",
      projectDeleted: "تم حذف المشروع بنجاح",
      surveysInProject: "الاستطلاعات في هذا المشروع",
      noSurveysYet: "لا توجد استطلاعات بعد",
      manageProjectSurveys: "إدارة الاستطلاعات",
      noProjectsYet: "لم يتم إنشاء مشاريع استطلاع بعد",
      experimentalTitle: "ميزة تجريبية",
      experimentalMessage: "الاستطلاع التفاعلي أداة تجريبية قيد التطوير النشط حالياً.",
      search: "البحث في الاستطلاعات",
      filterAll: "الكل",
      filterDraft: "مسودة",
      filterPublished: "منشور",
      sortNewest: "الأحدث أولاً",
      sortAlpha: "أبجدي",
      noResults: "لا توجد استطلاعات تطابق بحثك",
      analytics: "التحليلات",
      duplicate: "نسخ",
      duplicateSuccess: "تم نسخ الاستطلاع",
    },
    list: {
      emptyTitle: "لا توجد استطلاعات في هذا المشروع بعد",
      emptyBody: "أنشئ أول استطلاع لك لبدء جمع الردود.",
      fromTemplate: "ابدأ من قالب",
      fromWizard: "إنشاء بالذكاء الاصطناعي",
      fromTemplateTitle: "ابدأ من قالب",
      fromTemplateSubtitle: "اختر قالباً للحصول على مسودة استبيان قابلة للتحرير. نملأ مسبقاً الأسئلة وإعدادات متابعة الذكاء الاصطناعي ووضعاً افتراضياً مناسباً.",
      templatesEmpty: "لا توجد قوالب متاحة حالياً.",
      creatingFromTemplate: "جارٍ الإنشاء…",
      wizard: {
        title: "إنشاء استبيان بالذكاء الاصطناعي",
        subtitle: "أخبر ميمي بما تريد معرفته وستضع لك مسودة قابلة للتحرير.",
        generating: "ميمي تكتب لك مسودة الاستبيان…",
        generatingHint: "يستغرق عادة 20-40 ثانية. نولّد مجموعة أسئلة مخصصة مع إعدادات متابعة مناسبة.",
        goalLabel: "ما الذي تريد معرفته؟",
        goalPlaceholder: "مثال: لماذا يتوقف العملاء بعد الشهر الأول — ما الذي يدفع القرار وما الذي كان يمكن أن يغيّره.",
        goalHelp: "الأهداف المحددة تنتج استبيانات أفضل. اهدف إلى 1-3 جمل.",
        audienceLabel: "من سيجيب على هذا الاستبيان؟",
        audiencePlaceholder: "مثال: عملاء ألغوا الاشتراك خلال آخر 30 يوماً",
        languageLabel: "اللغة",
        depthLabel: "العمق",
        nQuestionsLabel: "عدد الأسئلة المستهدف",
        generate: "إنشاء",
        errorGoalTooShort: "يجب أن يحتوي الهدف على 10 أحرف على الأقل ليتمكن الذكاء الاصطناعي من العمل.",
        errorAudienceTooShort: "أخبرنا بالجمهور حتى يتمكن الذكاء الاصطناعي من تكييف الأسئلة.",
      },
      title: "الاستطلاع",
      create: "إنشاء استطلاع",
      createDialogTitle: "استطلاع جديد",
      titleField: "العنوان",
      descriptionField: "الوصف",
      cancel: "إلغاء",
      save: "حفظ",
      builder: "المنشئ",
      export: "تصدير",
      publish: "نشر",
      status: "الحالة",
      linkCopied: "تم نسخ رابط المشاركة",
      publishFirst: "يرجى نشر الاستطلاع أولاً",
      publishReady: "تم نشر الاستطلاع",
      publishError: "فشل النشر",
      createSuccess: "تم إنشاء الاستطلاع",
      createError: "فشل الإنشاء",
      exportReady: "CSV جاهز",
      exportError: "فشل التصدير",
      error: "فشل تحميل الاستطلاعات",
      copyLink: "نسخ رابط المشاركة",
      downloadCsv: "تحميل CSV",
      deleteSurvey: "حذف الاستطلاع",
      publishGetLink: "انشر واحصل على الرابط",
      published: "تم نشر الاستطلاع — رابط المشاركة جاهز.",
      questionsLabel: "أسئلة",
      responsesLabel: "ردود"
    },
    builder: {
      minDwellLabel: "أدنى وقت للعرض (ثوانٍ)",
      minDwellHelp: "يجب أن يبقى المشاركون في هذه الصفحة هذه المدة قبل تفعيل «التالي/إرسال». 0 = إيقاف.",
      saving: "جارٍ الحفظ…",
      saved: "تم الحفظ",
      saveFailed: "تعذّر حفظ التغييرات",
      aiFollowUp: {
        label: "متابعة الذكاء الاصطناعي",
        helperNone: "بدون متابعة — يجيب المشاركون وينتقلون",
        helperLight: "Light — متابعة محادثة واحدة لإثراء الإجابة",
        helperDeep: "Deep — حتى ثلاث متابعات للحصول على رؤى نوعية معمّقة",
        optionNone: "بدون — بدون متابعة",
        optionLight: "Light — متابعة واحدة",
        optionDeep: "Deep — حتى 3 متابعات",
      },
      cancel: "إلغاء",
      surveyMode: {
        label: "وضع الاستبيان",
        form: {
          title: "استبيان تقليدي",
          subtitle: "استبيان نموذجي قياسي، بدون ذكاء اصطناعي",
        },
        aiFollowUp: {
          title: "استبيان بمتابعات الذكاء الاصطناعي",
          subtitle: "نموذج + أسئلة متابعة من الذكاء الاصطناعي للإجابات المفتوحة",
          hint: "نصيحة: اضبط شدة «متابعة الذكاء الاصطناعي» على كل سؤال مفتوح أدناه. Light = متابعة واحدة، Deep = حتى 3 متابعات.",
        },
        aiInterview: {
          title: "استبيان حواري",
          subtitle: "مقابلة حوارية بقيادة الذكاء الاصطناعي، حوار حر",
        },
      },
      preflight: {
        title: "فحص ما قبل النشر",
        loading: "جارٍ فحص الاستبيان…",
        ready: "جاهز للنشر",
        readyHint: "اجتاز استبيانك جميع الفحوصات. سيرى المشاركون تجربة سلسة.",
        hasErrors: "أصلح المشكلات أدناه قبل النشر:",
        warningsOnly: "يمكنك النشر، لكن تحقّق من النقاط التالية:",
        fallback: "تعذّر تشغيل فحص الصحة — يُسمح بالنشر على أي حال.",
        rerun: "إعادة الفحص",
      },
      pipingChip: 'إدراج إجابة سابقة',
      pipingTooltipTitle: 'إظهار إجابة سابقة داخل هذا السؤال',
      pipingTooltipBody: 'انقر على وسم لنسخه ثم الصقه في نص سؤالك. عندما يراه المستجيب، يتحول إلى إجابته السابقة.',
      constantSumItems: 'العناصر',
      constantSumItemsHelp: 'عنصر واحد في كل سطر',
      constantSumTotal: 'المجموع',
      constantSumTotalHelp: 'المجموع الذي يجب أن يوزعه المستجيبون (مثلاً 100)',
      emailAutoConfig: 'يتم التحقق منه كعنوان بريد إلكتروني',
      fileAcceptHelp: 'أنواع الملفات المسموح بها (مفصولة بفواصل، مثل .pdf,.docx)',
      fileAcceptTypes: 'الأنواع المقبولة',
      fileMaxSize: 'الحجم الأقصى للملف (ميجابايت)',
      imageChoiceHelp: 'عنوان URL واحد للصورة في كل سطر. التسميات الاختيارية تتطابق حسب الترتيب.',
      imageChoiceLabels: 'التسميات',
      imageChoiceMultiple: 'السماح بتحديدات متعددة',
      imageChoiceUrls: 'روابط الصور',
      phoneAutoConfig: 'يتم التحقق منه كرقم هاتف',
      ratingMax: 'أقصى تقييم',
      sectionBreakInfo: 'فاصل بصري فقط — لا يتم جمع إجابة.',
      thankYouPage: 'صفحة الشكر',
      thankYouRedirectHelp: 'رابط اختياري لإعادة التوجيه بعد الإرسال',
      yesNoAutoConfig: 'اختيار نعم / لا',
      loadError: "فشل تحميل الاستطلاع", saveMeta: "تم تحديث الاستطلاع", metaSaved: "تم حفظ التفاصيل", saveError: "فشل الحفظ", addError: "فشل إضافة السؤال", questionAdded: "تمت إضافة السؤال", saveQuestion: "تم حفظ السؤال", duplicate: "تكرار", duplicateSuccess: "تم تكرار السؤال", preview: "معاينة", previewMode: "وضع المعاينة", moveToBlock: "نقل إلى الكتلة",
      types: {
        image: "صورة",
        short_text: "إجابة قصيرة", long_text: "فقرة", single_choice: "اختيار من متعدد", multi_choice: "مربعات اختيار", number: "رقم", date: "تاريخ", ranking: "ترتيب", bipolar_scale: "مقياس ثنائي القطب", info_text: "نص معلوماتي",
        slider: "شريط تمرير", dropdown: "قائمة منسدلة", nps: "صافي نقاط الترويج", matrix: "مصفوفة",
        ai_interview: "مقابلة ذكاء اصطناعي",
        rating_scale: "مقياس التقييم",
        yes_no: "نعم / لا",
        email: "البريد الإلكتروني",
        phone: "رقم الهاتف",
        image_choice: "اختيار صورة",
        file_upload: "رفع ملف",
        constant_sum: "المجموع الثابت",
        section_break: "فاصل القسم",
        page_break: "فاصل صفحة"
      },
      typeDescriptions: {
        short_text: "إجابة نصية قصيرة", long_text: "إجابة نصية طويلة", single_choice: "اختر خياراً واحداً", multi_choice: "حدد خيارات متعددة", number: "قيمة رقمية", date: "منتقي التاريخ", ranking: "ترتيب العناصر", bipolar_scale: "مقياس بين قطبين متعاكسين", info_text: "عرض نص أو صور (بدون إجابة مطلوبة)",
        slider: "اسحب لتحديد قيمة في نطاق", dropdown: "حدد خياراً من قائمة منسدلة", nps: "قيّم من 0 إلى 10 (صافي نقاط الترويج)", matrix: "قيّم عناصر متعددة على نفس المقياس",
        ai_interview: "تضمين محادثة مقابلة مدعومة بالذكاء الاصطناعي",
        rating_scale: "تقييم بالنجوم (مثل 1-5 نجوم)",
        yes_no: "سؤال بسيط نعم أو لا",
        email: "عنوان بريد إلكتروني مع التحقق",
        phone: "رقم هاتف مع التحقق",
        image_choice: "اختر من خيارات الصور",
        file_upload: "رفع ملف مرفق",
        constant_sum: "توزيع النقاط بين العناصر",
        section_break: "فاصل مرئي بين الأقسام",
        page_break: "يبدأ صفحة جديدة للمستجيبين"
      },
      stepValue: "الخطوة", npsAutoConfig: "يتم تكوين NPS تلقائياً كمقياس من 0 إلى 10. لا حاجة لتكوين إضافي.", matrixRows: "تسميات الصفوف (واحد لكل سطر)", matrixRowsHelp: "أدخل العناصر المراد تقييمها، واحد لكل سطر", matrixColumns: "تسميات الأعمدة (واحد لكل سطر)", matrixColumnsHelp: "أدخل خيارات المقياس، واحد لكل سطر",
      interviewConfig: {
        title: "إعدادات مقابلة الذكاء الاصطناعي",
        interactionMode: "وضع التفاعل",
        voiceToVoice: "صوت إلى صوت",
        voiceToText: "صوت إلى نص",
        textToText: "نص إلى نص",
        textToVoice: "نص إلى صوت",
        language: "لغة المقابلة",
        maxDuration: "المدة القصوى (دقائق)",
        outline: "مخطط المقابلة / التعليمات",
        outlinePlaceholder: "صف ما يجب أن يسأل عنه الذكاء الاصطناعي...",
        outlineHelp: "سيتبع المحاور الذكي هذا المخطط لتوجيه المحادثة.",
      },
      conditions: "الشروط",
      addCondition: "إضافة شرط",
      removeCondition: "إزالة",
      conditionLogicAnd: "و (يجب أن تتطابق جميعها)",
      conditionLogicOr: "أو (يجب أن تتطابق واحدة على الأقل)",
      conditionQuestion: "إذا كان السؤال",
      conditionOperator: "العامل",
      conditionValue: "القيمة",
      operatorEquals: "يساوي",
      operatorNotEquals: "لا يساوي",
      operatorContains: "يحتوي على",
      operatorGreaterThan: "أكبر من",
      operatorLessThan: "أصغر من",
      operatorIsEmpty: "فارغ",
      operatorIsNotEmpty: "غير فارغ",
      pipingHelp: 'نصيحة: استخدم ${variable_name} لإدراج إجابة سابقة',
      availableVariables: 'المتاحة: {{variables}}',
      // Share & Distribution (Phase 6)
      share: 'مشاركة',
      shareTitle: 'شارك استبيانك',
      tabLink: 'رابط / QR',
      tabEmbed: 'تضمين',
      tabSocial: 'وسائل التواصل الاجتماعي',
      tabProtect: 'حماية',
      copy: 'نسخ',
      copied: 'تم النسخ',
      linkCopied: 'تم نسخ الرابط',
      qrHint: 'امسح ضوئياً باستخدام كاميرا الهاتف لفتح الاستبيان',
      downloadQR: 'تنزيل رمز QR (PNG)',
      embedHelp: 'الصق هذا الكود HTML على موقعك لتضمين الاستبيان',
      copyEmbed: 'نسخ رمز التضمين',
      embedCopied: 'تم نسخ رمز التضمين',
      preview: 'معاينة',
      socialHelp: 'شارك استبيانك على منصات وسائل التواصل الاجتماعي:',
      shareOn: 'شارك في',
      passwordProtection: 'حماية كلمة المرور',
      passwordHint: 'اطلب من المستجيبين إدخال كلمة مرور قبل البدء',
      passwordActive: 'تم تعيين كلمة مرور. أدخل واحدة جديدة أدناه لتغييرها أو اتركها فارغة للإزالة',
      passwordPlaceholder: 'اترك فارغاً بدون كلمة مرور',
      passwordSaved: 'تم تحديث كلمة المرور',
      passwordRemoved: 'تم إزالة كلمة المرور',
      removePassword: 'إزالة كلمة المرور',
      apply: 'تطبيق',
      schedule: 'الجدول الزمني',
      scheduleHelp: 'افتح واغلق استبيانك تلقائياً في أوقات محددة. اترك فارغاً للفتح دائماً',
      openAt: 'يفتح في',
      closeAt: 'يغلق في',
      saveSchedule: 'حفظ الجدول الزمني',
      scheduleSaved: 'تم حفظ الجدول الزمني',
      saveError: 'فشل الحفظ',
      // Theming (Phase 5)
      themeBranding: 'الموضوع والعلامة التجارية',
      themeBrandingHelp: 'تخصيص مظهر صفحة الاستبيان التي يراها المستجيبون',
      themePresets: 'الإعدادات المسبقة السريعة',
      primaryColor: 'اللون الأساسي',
      backgroundColor: 'الخلفية',
      textColor: 'لون النص',
      fontFamily: 'نمط الخط',
      fontSystem: 'الافتراضي للنظام (حديث)',
      fontSerif: 'Serif (كلاسيكي)',
      fontRounded: 'مستدير (ودود)',
      fontMono: 'Monospace (تقني)',
      logoUrl: 'عنوان URL لصورة الشعار',
      logoUrlHelp: 'يعرض فوق عنوان الاستبيان. اختياري',
      headerImageUrl: 'عنوان URL لصورة الرأس',
      headerImageUrlHelp: 'صورة بانر عريضة في الأعلى. اختياري',
      themePreview: 'معاينة',
      sampleSurveyTitle: 'عنوان استبيانك',
      sampleQuestionText: 'هذا ما تبدو عليه السؤال',
      sampleSubmit: 'إرسال',
      resetTheme: 'إعادة تعيين إلى الافتراضي',
      randomizeQuestions: 'ترتيب عشوائي للأسئلة',
      randomizeOptions: "ترتيب عشوائي للخيارات",
      thankYouSettings: "صفحة الشكر",
      thankYouTitle: "العنوان",
      thankYouMessage: "الرسالة",
      thankYouRedirect: "رابط إعادة التوجيه (اختياري)",
      maxResponses: "الحد الأقصى للردود (اختياري)",
      maxResponsesHelp: "اتركه فارغاً للحد غير المحدود",
      deliveryMode: "وضع التسليم",
      deliveryConversational: "محادثة (مقابلة بالذكاء الاصطناعي)",
      deliveryForm: "نموذج قياسي",
      deliveryModeHelp: "يستخدم وضع المحادثة الذكاء الاصطناعي لإجراء المقابلات. يعرض وضع النموذج القياسي جميع الأسئلة مرة واحدة.",
      testWithSynthetic: "اختبار مع إنسان اصطناعي",
      fromLibrary: "من المكتبة",
      fromLibraryHint: "انسخ من القوالب والحزم الجاهزة",
      templateLibrary: "مكتبة القوالب",
      templates: "القوالب",
      bundles: "الحزم",
      allTemplates: "جميع القوالب",
      myTemplates: "قوالسي",
      allCategories: "جميع الفئات",
      selectTemplate: "حدد قالب",
      templateName: "اسم القالب",
      category: "الفئة",
      search: "بحث",
      preview: "معاينة",
      inserting: "جاري الإدراج...",
      insertTemplate: "إدراج قالب",
      insertBundle: "إدراج حزمة",
      noTemplates: "لم يتم العثور على قوالب",
      noBundles: "لم يتم العثور على حزم",
      templateInsertedSuccess: "تم إدراج القالب",
      bundleInsertedSuccess: "تم إدراج الحزمة",
      templateInsertError: "فشل إدراج القالب",
      cancel: "إلغاء",
      skipLogic: "منطق التخطي",
      skipLogicHelp: "إظهار هذا السؤال فقط عند استيفاء الشروط",
      displayLogic: "منطق العرض",
      displayLogicHelp: "إظهار هذا السؤال فقط عندما تتطابق الإجابات السابقة مع هذه الشروط",
      conditionIf: "إذا",
      logicTab: "المنطق",
      noLogicYet: "لم يتم تكوين منطق التخطي",
      noLogicHint: "انقر على سؤال وانتقل إلى قسم منطق التخطي لإضافة شروط.",
      logicSummaryPrefix: "إظهار هذا السؤال عندما",
      settings: "إعدادات الاستبيان",
      surveyFlow: "تدفق الاستطلاع",
      addBlock: "إضافة كتلة",
      addRandomizer: "إضافة عشوائي",
      blockPlaceholder: "كتلة بلا عنوان",
      deleteBlock: "حذف الكتلة",
      confirmDeleteBlock: "حذف هذه الكتلة؟ ستصبح أسئلتها غير مخصصة.",
      randomizer: "عشوائي",
      randomizerShow: "عرض",
      randomizerOf: "من",
      addVariant: "إضافة نسخة",
      questionsCount: "{n} سؤال/أسئلة",
      unassigned: "أسئلة غير مخصصة",
      noBlocks: "لا توجد كتل بعد",
      renameBlock: "إعادة تسمية",
      addQuestion: "إضافة",
      questionSaved: "تم حفظ السؤال بنجاح",
      questionDeleted: "تم حذف السؤال",
      deleteError: "فشل الحذف",
      reorderError: "فشل إعادة الترتيب",
      confirmDelete: "حذف هذا السؤال؟",
      deleteSurvey: "حذف الاستطلاع",
      confirmDeleteSurvey: "هل أنت متأكد من حذف هذا الاستطلاع؟ لا يمكن التراجع عن هذا الإجراء.",
      surveyDeleted: "تم حذف الاستطلاع بنجاح",
      publishCopied: "تم النشر ونسخ الرابط",
      publishSuccess: "تم نشر الاستطلاع بنجاح",
      publishError: "فشل النشر",
      linkCopied: "تم نسخ الرابط إلى الحافظة",
      questions: "الأسئلة",
      noQuestions: "لا توجد أسئلة بعد",
      clickToAdd: "انقر على الزر أدناه لإضافة سؤالك الأول",
      addFirstQuestion: "إضافة سؤال",
      untitledQuestion: "(سؤال بدون عنوان)",
      promptPlaceholder: "أدخل سؤالك هنا...",
      surveyTitle: "عنوان الاستطلاع",
      surveyDescription: "وصف الاستطلاع",
      noDescription: "بدون وصف",
      questionType: "نوع السؤال",
      variableName: "اسم المتغير",
      variableHelp: "استخدم snake_case؛ يجب أن يكون فريدًا",
      requiredQuestion: "سؤال إلزامي",
      isConversational: "اسأل بشكل محادثي",
      askForm: "اسأل عبر نموذج",
      optionsPlaceholder: "الخيار 1\nالخيار 2\nالخيار 3",
      minValue: "القيمة الدنيا",
      maxValue: "القيمة القصوى",
      saveChanges: "حفظ التغييرات",
      moreOptions: "المزيد من الخيارات",
      moveUp: "تحريك لأعلى",
      moveDown: "تحريك لأسفل",
      expand: "توسيع",
      collapse: "طي",
      delete: "حذف",
      republish: "إعادة النشر",
      unpublish: "إلغاء النشر",
      unpublishing: "جارٍ إلغاء النشر…",
      unpublishSuccess: "تم إلغاء نشر الاستبيان. أصبح الآن مسودة.",
      unpublishError: "فشل إلغاء نشر الاستبيان",
      unpublishTooltip: "حظر المشاركين الجدد. تُحفَظ الردود الحالية، ويُحتَفَظ برابط المشاركة لإعادة النشر.",
      confirmUnpublish: "إلغاء نشر هذا الاستبيان؟ سيتم حظر المشاركين الجدد. تُحفَظ الردود الحالية. يمكنك إعادة النشر لاحقًا بنفس الرابط.",
      copyLink: "نسخ رابط المشاركة",
      meta: "معلومات الاستطلاع",
      titleField: "العنوان",
      descriptionField: "الوصف",
      descriptionPlaceholder: "صف الغرض من هذا الاستطلاع...",
      language: "اللغة",
      save: "حفظ",
      imageUrl: "رابط الصورة",
      imageUrlHelp: "اختياري: أدخل رابطاً لعرض صورة",
      imagePreview: "معاينة",
      imageLoadError: "فشل تحميل الصورة. يرجى التحقق من الرابط.",
      addImage: "إضافة صورة",
      questionImage: "صورة السؤال",
      removeImage: "إزالة الصورة",
      uploadImage: "رفع صورة",
      uploading: "جاري الرفع…",
      imageUrlPlaceholder: "أو الصق رابط الصورة",
      imageHelp: "PNG أو JPEG أو GIF أو WebP. بحد أقصى 2 ميجابايت.",
      imageUploadFailed: "فشل الرفع.",
      uploadImages: "رفع الصور",
      addMoreImages: "إضافة المزيد",
      urlPasteHint: "اضغط Enter للإضافة",
      removeAllImages: "إزالة الكل",
      itemsToRank: "العناصر المراد ترتيبها",
      itemsToRankHelp: "أدخل العناصر المراد ترتيبها، واحد في كل سطر",
      bipolarLabels: "تسميات المقياس",
      leftLabel: "التسمية اليسرى",
      rightLabel: "التسمية اليمنى",
      surveyLanguage: "لغة الاستطلاع",
      surveyLanguageHelp: "اختر 'مرن' للسماح للمشاركين باختيار لغتهم",
      flexible: "مرن (يختار المشارك)",
      publish: "نشر ونسخ الرابط",
      editor: "محرر الأسئلة",
      selectPrompt: "اختر سؤالاً للتحرير",
      prompt: "نص السؤال",
      variable: "اسم المتغير",
      required: "مطلوب",
      options: "الخيارات (واحد في كل سطر)",
      optionsHelp: "أدخل خياراً واحداً في كل سطر",
      min: "الحد الأدنى",
      max: "الحد الأقصى",
      saveQuestionBtn: "حفظ السؤال",
      defaultPrompt: "سؤال جديد",
    },
    languages: { en: "الإنجليزية", zh: "الصينية", fr: "الفرنسية", de: "الألمانية", es: "الإسبانية", nb: "النرويجية", nl: "الهولندية", pt: "البرتغالية", ja: "اليابانية", ar: "العربية", flexible: "مرن (يختار المشارك)" },
    synthetic: {
      dialogTitle: "اختبار مع مشارك اصطناعي", dialogSubtitle: "قم بتكوين الملف الديموغرافي للمشارك المحاكى",
      age: "العمر", gender: "الجنس", male: "ذكر", female: "أنثى", nonBinary: "غير ثنائي", preferNotToSay: "أفضل عدم القول",
      nationality: "الجنسية", ethnicity: "العرق", education: "التعليم",
      highSchool: "الثانوية", bachelors: "بكالوريوس", masters: "ماجستير", phd: "دكتوراه", vocational: "مهني", other: "أخرى",
      occupation: "المهنة", personality: "سمات الشخصية", additionalContext: "سياق إضافي (اختياري)",
      runTest: "تشغيل الاختبار", cancel: "إلغاء",
      simulating: "جارٍ محاكاة إجابات المشارك...", simulatingHint: "قد يستغرق هذا 15-30 ثانية",
      resultsTitle: "نتائج الاختبار الاصطناعي", syntheticPersona: "ملف المشارك الاصطناعي", synthetic: "اصطناعي",
      question: "سؤال", type: "النوع", answer: "إجابة", close: "إغلاق",
    },
    status: { draft: "مسودة", published: "منشور", archived: "مؤرشف" },
    start: { untitledSurvey: "استبيان بدون عنوان", timeEstimate: "حوالي {{minutes}} {{unit}}", questionCount: "{{count}} {{unit}}", minute: "دقيقة", minutes: "دقائق", question: "سؤال", questions: "أسئلة",
      minute: "دقيقة",
      minutes: "دقائق",
      question: "سؤال",
      questions: "أسئلة", privacyNote: "إجاباتك سرية", startButton: "ابدأ الاستبيان", selectLanguageFirst: "يُرجى اختيار لغة للمتابعة.", title: "بدء الاستطلاع", subtitle: "ابدأ الاستطلاع التفاعلي.", button: "بدء", loading: "جار بدء الاستطلاع...", error: "تعذّر بدء الجلسة", selectLanguage: "اختر لغتك", selectLanguageSubtitle: "اختر اللغة لجلسة الاستطلاع الخاصة بك", surveyFull: "وصل هذا الاستطلاع إلى الحد الأقصى للردود" },
    session: {
      dwellWait: "يمكنك المتابعة خلال {{n}} ثانية", redirectingIn: "إعادة التوجيه خلال {seconds}…", continueNow: "متابعة الآن", cancelRedirect: "إلغاء", redirectCancelled: "تم إلغاء إعادة التوجيه التلقائية.", continueToSite: "المتابعة إلى الموقع", loadError: "فشل تحميل الجلسة", connected: "متصل بالمساعد", wsError: "خطأ في اتصال الدردشة", answersSaved: "تم حفظ الإجابات", saveError: "خطأ في الحفظ", submitted: "تم الإرسال", submitError: "خطأ في الإرسال", progress: "التقدم: {{answered}} / {{total}} مطلوبة", placeholder: "اكتب إجابتك", send: "إرسال", verification: "التحقق", required: "مطلوب", saveEdits: "حفظ التعديلات", submit: "موافقة وإرسال", loading: "جار التحميل...", processing: "جار معالجة إجابتك...", stopRecording: "إيقاف التسجيل", pressToSpeak: "اضغط للتحدث", reviewSubmit: "مراجعة وإرسال", answerMore: "أجب على {{count}} سؤال(أسئلة) مطلوب(ة) آخر", transcribeFailed: "تعذّر نسخ الصوت. يرجى المحاولة مجدداً.", audioProcessError: "فشلت معالجة الصوت: {{error}}", micError: "لا يمكن الوصول إلى الميكروفون: {{error}}", rankingPlaceholder: "أدخل ترتيب التصنيف (مثلاً: أ > ب > ج)", thankYouTitle: "شكراً لك!", thankYouMessage: "تم إرسال إجاباتك بنجاح.", returnHome: "العودة إلى الرئيسية", redirecting: "جارٍ إعادة التوجيه...", previewBanner: "[وضع المعاينة] هذه معاينة. لن يتم حفظ الإجابات في التحليلات.", progressLabel: "التقدم", autoSaving: "جارٍ الحفظ…", autoSaved: "تم الحفظ", saveAndContinue: "احفظ وتابع لاحقاً", saveAndContinueTooltip: "احفظ تقدمك واحصل على رابط للمتابعة لاحقاً", saveAndContinueTitle: "احفظ تقدمك", saveAndContinueBody: "تم حفظ إجاباتك. أضف هذا الرابط إلى المفضلة أو أرسله إلى بريدك الإلكتروني لمتابعة ما بدأته — من أي جهاز.", copyLink: "نسخ الرابط", linkCopied: "تم نسخ الرابط إلى الحافظة", close: "إغلاق", alreadySubmittedWarning: "تُشير سجلاتنا إلى أنك ربما أرسلت هذا الاستطلاع من قبل. يمكنك المتابعة، لكن قد يتم استبعاد الردود المكررة.", connectionError: "خطأ في الاتصال", reconnecting: "جار إعادة الاتصال بالمساعد...", connectionLost: "انقطع الاتصال. يرجى تحديث الصفحة.", typeHere: "اكتب إجابتك هنا...", send: "إرسال", formMode: "نموذج قياسي", conversationalMode: "مقابلة بالذكاء الاصطناعي", voicePlayback: "صوت", switchToForm: "التبديل إلى النموذج", switchToConversational: "التبديل إلى مقابلة الذكاء الاصطناعي", status_in_progress: "قيد التقدم", pageOf: "صفحة {current} من {total}", nextPage: "التالي", previousPage: "السابق", status_submitted: "تم الإرسال", status_awaiting_verification: "قيد المراجعة", requiredField: "مطلوب", fieldRequired: "{label} مطلوب", invalidEmail: "يرجى إدخال عنوان بريد إلكتروني صالح", invalidPhone: "يرجى إدخال رقم هاتف صالح", invalidNumber: "يرجى إدخال رقم صالح", sumMustEqual: "يجب أن يكون مجموع القيم {total} (الحالي: {sum})", npsNotLikely: "غير محتمل على الإطلاق", npsExtremely: "محتمل للغاية", chooseFile: "اختيار ملف", chooseFileDrag: "اختر ملفاً أو اسحبه وأفلته", fileSelected: "تم اختيار الملف", selectedPrefix: "المُختار:", acceptedPrefix: "المقبول:", fileTooLarge: "يجب أن يكون حجم الملف أقل من {size} ميغابايت", constantSumInstruction: "وزّع ما مجموعه {total} نقطة على العناصر أدناه.", remaining: "المتبقي:", pageBreakLabel: "فاصل صفحة", noFormConfigured: "لم يتم تكوين أي نموذج", noFormConfiguredBody: "لم يقم مالك المشروع بإعداد نموذج مخصص بعد.", unsupportedField: "نوع حقل غير مدعوم: {type}", imageNotConfigured: "لم يتم تكوين رابط الصورة", videoNotConfigured: "لم يتم تكوين رابط الفيديو", dragToReorder: "اسحب لإعادة الترتيب", moveUp: "تحريك لأعلى", moveDown: "تحريك لأسفل", requiredSummary: "يرجى الإجابة على الأسئلة المميّزة قبل المتابعة.", yes: "نعم", no: "لا", optionN: "الخيار {n}", surveyImageAlt: "معلومات الاستطلاع", interview: { title: "مقابلة ذكاء اصطناعي", description: "يتضمن هذا السؤال مقابلة قصيرة بالذكاء الاصطناعي.", start: "بدء المقابلة", starting: "جاري بدء المقابلة...", ending: "جاري حفظ المقابلة...", completed: "اكتملت المقابلة", end: "إنهاء", stopRecording: "إيقاف التسجيل", tapToSpeak: "اضغط للتحدث", typePlaceholder: "اكتب إجابتك..." } },
    export: { loadError: "خطأ في تحميل الاستطلاع", description: "تحميل الإجابات المرسلة كـ CSV.", button: "تحميل CSV", success: "التصدير جاهز", error: "خطأ في التصدير", xlsx: "تحميل XLSX" },
    analytics: { filterScopeNote: "يقتصر فلتر التاريخ هذا على تضييق قائمة الردود والجداول المتقاطعة فقط. أما بطاقات الملخص والرسوم البيانية وملخص الذكاء الاصطناعي فتعكس دائمًا جميع الردود.", clearFilter: 'مسح الفلتر', columnQuestion: 'سؤال العمود', crossTab: 'جدول متقاطع', crossTabHelp: 'قارن كيف تتوزع إجابات سؤال وفقاً لسؤال آخر.', crossTabNeedTwo: 'اختر سؤالين للمقارنة.', crossTabPickTwo: 'حدد سؤالاً للصف وآخر للعمود', crossTabTitle: 'الجدول المتقاطع', duration: 'المدة', filterBy: 'تصفية حسب', from: 'من', individualResponses: 'الردود الفردية', language: 'اللغة', next: 'التالي', noAnswer: 'لا إجابة', noResponsesYet: 'لا توجد ردود بعد', deleteResponse: 'حذف الرد', confirmDeleteResponse: 'حذف هذا الرد نهائيًا؟ لا يمكن التراجع عن هذا الإجراء.', responseDeleted: 'تم حذف الرد.', deleteFailed: 'فشل حذف الرد.', download: 'تنزيل', downloadCsv: 'تنزيل CSV', downloadXlsx: 'تنزيل Excel (XLSX)', exportFailed: 'فشل التصدير.', prev: 'السابق', respondent: 'المستجيب', rowQuestion: 'سؤال الصف', sampleResponses: 'عينات من الردود', submittedAt: 'تاريخ الإرسال', to: 'إلى', viewResponses: 'عرض الردود', wordCloud: 'سحابة الكلمات', title: "تحليلات الاستطلاع", totalResponses: "إجمالي الردود", completionRate: "معدل الإكمال", avgDuration: "متوسط المدة", minutes: "دقائق", responsesOverTime: "الردود عبر الوقت", questionBreakdown: "تحليل الأسئلة", responses: "ردود", noData: "لا توجد ردود بعد", refresh: "تحديث", detractors: "المنتقدون (0-6)", passives: "المحايدون (7-8)", promoters: "المروّجون (9-10)" }
  }
};






